import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ActiveColegioBadge from './common/ActiveColegioBadge';
import './NewDashboard.css';
import './AdminDashboard.css';
import './common/DataTableWithFilters.css';
import './common/DataTable.css';
import DataTable from './common/DataTable';
import { useAuth } from '../contexts/AuthContext';
import ColegioService from '../services/ColegioService';
import usuarioService from '../services/UsuarioService';
import colegioApiService from '../services/ColegioApiService';
import materiaService from '../services/MateriaService';
import aulaService from '../services/AulaService';
import areaService from '../services/AreaService';
import divisionService from '../services/DivisionService';
import adminDashboardService from '../services/AdminDashboardService';
import adminAsistenteService from '../services/AdminAsistenteService';
import procesosAsincronicosService from '../services/ProcesosAsincronicosService';
import solicitudesService from '../services/SolicitudesService';
import ChatModal from './common/ChatModal';
import AulaStatsModal from './common/AulaStatsModal';
import AulaRecursosModal from './common/AulaRecursosModal';
import UserSessionMenu from './common/UserSessionMenu';
import SearchableSelect from './common/SearchableSelect';
import AdminCursosSection from './AdminCursosSection';
import AdminAlumnoFamiliaresModal from './AdminAlumnoFamiliaresModal';
import TableExportActions from './common/TableExportActions';
import { ADMIN_DASHBOARD_MENU_ITEMS, filterAdminMenuItems, hasOpenAIChatsEnabled, isAdminColegio, isFeatureEnabled } from '../utils/adminMenu';
import { ITEMS_PER_PAGE_OPTIONS } from '../utils/pagination';
import AdminMenu from './common/menus/AdminMenu';

const ADMIN_PRIMARY_GRID_DEFAULT_PAGE_SIZE = 300;
const MATERIA_CURSO_FILTER = {
  CON_CURSO: 'conCurso',
  SIN_CURSO: 'sinCurso',
  TODAS: 'todas'
};

const formatDashboardDateTime = (value) => {
  if (!value) return 'Sin actividad reciente';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Sin actividad reciente';
  return date.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getFilterItemId = (item) => String(item?.id || item?.Id || '').trim();

const getFilterItemName = (item, fallback = 'Sin nombre') => (
  item?.nombre || item?.Nombre || item?.displayName || item?.DisplayName || fallback
);

const getFilterItemEmail = (item) => item?.email || item?.Email || '';

const getFilterItemTotalAulas = (item) => Number(item?.totalAulas ?? item?.TotalAulas ?? 0);

const getTopProfessorName = (item) => item?.nombre || item?.Nombre || item?.displayName || item?.DisplayName || 'Profesor';

const getTopProfessorEmail = (item) => item?.email || item?.Email || '';

const getTopProfessorTotalAulas = (item) => Number(item?.totalAulas ?? item?.TotalAulas ?? 0);

const getAulaAssistantCount = (aula) => {
  const explicit = aula?.cantidadAsistentes ?? aula?.CantidadAsistentes;
  if (explicit !== undefined && explicit !== null) return Number(explicit) || 0;
  const ids = aula?.idsAsistentes || aula?.IdsAsistentes || [];
  return Array.isArray(ids) ? ids.length : 0;
};

const getMateriaScope = (m) => {
  const valorAnio = m.valorAnio ?? m.ValorAnio ?? null;
  const division = String(m.division || m.Division || '').trim();
  if (!valorAnio) return division || '';
  return division ? `${valorAnio}° ${division}` : `${valorAnio}°`;
};

const getMateriaNombre = (m) => {
  const nombre = m.nombre || m.Nombre || 'Materia';
  const scope = getMateriaScope(m);
  return scope ? `${nombre} · ${scope}` : nombre;
};

const normalizeConfirmText = (value) => String(value || '').trim().replace(/\s+/g, ' ').toUpperCase();

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const isGeneralAdmin = !isAdminColegio(user);
  const featureFlags = useMemo(() => ColegioService.getFeatureFlags(), []);
  const hasModuloAlumnos = useMemo(() => {
    if (!Array.isArray(featureFlags) || featureFlags.length === 0) return true;
    return isFeatureEnabled(featureFlags, 'ModuloAlumnos');
  }, [featureFlags]);
  const hasMensajesAula = useMemo(() => {
    if (!Array.isArray(featureFlags) || featureFlags.length === 0) return true;
    return isFeatureEnabled(featureFlags, 'PermiteEnviarMensajesEnAula');
  }, [featureFlags]);

  const hasArchivosAula = useMemo(() => {
    if (!Array.isArray(featureFlags) || featureFlags.length === 0) return true;
    return isFeatureEnabled(featureFlags, 'PermiteSubirArchivosAlAula');
  }, [featureFlags]);
  const hasOpenAIChats = useMemo(() => hasOpenAIChatsEnabled(featureFlags), [featureFlags]);
  const hasAgentesClasicos = useMemo(() => {
    if (!Array.isArray(featureFlags) || featureFlags.length === 0) return true;
    return isFeatureEnabled(featureFlags, 'PermiteAgentesClasicosGPT');
  }, [featureFlags]);
  const hasPizarraAula = useMemo(() => {
    if (!Array.isArray(featureFlags) || featureFlags.length === 0) return true;
    return isFeatureEnabled(featureFlags, 'PermitePizarraCompartidaEnAula');
  }, [featureFlags]);
  const hasChatAula = useMemo(() => {
    if (!Array.isArray(featureFlags) || featureFlags.length === 0) return true;
    return isFeatureEnabled(featureFlags, 'PermiteChatGrupalEnAula');
  }, [featureFlags]);
  const hasVideollamadaAula = useMemo(() => {
    if (!Array.isArray(featureFlags) || featureFlags.length === 0) return true;
    return isFeatureEnabled(featureFlags, 'PermiteIniciarVideoLlamadaEnAula');
  }, [featureFlags]);
  const hasLinksAula = true;
  const hasRecursosAula = hasMensajesAula || hasArchivosAula || hasAgentesClasicos || hasOpenAIChats || hasLinksAula || hasPizarraAula || hasChatAula || hasVideollamadaAula;
  const [activeMenuItem, setActiveMenuItem] = useState('resumen');
  const [profesores, setProfesores] = useState([]);
  const [profesoresLoading, setProfesoresLoading] = useState(false);
  const [profesoresError, setProfesoresError] = useState('');
  const [importProfesoresModalOpen, setImportProfesoresModalOpen] = useState(false);
  const [importProfesoresFile, setImportProfesoresFile] = useState(null);
  const [importProfesoresLoading, setImportProfesoresLoading] = useState(false);
  const [importProfesoresMessage, setImportProfesoresMessage] = useState('');
  const [importAreasModalOpen, setImportAreasModalOpen] = useState(false);
  const [importAreasFile, setImportAreasFile] = useState(null);
  const [importAreasLoading, setImportAreasLoading] = useState(false);
  const [importAreasMessage, setImportAreasMessage] = useState('');
  const [importMateriasModalOpen, setImportMateriasModalOpen] = useState(false);
  const [importMateriasFile, setImportMateriasFile] = useState(null);
  const [importMateriasLoading, setImportMateriasLoading] = useState(false);
  const [importMateriasMessage, setImportMateriasMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ open: false, profesor: null, nextActivo: true });
  const [toggleLoading, setToggleLoading] = useState(false);
  const [resetProfesorModal, setResetProfesorModal] = useState({ open: false, profesor: null });
  const [resetProfesorLoading, setResetProfesorLoading] = useState(false);
  const [resetProfesorResult, setResetProfesorResult] = useState({ open: false, ok: true, message: '' });
  const [associateModalOpen, setAssociateModalOpen] = useState(false);
  const [associateEmail, setAssociateEmail] = useState('');
  const [associateLoading, setAssociateLoading] = useState(false);
  const [associateMessage, setAssociateMessage] = useState('');
  const [missingModal, setMissingModal] = useState({ open: false, email: '' });
  const [associateSuccess, setAssociateSuccess] = useState(false);
  const [disassociateModal, setDisassociateModal] = useState({ open: false, profesor: null });
  const [disassociateLoading, setDisassociateLoading] = useState(false);
  const [alumnos, setAlumnos] = useState([]);
  const [alumnosLoading, setAlumnosLoading] = useState(false);
  const [alumnosError, setAlumnosError] = useState('');
  const [alumnosSearch, setAlumnosSearch] = useState('');
  const [alumnoAssociateModalOpen, setAlumnoAssociateModalOpen] = useState(false);
  const [alumnoAssociateEmail, setAlumnoAssociateEmail] = useState('');
  const [alumnoAssociateLoading, setAlumnoAssociateLoading] = useState(false);
  const [alumnoAssociateMessage, setAlumnoAssociateMessage] = useState('');
  const [alumnoAssociateSuccess, setAlumnoAssociateSuccess] = useState(false);
  const [alumnoMissingModal, setAlumnoMissingModal] = useState({ open: false, email: '' });
  const [alumnoDisassociateModal, setAlumnoDisassociateModal] = useState({ open: false, alumno: null });
  const [alumnoDisassociateLoading, setAlumnoDisassociateLoading] = useState(false);
  const [alumnoToggleModal, setAlumnoToggleModal] = useState({ open: false, alumno: null, nextActivo: true });
  const [alumnoToggleLoading, setAlumnoToggleLoading] = useState(false);
  const [resetAlumnoModal, setResetAlumnoModal] = useState({ open: false, alumno: null });
  const [resetAlumnoLoading, setResetAlumnoLoading] = useState(false);
  const [resetAlumnoResult, setResetAlumnoResult] = useState({ open: false, ok: true, message: '' });
  const [alumnoFamiliaresModal, setAlumnoFamiliaresModal] = useState({ open: false, alumno: null });
  const [adminAreaUsers, setAdminAreaUsers] = useState([]);
  const [adminAreaLoading, setAdminAreaLoading] = useState(false);
  const [adminAreaError, setAdminAreaError] = useState('');
  const [adminAreaSearch, setAdminAreaSearch] = useState('');
  const [adminAreaToggleModal, setAdminAreaToggleModal] = useState({ open: false, user: null, nextActivo: true });
  const [adminAreaToggleLoading, setAdminAreaToggleLoading] = useState(false);
  const [colegiosList, setColegiosList] = useState([]);
  const [colegiosLoading, setColegiosLoading] = useState(false);
  const [colegiosError, setColegiosError] = useState('');
  const [colegiosSearch, setColegiosSearch] = useState('');
  const [colegioToggleModal, setColegioToggleModal] = useState({ open: false, colegio: null, nextActivo: true });
  const [colegioToggleLoading, setColegioToggleLoading] = useState(false);
  const [materias, setMaterias] = useState([]);
  const [materiasLoading, setMateriasLoading] = useState(false);
  const [materiasError, setMateriasError] = useState('');
  const [materiasNotice, setMateriasNotice] = useState('');
  const [materiasSearch, setMateriasSearch] = useState('');
  const [materiasCursoFilter, setMateriasCursoFilter] = useState(MATERIA_CURSO_FILTER.CON_CURSO);
  const [materiaToggleModal, setMateriaToggleModal] = useState({ open: false, materia: null, nextActivo: true });
  const [materiaToggleLoading, setMateriaToggleLoading] = useState(false);
  const [materiasSinCursosTotal, setMateriasSinCursosTotal] = useState(0);
  const [deleteUnlinkedMateriasModal, setDeleteUnlinkedMateriasModal] = useState({
    open: false,
    confirmText: '',
    loading: false,
    error: ''
  });
  const [aulas, setAulas] = useState([]);
  const [aulasLoading, setAulasLoading] = useState(false);
  const [aulasError, setAulasError] = useState('');
  const [aulasSearch, setAulasSearch] = useState('');
  const [aulasMateriaIds, setAulasMateriaIds] = useState([]);
  const [aulasProfesorIds, setAulasProfesorIds] = useState([]);
  const [aulasMateriasOptions, setAulasMateriasOptions] = useState([]);
  const [aulasProfesoresOptions, setAulasProfesoresOptions] = useState([]);
  const [aulasTopProfesores, setAulasTopProfesores] = useState([]);
  const [aulaToggleModal, setAulaToggleModal] = useState({ open: false, aula: null, nextActivo: true });
  const [aulaToggleLoading, setAulaToggleLoading] = useState(false);
  const [aulaStatsModal, setAulaStatsModal] = useState({ open: false, aula: null });
  const [aulaResourcesModal, setAulaResourcesModal] = useState({ open: false, aula: null });
  const [anios, setAnios] = useState([]);
  const [areas, setAreas] = useState([]);
  const [areasLoading, setAreasLoading] = useState(false);
  const [areasError, setAreasError] = useState('');
  const [areasSearch, setAreasSearch] = useState('');
  const [areaToggleModal, setAreaToggleModal] = useState({ open: false, area: null, nextActivo: true });
  const [areaToggleLoading, setAreaToggleLoading] = useState(false);
  const [divisiones, setDivisiones] = useState([]);
  const [divisionesLoading, setDivisionesLoading] = useState(false);
  const [divisionesError, setDivisionesError] = useState('');
  const [divisionesSearch, setDivisionesSearch] = useState('');
  const [divisionDeleteModal, setDivisionDeleteModal] = useState({ open: false, division: null });
  const [divisionDeleteLoading, setDivisionDeleteLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState('');
  const [asistentes, setAsistentes] = useState([]);
  const [asistentesLoading, setAsistentesLoading] = useState(false);
  const [asistentesError, setAsistentesError] = useState('');
  const [asistentesSearch, setAsistentesSearch] = useState('');
  const [profesoresPage, setProfesoresPage] = useState(1);
  const [profesoresTotal, setProfesoresTotal] = useState(0);
  const [profesoresPageSize, setProfesoresPageSize] = useState(ADMIN_PRIMARY_GRID_DEFAULT_PAGE_SIZE);
  const [alumnosPage, setAlumnosPage] = useState(1);
  const [alumnosTotal, setAlumnosTotal] = useState(0);
  const [alumnosPageSize, setAlumnosPageSize] = useState(ADMIN_PRIMARY_GRID_DEFAULT_PAGE_SIZE);
  const [adminAreaPage, setAdminAreaPage] = useState(1);
  const [adminAreaTotal, setAdminAreaTotal] = useState(0);
  const [adminAreaPageSize, setAdminAreaPageSize] = useState(10);
  const [materiasPage, setMateriasPage] = useState(1);
  const [materiasTotal, setMateriasTotal] = useState(0);
  const [materiasPageSize, setMateriasPageSize] = useState(ADMIN_PRIMARY_GRID_DEFAULT_PAGE_SIZE);
  const [aulasPage, setAulasPage] = useState(1);
  const [aulasTotal, setAulasTotal] = useState(0);
  const [aulasPageSize, setAulasPageSize] = useState(10);
  const [areasPage, setAreasPage] = useState(1);
  const [areasTotal, setAreasTotal] = useState(0);
  const [areasPageSize, setAreasPageSize] = useState(10);
  const [divisionesPage, setDivisionesPage] = useState(1);
  const [divisionesTotal, setDivisionesTotal] = useState(0);
  const [divisionesPageSize, setDivisionesPageSize] = useState(10);
  const [asistentesPage, setAsistentesPage] = useState(1);
  const [asistentesTotal, setAsistentesTotal] = useState(0);
  const [asistentesPageSize, setAsistentesPageSize] = useState(10);
  const [reportesLoadingId, setReportesLoadingId] = useState(null);
  const [reportesError, setReportesError] = useState('');
  const pageSize = 20;
  const [showChatModal, setShowChatModal] = useState(false);
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [asistenteToggleModal, setAsistenteToggleModal] = useState({ open: false, asistente: null, nextActivo: true });
  const [asistenteToggleLoading, setAsistenteToggleLoading] = useState(false);
  const [solicitudesRecibidas, setSolicitudesRecibidas] = useState([]);
  const [solicitudesRecibidasLoading, setSolicitudesRecibidasLoading] = useState(false);
  const [solicitudesRecibidasError, setSolicitudesRecibidasError] = useState('');
  const [solicitudesPendientes, setSolicitudesPendientes] = useState(0);
  const [procesosAsincronicos, setProcesosAsincronicos] = useState([]);
  const [procesosAsincronicosLoading, setProcesosAsincronicosLoading] = useState(false);
  const [procesosAsincronicosError, setProcesosAsincronicosError] = useState('');
  const [procesosAsincronicosNotice, setProcesosAsincronicosNotice] = useState('');
  const [selectedProcesoId, setSelectedProcesoId] = useState(null);
  const [selectedProcesoDetalle, setSelectedProcesoDetalle] = useState(null);
  const [selectedProcesoDetalleLoading, setSelectedProcesoDetalleLoading] = useState(false);
  const [selectedProcesoDetalleError, setSelectedProcesoDetalleError] = useState('');
  const filteredMenuItems = useMemo(() => {
    return filterAdminMenuItems(ADMIN_DASHBOARD_MENU_ITEMS, user, featureFlags);
  }, [featureFlags, user]);

  const activeColegio = useMemo(() => ColegioService.getActiveColegio(), []);
  const activeColegioId = useMemo(() => ColegioService.getActiveColegioId(), []);
  const activeColegioName = activeColegio?.nombre || activeColegio?.name || activeColegio?.descripcion || 'Colegio seleccionado';
  const profesoresTemplateUrl = `${process.env.PUBLIC_URL || ''}/templates/plantilla-importacion-profesores.xlsx`;
  const areasTemplateUrl = `${process.env.PUBLIC_URL || ''}/templates/plantilla-importacion-areas.xlsx`;
  const materiasTemplateUrl = `${process.env.PUBLIC_URL || ''}/templates/plantilla-importacion-materias.xlsx`;
  const solicitudesPollMinutes = useMemo(() => {
    const raw = process.env.REACT_APP_ADMIN_SOLICITUDES_POLL_MINUTES;
    const parsed = raw ? Number(raw) : 1;
    if (!Number.isFinite(parsed) || parsed <= 0) return 1;
    return parsed;
  }, []);

  

  const handleBackToSelectColegio = () => {
    ColegioService.clearColegioData();
    navigate('/admin/select-colegio', { replace: true });
  };

  useEffect(() => {
    if (!activeColegioId) {
      navigate('/admin/select-colegio', { replace: true });
    }
  }, [activeColegioId, navigate]);

  const loadSolicitudesRecibidas = useCallback(async () => {
    if (!isGeneralAdmin || !activeColegioId) {
      setSolicitudesRecibidas([]);
      setSolicitudesPendientes(0);
      setSolicitudesRecibidasError('');
      return;
    }
    setSolicitudesRecibidasLoading(true);
    setSolicitudesRecibidasError('');
    try {
      const response = await solicitudesService.getSolicitudesRecibidasGptClasicos({ skip: 0, take: 20 });
      const items = response?.items || response?.Items || [];
      const pendientes = response?.totalPendientes ?? response?.TotalPendientes ?? 0;
      setSolicitudesRecibidas(Array.isArray(items) ? items : []);
      setSolicitudesPendientes(Number.isFinite(pendientes) ? pendientes : 0);
    } catch (error) {
      setSolicitudesRecibidasError(error.message || 'No pudimos cargar las solicitudes.');
      setSolicitudesRecibidas([]);
      setSolicitudesPendientes(0);
    } finally {
      setSolicitudesRecibidasLoading(false);
    }
  }, [activeColegioId, isGeneralAdmin]);

  useEffect(() => {
    if (!isGeneralAdmin) return undefined;
    loadSolicitudesRecibidas();
    const intervalMs = solicitudesPollMinutes * 60 * 1000;
    const intervalId = setInterval(loadSolicitudesRecibidas, intervalMs);
    return () => clearInterval(intervalId);
  }, [isGeneralAdmin, loadSolicitudesRecibidas, solicitudesPollMinutes]);

  useEffect(() => {
    if (!hasModuloAlumnos && (activeMenuItem === 'alumnos' || activeMenuItem === 'aulas')) {
      setActiveMenuItem('resumen');
      return;
    }
    if (!hasOpenAIChats && activeMenuItem === 'asistentes') {
      setActiveMenuItem('resumen');
    }
  }, [activeMenuItem, hasModuloAlumnos, hasOpenAIChats]);

  

  useEffect(() => {
    if (location.state?.tab) {
      const requested = location.state.tab === 'colegios' ? 'profesores' : location.state.tab;
      if (requested === 'aulas-docentes') {
        navigate('/aulas-docentes', { replace: true });
        return;
      }
      if (requested === 'mensajes') {
        navigate('/mensajes', { replace: true });
        return;
      }
      setActiveMenuItem(requested);
    }

    if (location.state?.selectedProcesoId) {
      setSelectedProcesoId(location.state.selectedProcesoId);
    }

    if (location.state?.procesosNotice) {
      setProcesosAsincronicosNotice(location.state.procesosNotice);
    }
  }, [location.state, navigate]);

  useEffect(() => {
    if (!filteredMenuItems.some((item) => item.id === activeMenuItem)) {
      setActiveMenuItem(filteredMenuItems[0]?.id || 'resumen');
    }
  }, [activeMenuItem, filteredMenuItems]);

  const getRange = useCallback((page, currentPageSize = pageSize) => {
    const safePageSize = Math.max(Number(currentPageSize) || pageSize, 1);
    const start = (page - 1) * safePageSize;
    const end = start + safePageSize - 1;
    return { start, end };
  }, [pageSize]);

  const clampPage = useCallback((page, total, setPage, currentPageSize = pageSize) => {
    const safePageSize = Math.max(Number(currentPageSize) || pageSize, 1);
    const maxPage = Math.max(1, Math.ceil(total / safePageSize));
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [pageSize]);

  const loadProfesores = useCallback(async () => {
    if (!activeColegioId) return;

    setProfesoresLoading(true);
    setProfesoresError('');

    try {
      const { start, end } = getRange(profesoresPage, profesoresPageSize);
      const response = await usuarioService.getProfesoresPorColegio(activeColegioId, { start, end, includeTotal: true });
      const lista = response?.items || [];
      setProfesores(Array.isArray(lista) ? lista : []);
      setProfesoresTotal(response?.total ?? (Array.isArray(lista) ? lista.length : 0));
    } catch (error) {
      setProfesoresError(error.message || 'No pudimos cargar los profesores de este colegio.');
      setProfesoresTotal(0);
    } finally {
      setProfesoresLoading(false);
    }
  }, [activeColegioId, getRange, profesoresPage, profesoresPageSize]);

  const loadProcesosAsincronicos = useCallback(async () => {
    if (!activeColegioId) return;

    setProcesosAsincronicosLoading(true);
    setProcesosAsincronicosError('');

    try {
      const response = await procesosAsincronicosService.getProcesos(activeColegioId);
      const items = response?.items || response?.Items || [];
      const normalized = Array.isArray(items) ? items : [];
      setProcesosAsincronicos(normalized);

      if (selectedProcesoId) {
        const stillExists = normalized.some((item) => (item.id || item.Id) === selectedProcesoId);
        if (!stillExists) {
          setSelectedProcesoId(null);
          setSelectedProcesoDetalle(null);
          setSelectedProcesoDetalleError('');
        }
      }
    } catch (error) {
      setProcesosAsincronicosError(error.message || 'No pudimos cargar las solicitudes de procesos.');
      setProcesosAsincronicos([]);
    } finally {
      setProcesosAsincronicosLoading(false);
    }
  }, [activeColegioId, selectedProcesoId]);

  const loadProcesoDetalle = useCallback(async (procesoId) => {
    if (!procesoId) return;

    setSelectedProcesoDetalleLoading(true);
    setSelectedProcesoDetalleError('');

    try {
      const response = await procesosAsincronicosService.getProcesoDetalle(procesoId, activeColegioId);
      setSelectedProcesoDetalle(response);
    } catch (error) {
      setSelectedProcesoDetalle(null);
      setSelectedProcesoDetalleError(error.message || 'No pudimos obtener el detalle del proceso.');
    } finally {
      setSelectedProcesoDetalleLoading(false);
    }
  }, [activeColegioId]);

  const refreshSelectedProcesoDetalle = useCallback(async () => {
    if (!selectedProcesoId) return;
    await loadProcesoDetalle(selectedProcesoId);
  }, [loadProcesoDetalle, selectedProcesoId]);

  const loadAlumnos = useCallback(async () => {
    if (!activeColegioId) return;

    setAlumnosLoading(true);
    setAlumnosError('');

    try {
      const { start, end } = getRange(alumnosPage, alumnosPageSize);
      const response = await usuarioService.getAlumnosPorColegio(activeColegioId, { start, end, includeTotal: true });
      const lista = response?.items || [];
      setAlumnos(Array.isArray(lista) ? lista : []);
      setAlumnosTotal(response?.total ?? (Array.isArray(lista) ? lista.length : 0));
    } catch (error) {
      const msg = error.message || 'No pudimos cargar los alumnos de este colegio.';
      const friendly = msg.toLowerCase().includes('rol alumno no configurado')
        ? 'No hay alumnos asociados a este colegio.'
        : msg;
      setAlumnosError(friendly);
      setAlumnosTotal(0);
    } finally {
      setAlumnosLoading(false);
    }
  }, [activeColegioId, getRange, alumnosPage, alumnosPageSize]);

  const loadAdminAreaUsers = useCallback(async () => {
    setAdminAreaLoading(true);
    setAdminAreaError('');
    try {
      if (!activeColegioId) {
        setAdminAreaUsers([]);
        setAdminAreaTotal(0);
        return;
      }
      const { start, end } = getRange(adminAreaPage, adminAreaPageSize);
      const response = await usuarioService.getAdminAreaUsersPorColegio(activeColegioId, { start, end, includeTotal: true });
      const lista = response?.items || [];
      setAdminAreaUsers(Array.isArray(lista) ? lista : []);
      setAdminAreaTotal(response?.total ?? (Array.isArray(lista) ? lista.length : 0));
    } catch (error) {
      setAdminAreaError(error.message || 'No pudimos cargar los admins de área.');
      setAdminAreaTotal(0);
    } finally {
      setAdminAreaLoading(false);
    }
  }, [activeColegioId, getRange, adminAreaPage, adminAreaPageSize]);

  const loadMaterias = useCallback(async () => {
    setMateriasLoading(true);
    setMateriasError('');
    try {
      const { start, end } = getRange(materiasPage, materiasPageSize);
      const response = await materiaService.getMaterias({
        idColegio: activeColegioId,
        start,
        end,
        cursoFiltro: materiasCursoFilter,
        includeTotal: true
      });
      const lista = response?.items || [];
      setMaterias(Array.isArray(lista) ? lista : []);
      setMateriasTotal(response?.total ?? (Array.isArray(lista) ? lista.length : 0));
      setMateriasSinCursosTotal(response?.totalSinCursosAsociados ?? 0);
    } catch (error) {
      setMateriasError(error.message || 'No pudimos cargar las materias.');
      setMateriasTotal(0);
      setMateriasSinCursosTotal(0);
    } finally {
      setMateriasLoading(false);
    }
  }, [activeColegioId, getRange, materiasCursoFilter, materiasPage, materiasPageSize]);

  const loadAulas = useCallback(async () => {
    setAulasLoading(true);
    setAulasError('');
    try {
      const { start, end } = getRange(aulasPage, aulasPageSize);
      const response = await aulaService.getAulas({
        idColegio: activeColegioId,
        start,
        end,
        search: aulasSearch.trim(),
        materiaIds: aulasMateriaIds,
        profesorIds: aulasProfesorIds,
        includeTotal: true
      });
      const lista = response?.items || [];
      setAulas(Array.isArray(lista) ? lista : []);
      setAulasTotal(response?.total ?? (Array.isArray(lista) ? lista.length : 0));
      setAulasMateriasOptions(Array.isArray(response?.materias) ? response.materias : []);
      setAulasProfesoresOptions(Array.isArray(response?.profesores) ? response.profesores : []);
      setAulasTopProfesores(Array.isArray(response?.topProfesores) ? response.topProfesores : []);
    } catch (error) {
      setAulasError(error.message || 'No pudimos cargar las aulas.');
      setAulas([]);
      setAulasTotal(0);
      setAulasTopProfesores([]);
    } finally {
      setAulasLoading(false);
    }
  }, [activeColegioId, getRange, aulasPage, aulasPageSize, aulasSearch, aulasMateriaIds, aulasProfesorIds]);

  const loadAnios = useCallback(async () => {
    try {
      const list = await materiaService.getAnios();
      setAnios(Array.isArray(list) ? list : []);
    } catch (error) {
      console.warn('No pudimos cargar años:', error);
    }
  }, []);

  const loadAreas = useCallback(async () => {
    setAreasLoading(true);
    setAreasError('');
    try {
      const { start, end } = getRange(areasPage, areasPageSize);
      const response = await areaService.getAreas(activeColegioId, { start, end, includeTotal: true });
      const list = response?.items || [];
      const arr = Array.isArray(list) ? list : [];
      setAreas(arr);
      setAreasTotal(response?.total ?? arr.length);
      // sincronizar storage para formularios de materias
      ColegioService.setColegioData({
        colegios: ColegioService.getColegios(),
        areas: arr,
        materias: ColegioService.getMaterias()
      });
    } catch (error) {
      setAreasError(error.message || 'No pudimos cargar las áreas.');
      setAreasTotal(0);
    } finally {
      setAreasLoading(false);
    }
  }, [activeColegioId, getRange, areasPage, areasPageSize]);

  const loadDivisiones = useCallback(async () => {
    if (!activeColegioId) return;

    setDivisionesLoading(true);
    setDivisionesError('');
    try {
      const { start, end } = getRange(divisionesPage, divisionesPageSize);
      const response = await divisionService.getDivisiones(activeColegioId, { start, end, includeTotal: true });
      const list = response?.items || [];
      const arr = Array.isArray(list) ? list : [];
      setDivisiones(arr);
      setDivisionesTotal(response?.total ?? arr.length);
    } catch (error) {
      setDivisionesError(error.message || 'No pudimos cargar las divisiones.');
      setDivisiones([]);
      setDivisionesTotal(0);
    } finally {
      setDivisionesLoading(false);
    }
  }, [activeColegioId, divisionesPage, divisionesPageSize, getRange]);

  const loadDashboard = useCallback(async () => {
    if (!activeColegioId) return;
    setDashboardLoading(true);
    setDashboardError('');
    try {
      const data = await adminDashboardService.getDashboard(activeColegioId);
      setDashboardData(data || {});
    } catch (error) {
      setDashboardError(error.message || 'No pudimos cargar los indicadores.');
    } finally {
      setDashboardLoading(false);
    }
  }, [activeColegioId]);

  const loadAsistentes = useCallback(async () => {
    setAsistentesLoading(true);
    setAsistentesError('');
    setReportesError('');
    try {
      const { start, end } = getRange(asistentesPage, asistentesPageSize);
      const response = await adminAsistenteService.getAsistentes({ start, end, includeTotal: true });
      const list = response?.items || [];
      setAsistentes(Array.isArray(list) ? list : []);
      setAsistentesTotal(response?.total ?? (Array.isArray(list) ? list.length : 0));
    } catch (error) {
      setAsistentesError(error.message || 'No pudimos cargar los asistentes.');
      setAsistentesTotal(0);
    } finally {
      setAsistentesLoading(false);
    }
  }, [getRange, asistentesPage, asistentesPageSize]);

  useEffect(() => {
    if (activeMenuItem === 'resumen') {
      loadDashboard();
    }
  }, [activeMenuItem, loadDashboard]);

  const loadColegios = useCallback(async () => {
    setColegiosLoading(true);
    setColegiosError('');
    try {
      const resp = await colegioApiService.getColegios();
      const list = resp.colegios || resp.Colegios || resp.data || resp || [];
      setColegiosList(Array.isArray(list) ? list : []);
    } catch (error) {
      setColegiosError(error.message || 'No pudimos cargar los colegios.');
    } finally {
      setColegiosLoading(false);
    }
  }, []);

  const handleMenuClick = (itemId) => {
    if (itemId === 'mensajes') {
      navigate('/mensajes');
      return;
    }
    if (itemId === 'aulas-docentes') {
      navigate('/aulas-docentes');
      return;
    }
    if (itemId === 'agentes-clasicos-gpt') {
      navigate('/mis-agentes-clasicos-gpt');
      return;
    }
    setActiveMenuItem(itemId);
  };

  const handleMenuSelect = (menuId) => {
    setIsSidebarOpen(false);
    handleMenuClick(menuId);
  };

  useEffect(() => {
    if (activeMenuItem === 'profesores') {
      loadProfesores();
    } else if (activeMenuItem === 'alumnos') {
      loadAlumnos();
    } else if (activeMenuItem === 'materias') {
      loadMaterias();
      loadAnios();
    } else if (activeMenuItem === 'aulas') {
      loadAulas();
    } else if (activeMenuItem === 'areas') {
      loadAreas();
    } else if (activeMenuItem === 'divisiones') {
      loadDivisiones();
    } else if (activeMenuItem === 'asistentes') {
      loadAsistentes();
    } else if (activeMenuItem === 'procesos-asincronicos') {
      loadProcesosAsincronicos();
    }
  }, [activeMenuItem, loadProfesores, loadAlumnos, loadAdminAreaUsers, loadMaterias, loadAulas, loadAnios, loadAreas, loadDivisiones, loadAsistentes, loadProcesosAsincronicos]);

  useEffect(() => {
    if (activeMenuItem === 'procesos-asincronicos' && selectedProcesoId) {
      loadProcesoDetalle(selectedProcesoId);
    }
  }, [activeMenuItem, selectedProcesoId, loadProcesoDetalle]);

  useEffect(() => {
    if (activeMenuItem !== 'procesos-asincronicos') {
      return undefined;
    }

    const hasActiveProcesses = procesosAsincronicos.some((item) => !(item?.finalizado ?? item?.Finalizado ?? false));
    if (!hasActiveProcesses) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      loadProcesosAsincronicos();
      refreshSelectedProcesoDetalle();
    }, 20000);

    return () => clearInterval(intervalId);
  }, [activeMenuItem, procesosAsincronicos, loadProcesosAsincronicos, refreshSelectedProcesoDetalle]);

  useEffect(() => {
    clampPage(profesoresPage, profesoresTotal, setProfesoresPage, profesoresPageSize);
  }, [profesoresPage, profesoresTotal, profesoresPageSize, clampPage]);

  useEffect(() => {
    clampPage(alumnosPage, alumnosTotal, setAlumnosPage, alumnosPageSize);
  }, [alumnosPage, alumnosTotal, alumnosPageSize, clampPage]);

  useEffect(() => {
    clampPage(adminAreaPage, adminAreaTotal, setAdminAreaPage, adminAreaPageSize);
  }, [adminAreaPage, adminAreaTotal, adminAreaPageSize, clampPage]);

  useEffect(() => {
    clampPage(materiasPage, materiasTotal, setMateriasPage, materiasPageSize);
  }, [materiasPage, materiasTotal, materiasPageSize, clampPage]);

  useEffect(() => {
    clampPage(aulasPage, aulasTotal, setAulasPage, aulasPageSize);
  }, [aulasPage, aulasTotal, aulasPageSize, clampPage]);

  useEffect(() => {
    clampPage(areasPage, areasTotal, setAreasPage, areasPageSize);
  }, [areasPage, areasTotal, areasPageSize, clampPage]);

  useEffect(() => {
    clampPage(divisionesPage, divisionesTotal, setDivisionesPage, divisionesPageSize);
  }, [divisionesPage, divisionesTotal, divisionesPageSize, clampPage]);

  useEffect(() => {
    clampPage(asistentesPage, asistentesTotal, setAsistentesPage, asistentesPageSize);
  }, [asistentesPage, asistentesTotal, asistentesPageSize, clampPage]);

  const filteredProfesores = useMemo(() => {
    if (!searchTerm.trim()) return profesores;
    const term = searchTerm.toLowerCase();

    return profesores.filter((prof) => {
      const fullName = `${prof.nombre || prof.Nombre || ''} ${prof.apellido || prof.Apellido || ''} ${prof.displayName || prof.DisplayName || ''}`.toLowerCase();
      const email = (prof.email || prof.Email || '').toLowerCase();
      const dni = (prof.dni || prof.Dni || '').toLowerCase();
      return fullName.includes(term) || email.includes(term) || dni.includes(term);
    });
  }, [profesores, searchTerm]);

  const filteredAsistentes = useMemo(() => {
    if (!asistentesSearch.trim()) return asistentes;
    const term = asistentesSearch.toLowerCase();
    return asistentes.filter((a) => {
      const nombre = (a.nombre || a.Nombre || '').toLowerCase();
      const tipo = (a.nombreTipoAsistente || a.NombreTipoAsistente || '').toLowerCase();
      const area = (a.nombreArea || a.NombreArea || '').toLowerCase();
      const materia = (a.nombreMateria || a.NombreMateria || '').toLowerCase();
      const id = (a.idOpenAIAssistant || '').toLowerCase();
      return (
        nombre.includes(term) ||
        tipo.includes(term) ||
        area.includes(term) ||
        materia.includes(term) ||
        id.includes(term)
      );
    });
  }, [asistentes, asistentesSearch]);

  const paginate = (list, page) => {
    return Array.isArray(list) ? list : [];
  };

  const renderPagination = (totalItems, page, setPage, currentPageSize = pageSize, setCurrentPageSize = null) => {
    if (totalItems <= 0) return null;
    const safePageSize = Math.max(Number(currentPageSize) || pageSize, 1);
    const totalPages = Math.max(1, Math.ceil(totalItems / safePageSize));

    const currentPage = Math.min(Math.max(page, 1), totalPages);
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    const buttons = [];

    if (startPage > 1 && totalPages > 1) {
      buttons.push(
        <button key="first" className="pagination-btn" onClick={() => setPage(1)}>
          1
        </button>
      );
      if (startPage > 2) {
        buttons.push(<span key="ellipsis1" className="pagination-ellipsis">...</span>);
      }
    }

    if (totalPages > 1) {
      for (let i = startPage; i <= endPage; i++) {
        buttons.push(
          <button
            key={i}
            className={`pagination-btn ${i === currentPage ? 'active' : ''}`}
            onClick={() => setPage(i)}
          >
            {i}
          </button>
        );
      }
    } else {
      buttons.push(
        <button key="single" className="pagination-btn active" disabled>
          1
        </button>
      );
    }

    if (endPage < totalPages && totalPages > 1) {
      if (endPage < totalPages - 1) {
        buttons.push(<span key="ellipsis2" className="pagination-ellipsis">...</span>);
      }
      buttons.push(
        <button key="last" className="pagination-btn" onClick={() => setPage(totalPages)}>
          {totalPages}
        </button>
      );
    }

    const startItem = (currentPage - 1) * safePageSize + 1;
    const endItem = Math.min(currentPage * safePageSize, totalItems);

    return (
      <div className="pagination-container">
        {typeof setCurrentPageSize === 'function' && (
          <div className="items-per-page-container">
            <label htmlFor={`items-per-page-${String(totalItems)}-${String(page)}`}>Mostrar</label>
            <select
              id={`items-per-page-${String(totalItems)}-${String(page)}`}
              className="items-per-page-select"
              value={safePageSize}
              onChange={(e) => {
                const nextPageSize = Number(e.target.value) || safePageSize;
                setCurrentPageSize(nextPageSize);
                setPage(1);
              }}
            >
              {ITEMS_PER_PAGE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <span>registros</span>
          </div>
        )}
        <div className="pagination-info">
          Mostrando {startItem}-{endItem} de {totalItems}
        </div>
        <div className="pagination-controls">
          <button
            className="pagination-btn nav-btn"
            onClick={() => setPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1 || totalPages === 1}
            aria-label="Pagina anterior"
          >
            <i className="fas fa-chevron-left" />
          </button>
          {buttons}
          <button
            className="pagination-btn nav-btn"
            onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages || totalPages === 1}
            aria-label="Pagina siguiente"
          >
            <i className="fas fa-chevron-right" />
          </button>
        </div>
      </div>
    );
  };

  const getProfesorNombre = (prof) => {
    return prof.displayName || prof.DisplayName || `${prof.nombre || prof.Nombre || ''} ${prof.apellido || prof.Apellido || ''}`.trim() || prof.email || prof.Email || 'Sin nombre';
  };

  const filteredAlumnos = useMemo(() => {
    if (!alumnosSearch.trim()) return alumnos;
    const term = alumnosSearch.toLowerCase();

    return alumnos.filter((al) => {
      const fullName = `${al.nombre || al.Nombre || ''} ${al.apellido || al.Apellido || ''} ${al.displayName || al.DisplayName || ''}`.toLowerCase();
      const email = (al.email || al.Email || '').toLowerCase();
      const legajo = (al.legajo || al.Legajo || '').toLowerCase();
      return fullName.includes(term) || email.includes(term) || legajo.includes(term);
    });
  }, [alumnos, alumnosSearch]);

  const getAlumnoNombre = (al) => {
    return al.displayName || al.DisplayName || `${al.nombre || al.Nombre || ''} ${al.apellido || al.Apellido || ''}`.trim() || al.email || al.Email || 'Sin nombre';
  };

  const filteredColegios = useMemo(() => {
    if (!colegiosSearch.trim()) return colegiosList;
    const term = colegiosSearch.toLowerCase();
    return colegiosList.filter((r) => {
      const name = (r.nombre || r.Nombre || '').toLowerCase();
      const desc = (r.descripcion || r.Descripcion || '').toLowerCase();
      return name.includes(term) || desc.includes(term);
    });
  }, [colegiosList, colegiosSearch]);

  const getColegioNombre = (r) => r.nombre || r.Nombre || r.descripcion || r.Descripcion || 'Colegio';

  const filteredMaterias = useMemo(() => {
    if (!materiasSearch.trim()) return materias;
    const term = materiasSearch.toLowerCase();
    return materias.filter((m) => {
      const name = getMateriaNombre(m).toLowerCase();
      const desc = (m.descripcion || m.Descripcion || '').toLowerCase();
      const area = (m.nombreArea || m.areaNombre || m.NombreArea || m.AreaNombre || m.area || m.Area || '').toLowerCase();
      const codigo = (m.codigo || m.Codigo || '').toLowerCase();
      const division = String(m.division || m.Division || '').toLowerCase();
      const anio = String(m.valorAnio ?? m.ValorAnio ?? '').toLowerCase();
      return name.includes(term) || desc.includes(term) || area.includes(term) || codigo.includes(term) || division.includes(term) || anio.includes(term);
    });
  }, [materias, materiasSearch]);
  const deleteUnlinkedMateriasExpectedText = 'ELIMINAR MATERIAS NO ASOCIADAS';
  const canConfirmDeleteUnlinkedMaterias = normalizeConfirmText(deleteUnlinkedMateriasModal.confirmText) === deleteUnlinkedMateriasExpectedText;

  const aulasMateriaSelectOptions = useMemo(() => (
    aulasMateriasOptions
      .map((item) => {
        const id = getFilterItemId(item);
        if (!id) return null;
        const totalAulas = getFilterItemTotalAulas(item);
        const codigo = item?.codigo || item?.Codigo || '';
        return {
          value: id,
          label: getFilterItemName(item, 'Materia'),
          secondary: `${totalAulas} aula${totalAulas === 1 ? '' : 's'}${codigo ? ` · ${codigo}` : ''}`,
          searchText: codigo
        };
      })
      .filter(Boolean)
  ), [aulasMateriasOptions]);

  const aulasProfesorSelectOptions = useMemo(() => (
    aulasProfesoresOptions
      .map((item) => {
        const id = getFilterItemId(item);
        if (!id) return null;
        const totalAulas = getFilterItemTotalAulas(item);
        const email = getFilterItemEmail(item);
        return {
          value: id,
          label: getFilterItemName(item, 'Profesor'),
          secondary: `${totalAulas} aula${totalAulas === 1 ? '' : 's'}${email ? ` · ${email}` : ''}`,
          searchText: email
        };
      })
      .filter(Boolean)
  ), [aulasProfesoresOptions]);

  const aulasProfesoresSinAulas = useMemo(() => (
    aulasProfesoresOptions
      .filter((item) => getFilterItemTotalAulas(item) === 0)
      .sort((a, b) => getFilterItemName(a, 'Profesor').localeCompare(getFilterItemName(b, 'Profesor'), 'es'))
  ), [aulasProfesoresOptions]);

  const hasAulasFilters = aulasSearch.trim() || aulasMateriaIds.length > 0 || aulasProfesorIds.length > 0;

  const filteredAulas = useMemo(() => {
    if (!aulasSearch.trim()) return aulas;
    const term = aulasSearch.toLowerCase();
    return aulas.filter((a) => {
      const name = (a.nombre || a.Nombre || '').toLowerCase();
      const materia = (a.nombreMateria || a.NombreMateria || '').toLowerCase();
      const profesor = (a.nombreProfesor || a.NombreProfesor || '').toLowerCase();
      const ciclo = String(a.cicloLectivo || a.CicloLectivo || '').toLowerCase();
      const codigo = (a.codigoSuscripcion || a.CodigoSuscripcion || '').toLowerCase();
      return name.includes(term) || materia.includes(term) || profesor.includes(term) || ciclo.includes(term) || codigo.includes(term);
    });
  }, [aulas, aulasSearch]);

  const getAulaNombre = (a) => a.nombre || a.Nombre || 'Aula';

  const filteredAreas = useMemo(() => {
    if (!areasSearch.trim()) return areas;
    const term = areasSearch.toLowerCase();
    return areas.filter((a) => {
      const name = (a.nombre || a.Nombre || '').toLowerCase();
      const desc = (a.descripcion || a.Descripcion || '').toLowerCase();
      return name.includes(term) || desc.includes(term);
    });
  }, [areas, areasSearch]);

  const getAreaNombre = (a) => a.nombre || a.Nombre || 'Área';

  const filteredDivisiones = useMemo(() => {
    if (!divisionesSearch.trim()) return divisiones;
    const term = divisionesSearch.toLowerCase();
    return divisiones.filter((d) => {
      const name = (d.nombre || d.Nombre || '').toLowerCase();
      const desc = (d.descripcion || d.Descripcion || '').toLowerCase();
      return name.includes(term) || desc.includes(term);
    });
  }, [divisiones, divisionesSearch]);

  const getDivisionNombre = (d) => d.nombre || d.Nombre || 'División';

  const filteredAdminAreaUsers = useMemo(() => {
    if (!adminAreaSearch.trim()) return adminAreaUsers;
    const term = adminAreaSearch.toLowerCase();
    return adminAreaUsers.filter((u) => {
      const name = `${u.nombre || u.Nombre || ''} ${u.apellido || u.Apellido || ''} ${u.displayName || u.DisplayName || ''}`.toLowerCase();
      const email = (u.email || u.Email || '').toLowerCase();
      return name.includes(term) || email.includes(term);
    });
  }, [adminAreaUsers, adminAreaSearch]);

  const getResultMessage = (result = {}) => {
    return result.mensaje || result.Mensaje || result.message || result.Message || result.error || '';
  };

  const isProfesorActivo = (prof) => {
    if (typeof prof.activo === 'boolean') return prof.activo;
    if (typeof prof.Activo === 'boolean') return prof.Activo;
    return true;
  };

  const isAlumnoActivo = (al) => {
    if (typeof al.activo === 'boolean') return al.activo;
    if (typeof al.Activo === 'boolean') return al.Activo;
    return true;
  };

  const isAdminAreaActivo = (adminUser) => {
    if (typeof adminUser.activo === 'boolean') return adminUser.activo;
    if (typeof adminUser.Activo === 'boolean') return adminUser.Activo;
    return true;
  };

  const openToggleModal = (prof) => {
    const currentActive = isProfesorActivo(prof);
    setConfirmModal({
      open: true,
      profesor: prof,
      nextActivo: !currentActive
    });
  };

  const closeToggleModal = () => {
    setConfirmModal({ open: false, profesor: null, nextActivo: true });
  };

  const handleToggleProfesor = async () => {
    if (!confirmModal.profesor) return;
    const profesorId = confirmModal.profesor.id || confirmModal.profesor.Id;
    const nextActivo = confirmModal.nextActivo;

    if (!profesorId) {
      setProfesoresError('No pudimos identificar al profesor seleccionado.');
      closeToggleModal();
      return;
    }

    setToggleLoading(true);
    setProfesoresError('');

    try {
      await usuarioService.updateProfesorActivo(profesorId, nextActivo);

      setProfesores((prev) =>
        prev.map((p) => {
          const pid = p.id || p.Id;
          if (pid === profesorId) {
            return { ...p, activo: nextActivo, Activo: nextActivo };
          }
          return p;
        })
      );
    } catch (error) {
      setProfesoresError(error.message || 'No pudimos actualizar el estado del profesor.');
    } finally {
      setToggleLoading(false);
      closeToggleModal();
    }
  };

  const openDisassociateModal = (prof) => {
    setDisassociateModal({ open: true, profesor: prof });
  };

  const closeDisassociateModal = () => {
    setDisassociateModal({ open: false, profesor: null });
  };

  const handleDisassociate = async () => {
    if (!disassociateModal.profesor) return;
    const profesorId = disassociateModal.profesor.id || disassociateModal.profesor.Id;

    if (!profesorId || !activeColegioId) {
      setProfesoresError('No pudimos identificar al profesor o el colegio.');
      closeDisassociateModal();
      return;
    }

    setDisassociateLoading(true);
    setProfesoresError('');

    try {
      await usuarioService.desasociarProfesorDeColegio(profesorId, activeColegioId);
      await loadProfesores();
    } catch (error) {
      setProfesoresError(error.message || 'No pudimos desasociar al profesor.');
    } finally {
      setDisassociateLoading(false);
      closeDisassociateModal();
    }
  };

  const openResetProfesorModal = (prof) => {
    setResetProfesorModal({ open: true, profesor: prof });
  };

  const closeResetProfesorModal = () => {
    setResetProfesorModal({ open: false, profesor: null });
    setResetProfesorLoading(false);
  };

  const handleResetProfesorPassword = async () => {
    if (!resetProfesorModal.profesor) return;
    const profesorId = resetProfesorModal.profesor.id || resetProfesorModal.profesor.Id;

    if (!profesorId) {
      setResetProfesorResult({ open: true, ok: false, message: 'No pudimos identificar al profesor seleccionado.' });
      closeResetProfesorModal();
      return;
    }

    setResetProfesorLoading(true);
    try {
      await usuarioService.resetProfesorPassword(profesorId);
      setResetProfesorResult({ open: true, ok: true, message: 'Contraseña reseteada: ahora será su DNI.' });
    } catch (error) {
      setResetProfesorResult({ open: true, ok: false, message: error.message || 'No pudimos resetear la contraseña.' });
    } finally {
      setResetProfesorLoading(false);
      closeResetProfesorModal();
    }
  };

  // Alumnos - acciones
  const openAlumnoAssociateModal = () => {
    setAlumnoAssociateModalOpen(true);
    setAlumnoAssociateEmail('');
    setAlumnoAssociateMessage('');
    setAlumnoAssociateSuccess(false);
  };

  const closeAlumnoAssociateModal = () => {
    setAlumnoAssociateModalOpen(false);
    setAlumnoAssociateLoading(false);
    setAlumnoAssociateMessage('');
    setAlumnoAssociateSuccess(false);
  };

  const handleValidateAndAssociateAlumno = async () => {
    if (!alumnoAssociateEmail.trim()) {
      setAlumnoAssociateMessage('Debes ingresar un email.');
      return;
    }
    if (!activeColegioId) {
      setAlumnoAssociateMessage('Selecciona un colegio antes de asociar alumnos.');
      return;
    }

    setAlumnoAssociateLoading(true);
    setAlumnoAssociateMessage('');

    try {
      const result = await usuarioService.validateAlumnoPorEmail(alumnoAssociateEmail.trim(), activeColegioId);
    const message = getResultMessage(result) || 'Validación completada.';
    const asociado = result.asociado ?? result.Asociado ?? false;
    const asociacionCreada = result.asociacionCreada ?? result.AsociacionCreada ?? false;

      if (!asociado && !result.alumnoExiste && !result.AlumnoExiste) {
        closeAlumnoAssociateModal();
        setAlumnoMissingModal({ open: true, email: alumnoAssociateEmail.trim() });
        return;
      }

      if (asociado && asociacionCreada) {
        setAlumnoAssociateMessage('Alumno asociado exitosamente');
        setAlumnoAssociateSuccess(true);
        await loadAlumnos();
      } else if (asociado && !asociacionCreada) {
        setAlumnoAssociateMessage(message || 'El alumno ya está asociado al colegio.');
        setAlumnoAssociateSuccess(true);
      } else {
        setAlumnoAssociateMessage(message);
      }
    } catch (error) {
      setAlumnoAssociateMessage(error.message || 'No pudimos validar al alumno.');
    } finally {
      setAlumnoAssociateLoading(false);
    }
  };

  const openAlumnoDisassociateModal = (al) => {
    setAlumnoDisassociateModal({ open: true, alumno: al });
  };

  const closeAlumnoDisassociateModal = () => {
    setAlumnoDisassociateModal({ open: false, alumno: null });
  };

  const handleAlumnoDisassociate = async () => {
    if (!alumnoDisassociateModal.alumno) return;
    const alumnoId = alumnoDisassociateModal.alumno.id || alumnoDisassociateModal.alumno.Id;

    if (!alumnoId || !activeColegioId) {
      setAlumnosError('No pudimos identificar al alumno o el colegio.');
      closeAlumnoDisassociateModal();
      return;
    }

    setAlumnoDisassociateLoading(true);
    setAlumnosError('');

    try {
      await usuarioService.desasociarAlumnoDeColegio(alumnoId, activeColegioId);
      await loadAlumnos();
    } catch (error) {
      setAlumnosError(error.message || 'No pudimos desasociar al alumno.');
    } finally {
      setAlumnoDisassociateLoading(false);
      closeAlumnoDisassociateModal();
    }
  };

  const handleEditAlumno = (al) => {
    const alumnoId = al.id || al.Id;
    if (!alumnoId) {
      setAlumnosError('No pudimos identificar al alumno seleccionado.');
      return;
    }

    navigate(`/admin/alumnos/${alumnoId}/editar`, {
      replace: false,
      state: {
        alumno: al,
        idColegio: activeColegioId
      }
    });
  };

  const openResetAlumnoModal = (al) => {
    setResetAlumnoModal({ open: true, alumno: al });
  };

  const openAlumnoFamiliaresModal = (al) => {
    setAlumnoFamiliaresModal({ open: true, alumno: al });
  };

  const closeAlumnoFamiliaresModal = () => {
    setAlumnoFamiliaresModal({ open: false, alumno: null });
  };

  const closeResetAlumnoModal = () => {
    setResetAlumnoModal({ open: false, alumno: null });
    setResetAlumnoLoading(false);
  };

  const handleResetAlumnoPassword = async () => {
    if (!resetAlumnoModal.alumno) return;
    const alumnoId = resetAlumnoModal.alumno.id || resetAlumnoModal.alumno.Id;

    if (!alumnoId) {
      setResetAlumnoResult({ open: true, ok: false, message: 'No pudimos identificar al alumno seleccionado.' });
      closeResetAlumnoModal();
      return;
    }

    setResetAlumnoLoading(true);
    try {
      await usuarioService.resetAlumnoPassword(alumnoId);
      setResetAlumnoResult({
        open: true,
        ok: true,
        message: 'Contraseña reseteada: el nuevo password es 123456. El alumno deberá cambiarlo luego.'
      });
    } catch (error) {
      setResetAlumnoResult({ open: true, ok: false, message: error.message || 'No pudimos resetear la contraseña.' });
    } finally {
      setResetAlumnoLoading(false);
      closeResetAlumnoModal();
    }
  };

  const openAdminAreaToggleModal = (user) => {
    const currentActive = isAdminAreaActivo(user);
    setAdminAreaToggleModal({
      open: true,
      user,
      nextActivo: !currentActive
    });
  };

  const closeAdminAreaToggleModal = () => {
    setAdminAreaToggleModal({ open: false, user: null, nextActivo: true });
  };

  const handleToggleAdminArea = async () => {
    if (!adminAreaToggleModal.user) return;
    const userId = adminAreaToggleModal.user.id || adminAreaToggleModal.user.Id;
    const nextActivo = adminAreaToggleModal.nextActivo;

    if (!userId) {
      setAdminAreaError('No pudimos identificar al usuario de área.');
      closeAdminAreaToggleModal();
      return;
    }

    setAdminAreaToggleLoading(true);
    try {
      await usuarioService.updateAdminAreaActivo(userId, nextActivo);

      setAdminAreaUsers((prev) =>
        prev.map((u) => {
          const uid = u.id || u.Id;
          if (uid === userId) {
            return { ...u, activo: nextActivo, Activo: nextActivo };
          }
          return u;
        })
      );
    } catch (error) {
      setAdminAreaError(error.message || 'No pudimos actualizar el estado del usuario.');
    } finally {
      setAdminAreaToggleLoading(false);
      closeAdminAreaToggleModal();
    }
  };

  const handleEditAdminArea = (user) => {
    const userId = user.id || user.Id || 'nuevo';
    navigate(`/admin/admin-area/${userId}/editar`, {
      replace: false,
      state: {
        adminAreaUser: user,
        idColegio: activeColegioId
      }
    });
  };

  const handleCreateAdminArea = () => {
    navigate('/admin/admin-area/nuevo', {
      replace: false,
      state: {
        idColegio: activeColegioId
      }
    });
  };

  // Áreas acciones
  const handleCreateArea = () => {
    navigate('/admin/areas/nuevo', {
      replace: false,
      state: {
        idColegio: activeColegioId
      }
    });
  };

  const handleEditArea = (area) => {
    const areaId = area.id || area.Id || 'nuevo';
    navigate(`/admin/areas/${areaId}/editar`, {
      replace: false,
      state: {
        area,
        idColegio: activeColegioId
      }
    });
  };

  const openAreaToggleModal = (area) => {
    const currentActive = typeof area.activo === 'boolean' ? area.activo : typeof area.Activo === 'boolean' ? area.Activo : true;
    setAreaToggleModal({ open: true, area, nextActivo: !currentActive });
  };

  const closeAreaToggleModal = () => {
    setAreaToggleModal({ open: false, area: null, nextActivo: true });
  };

  const handleToggleArea = async () => {
    if (!areaToggleModal.area) return;
    const areaId = areaToggleModal.area.id || areaToggleModal.area.Id;
    const nextActivo = areaToggleModal.nextActivo;

    if (!areaId) {
      setAreasError('No pudimos identificar el área seleccionada.');
      closeAreaToggleModal();
      return;
    }

    setAreaToggleLoading(true);
    setAreasError('');

    try {
      await areaService.updateAreaActivo(areaId, nextActivo);

      setAreas((prev) =>
        prev.map((a) => {
          const aid = a.id || a.Id;
          if (aid === areaId) {
            return { ...a, activo: nextActivo, Activo: nextActivo };
          }
          return a;
        })
      );
    } catch (error) {
      setAreasError(error.message || 'No pudimos actualizar el estado del área.');
    } finally {
      setAreaToggleLoading(false);
      closeAreaToggleModal();
    }
  };

  const handleCreateDivision = () => {
    navigate('/admin/divisiones/nuevo', {
      replace: false,
      state: {
        idColegio: activeColegioId
      }
    });
  };

  const handleEditDivision = (division) => {
    const divisionId = division.id || division.Id || 'nuevo';
    navigate(`/admin/divisiones/${divisionId}/editar`, {
      replace: false,
      state: {
        division,
        idColegio: activeColegioId
      }
    });
  };

  const openDivisionDeleteModal = (division) => {
    setDivisionDeleteModal({ open: true, division });
  };

  const closeDivisionDeleteModal = () => {
    if (divisionDeleteLoading) return;
    setDivisionDeleteModal({ open: false, division: null });
  };

  const handleDeleteDivision = async () => {
    if (!divisionDeleteModal.division) return;
    const divisionId = divisionDeleteModal.division.id || divisionDeleteModal.division.Id;

    if (!divisionId) {
      setDivisionesError('No pudimos identificar la división seleccionada.');
      closeDivisionDeleteModal();
      return;
    }

    setDivisionDeleteLoading(true);
    setDivisionesError('');
    try {
      await divisionService.deleteDivision(divisionId);
      await loadDivisiones();
    } catch (error) {
      setDivisionesError(error.message || 'No pudimos eliminar la división.');
    } finally {
      setDivisionDeleteLoading(false);
      closeDivisionDeleteModal();
    }
  };

  // Materias acciones (placeholder hasta integrar endpoints)
  const handleCreateMateria = () => {
    navigate('/admin/materias/nuevo', {
      replace: false,
      state: {
        idColegio: activeColegioId
      }
    });
  };

  const handleEditMateria = (materia) => {
    const materiaId = materia.id || materia.Id || 'nuevo';
    navigate(`/admin/materias/${materiaId}/editar`, {
      replace: false,
      state: {
        materia,
        idColegio: activeColegioId
      }
    });
  };

  const openMateriaToggleModal = (materia) => {
    const currentActive = typeof materia.activo === 'boolean' ? materia.activo : typeof materia.Activo === 'boolean' ? materia.Activo : true;
    setMateriaToggleModal({
      open: true,
      materia,
      nextActivo: !currentActive
    });
  };

  const closeMateriaToggleModal = () => {
    setMateriaToggleModal({ open: false, materia: null, nextActivo: true });
  };

  const openDeleteUnlinkedMateriasModal = () => {
    setDeleteUnlinkedMateriasModal({
      open: true,
      confirmText: '',
      loading: false,
      error: ''
    });
    setMateriasNotice('');
    setMateriasError('');
  };

  const closeDeleteUnlinkedMateriasModal = () => {
    if (deleteUnlinkedMateriasModal.loading) return;
    setDeleteUnlinkedMateriasModal({
      open: false,
      confirmText: '',
      loading: false,
      error: ''
    });
  };

  const handleDeleteUnlinkedMaterias = async () => {
    if (!activeColegioId) {
      setDeleteUnlinkedMateriasModal((prev) => ({
        ...prev,
        error: 'No encontramos el colegio activo para ejecutar la limpieza.'
      }));
      return;
    }

    setDeleteUnlinkedMateriasModal((prev) => ({
      ...prev,
      loading: true,
      error: ''
    }));
    setMateriasNotice('');
    setMateriasError('');

    try {
      const response = await materiaService.deleteMateriasNoAsociadas(activeColegioId);
      const eliminadas = Number(response?.eliminadas ?? response?.Eliminadas ?? 0);
      const omitidas = Number(response?.omitidasPorRecursos ?? response?.OmitidasPorRecursos ?? 0);
      const detectadas = Number(response?.sinCursosDetectadas ?? response?.SinCursosDetectadas ?? 0);
      let notice = `Limpieza completada: ${eliminadas} materia${eliminadas === 1 ? '' : 's'} eliminada${eliminadas === 1 ? '' : 's'}.`;
      if (detectadas === 0) {
        notice = 'No encontramos materias sin cursos asociados para eliminar.';
      } else if (omitidas > 0) {
        notice += ` ${omitidas} materia${omitidas === 1 ? '' : 's'} se omitieron porque todavía tienen dependencias activas.`;
      }

      setMateriasNotice(notice);
      await loadMaterias();
      setDeleteUnlinkedMateriasModal({
        open: false,
        confirmText: '',
        loading: false,
        error: ''
      });
    } catch (error) {
      setDeleteUnlinkedMateriasModal((prev) => ({
        ...prev,
        loading: false,
        error: error.message || 'No pudimos eliminar las materias no asociadas.'
      }));
    }
  };

  const handleToggleMateria = async () => {
    if (!materiaToggleModal.materia) return;
    const materiaId = materiaToggleModal.materia.id || materiaToggleModal.materia.Id;
    const nextActivo = materiaToggleModal.nextActivo;

    if (!materiaId) {
      setMateriasError('No pudimos identificar la materia seleccionada.');
      closeMateriaToggleModal();
      return;
    }

    setMateriaToggleLoading(true);
    setMateriasError('');

    try {
      await materiaService.updateMateriaActivo(materiaId, nextActivo);

      setMaterias((prev) =>
        prev.map((m) => {
          const mid = m.id || m.Id;
          if (mid === materiaId) {
            return { ...m, activo: nextActivo, Activo: nextActivo };
          }
          return m;
        })
      );
    } catch (error) {
      setMateriasError(error.message || 'No pudimos actualizar la materia.');
    } finally {
      setMateriaToggleLoading(false);
      closeMateriaToggleModal();
    }
  };

  // Aulas acciones
  const handleCreateAula = () => {
    navigate('/admin/aulas/nuevo', {
      replace: false,
      state: {
        idColegio: activeColegioId
      }
    });
  };

  const handleEditAula = (aula) => {
    const aulaId = aula.id || aula.Id || 'nuevo';
    navigate(`/admin/aulas/${aulaId}/editar`, {
      replace: false,
      state: {
        aula,
        idColegio: activeColegioId
      }
    });
  };

  const handleOpenAula = (aula) => {
    const aulaId = aula?.id || aula?.Id;
    if (!aulaId || typeof window === 'undefined') return;

    const params = new URLSearchParams({
      id: aulaId,
      adminAccess: '1'
    });
    window.open(`${window.location.origin}/aula?${params.toString()}`, '_blank', 'noopener,noreferrer');
  };

  const openAulaToggleModal = (aula) => {
    const currentActive = typeof aula.activo === 'boolean' ? aula.activo : typeof aula.Activo === 'boolean' ? aula.Activo : true;
    setAulaToggleModal({
      open: true,
      aula,
      nextActivo: !currentActive
    });
  };

  const closeAulaToggleModal = () => {
    setAulaToggleModal({ open: false, aula: null, nextActivo: true });
  };

  const handleToggleAula = async () => {
    if (!aulaToggleModal.aula) return;
    const aulaId = aulaToggleModal.aula.id || aulaToggleModal.aula.Id;
    const nextActivo = aulaToggleModal.nextActivo;

    if (!aulaId) {
      setAulasError('No pudimos identificar el aula seleccionada.');
      closeAulaToggleModal();
      return;
    }

    setAulaToggleLoading(true);
    setAulasError('');

    try {
      await aulaService.updateAulaActivo(aulaId, nextActivo);

      setAulas((prev) =>
        prev.map((a) => {
          const aid = a.id || a.Id;
          if (aid === aulaId) {
            return { ...a, activo: nextActivo, Activo: nextActivo };
          }
          return a;
        })
      );
    } catch (error) {
      setAulasError(error.message || 'No pudimos actualizar el estado del aula.');
    } finally {
      setAulaToggleLoading(false);
      closeAulaToggleModal();
    }
  };

  const handleAulaUpdated = useCallback((updatedAula) => {
    const updatedId = updatedAula?.id || updatedAula?.Id;
    if (!updatedId) return;
    setAulaResourcesModal((prev) => (
      prev.aula && (prev.aula.id || prev.aula.Id) === updatedId
        ? { ...prev, aula: { ...prev.aula, ...updatedAula } }
        : prev
    ));
    setAulas((prev) =>
      prev.map((item) => ((item.id || item.Id) === updatedId ? { ...item, ...updatedAula } : item))
    );
  }, []);

  // Colegios acciones
  const openColegioToggleModal = (colegio) => {
    const currentActive = typeof colegio.activo === 'boolean' ? colegio.activo : typeof colegio.Activo === 'boolean' ? colegio.Activo : true;
    setColegioToggleModal({
      open: true,
      colegio,
      nextActivo: !currentActive
    });
  };

  const closeColegioToggleModal = () => {
    setColegioToggleModal({ open: false, colegio: null, nextActivo: true });
  };

  const handleToggleColegio = async () => {
    if (!colegioToggleModal.colegio) return;
    const regId = colegioToggleModal.colegio.id || colegioToggleModal.colegio.Id;
    const nextActivo = colegioToggleModal.nextActivo;

    if (!regId) {
      setColegiosError('No pudimos identificar el colegio.');
      closeColegioToggleModal();
      return;
    }

    setColegioToggleLoading(true);
    setColegiosError('');

    try {
      await colegioApiService.updateColegio(regId, {
        nombre: colegioToggleModal.colegio.nombre || colegioToggleModal.colegio.Nombre,
        descripcion: colegioToggleModal.colegio.descripcion || colegioToggleModal.colegio.Descripcion,
        activo: nextActivo
      });

      setColegiosList((prev) =>
        prev.map((r) => {
          const rid = r.id || r.Id;
          if (rid === regId) {
            return { ...r, activo: nextActivo, Activo: nextActivo };
          }
          return r;
        })
      );
    } catch (error) {
      setColegiosError(error.message || 'No pudimos actualizar el colegio.');
    } finally {
      setColegioToggleLoading(false);
      closeColegioToggleModal();
    }
  };

  const handleEditColegio = (colegio) => {
    const regId = colegio.id || colegio.Id;
    if (!regId) {
      setColegiosError('No pudimos identificar el colegio seleccionado.');
      return;
    }

    navigate(`/admin/colegios/${regId}/editar`, {
      replace: false,
      state: {
        colegio,
        idColegio: activeColegioId
      }
    });
  };

  const handleViewColegioStats = (colegio) => {
    const regId = colegio?.id || colegio?.Id;
    if (!regId) {
      setColegiosError('No pudimos identificar el colegio seleccionado.');
      return;
    }

    navigate(`/admin/colegios/${regId}/estadisticas`, {
      replace: false,
      state: {
        colegio
      }
    });
  };

  const handleCreateColegio = () => {
    navigate('/admin/colegios/nuevo', { replace: false });
  };

  const openAlumnoToggleModal = (al) => {
    const currentActive = isAlumnoActivo(al);
    setAlumnoToggleModal({
      open: true,
      alumno: al,
      nextActivo: !currentActive
    });
  };

  const closeAlumnoToggleModal = () => {
    setAlumnoToggleModal({ open: false, alumno: null, nextActivo: true });
  };

  const handleToggleAlumno = async () => {
    if (!alumnoToggleModal.alumno) return;
    const alumnoId = alumnoToggleModal.alumno.id || alumnoToggleModal.alumno.Id;
    const nextActivo = alumnoToggleModal.nextActivo;

    if (!alumnoId) {
      setAlumnosError('No pudimos identificar al alumno seleccionado.');
      closeAlumnoToggleModal();
      return;
    }

    setAlumnoToggleLoading(true);
    setAlumnosError('');

    try {
      await usuarioService.updateAlumnoActivo(alumnoId, nextActivo);

      setAlumnos((prev) =>
        prev.map((a) => {
          const aid = a.id || a.Id;
          if (aid === alumnoId) {
            return { ...a, activo: nextActivo, Activo: nextActivo };
          }
          return a;
        })
      );
    } catch (error) {
      setAlumnosError(error.message || 'No pudimos actualizar el estado del alumno.');
    } finally {
      setAlumnoToggleLoading(false);
      closeAlumnoToggleModal();
    }
  };

  const handleEditProfesor = (prof) => {
    const profesorId = prof.id || prof.Id;
    if (!profesorId) {
      setProfesoresError('No pudimos identificar al profesor seleccionado.');
      return;
    }

    navigate(`/admin/profesores/${profesorId}/editar`, {
      replace: false,
      state: {
        profesor: prof,
        idColegio: activeColegioId
      }
    });
  };

  const openImportProfesoresModal = () => {
    setImportProfesoresModalOpen(true);
    setImportProfesoresFile(null);
    setImportProfesoresMessage('');
  };

  const closeImportProfesoresModal = () => {
    if (importProfesoresLoading) return;
    setImportProfesoresModalOpen(false);
    setImportProfesoresFile(null);
    setImportProfesoresMessage('');
  };

  const handleImportProfesores = async () => {
    if (!activeColegioId) {
      setImportProfesoresMessage('Selecciona un colegio antes de importar profesores.');
      return;
    }

    if (!importProfesoresFile) {
      setImportProfesoresMessage('Debes seleccionar un archivo .xlsx.');
      return;
    }

    setImportProfesoresLoading(true);
    setImportProfesoresMessage('');

    try {
      const response = await procesosAsincronicosService.importarProfesoresExcel({
        idColegio: activeColegioId,
        archivo: importProfesoresFile
      });

      const registros = response?.cantidadDeRegistros ?? response?.CantidadDeRegistros ?? 0;
      const erroresIniciales = response?.cantidadConErrorInicial ?? response?.CantidadConErrorInicial ?? 0;
      const mensaje = response?.mensaje || response?.Mensaje || 'La solicitud de importación fue creada correctamente.';

      setProcesosAsincronicosNotice(
        `${mensaje} Registros detectados: ${registros}. Errores iniciales: ${erroresIniciales}.`
      );
      setImportProfesoresModalOpen(false);
      setImportProfesoresFile(null);
      setImportProfesoresMessage('');
      setActiveMenuItem('procesos-asincronicos');

      await loadProfesores();
      await loadProcesosAsincronicos();

      const processId = response?.procesoId || response?.ProcesoId;
      if (processId) {
        setSelectedProcesoId(processId);
        await loadProcesoDetalle(processId);
      }
    } catch (error) {
      setImportProfesoresMessage(error.message || 'No pudimos crear la solicitud de importación.');
    } finally {
      setImportProfesoresLoading(false);
    }
  };

  const openImportAreasModal = () => {
    setImportAreasModalOpen(true);
    setImportAreasFile(null);
    setImportAreasMessage('');
  };

  const closeImportAreasModal = () => {
    if (importAreasLoading) return;
    setImportAreasModalOpen(false);
    setImportAreasFile(null);
    setImportAreasMessage('');
  };

  const handleImportAreas = async () => {
    if (!activeColegioId) {
      setImportAreasMessage('Selecciona un colegio antes de importar áreas.');
      return;
    }

    if (!importAreasFile) {
      setImportAreasMessage('Debes seleccionar un archivo .xlsx.');
      return;
    }

    setImportAreasLoading(true);
    setImportAreasMessage('');

    try {
      const response = await procesosAsincronicosService.importarAreasExcel({
        idColegio: activeColegioId,
        archivo: importAreasFile
      });

      const registros = response?.cantidadDeRegistros ?? response?.CantidadDeRegistros ?? 0;
      const erroresIniciales = response?.cantidadConErrorInicial ?? response?.CantidadConErrorInicial ?? 0;
      const mensaje = response?.mensaje || response?.Mensaje || 'La solicitud de importación fue creada correctamente.';

      setProcesosAsincronicosNotice(
        `${mensaje} Registros detectados: ${registros}. Errores iniciales: ${erroresIniciales}.`
      );
      setImportAreasModalOpen(false);
      setImportAreasFile(null);
      setImportAreasMessage('');
      setActiveMenuItem('procesos-asincronicos');

      await loadAreas();
      await loadProcesosAsincronicos();

      const processId = response?.procesoId || response?.ProcesoId;
      if (processId) {
        setSelectedProcesoId(processId);
        await loadProcesoDetalle(processId);
      }
    } catch (error) {
      setImportAreasMessage(error.message || 'No pudimos crear la solicitud de importación.');
    } finally {
      setImportAreasLoading(false);
    }
  };

  const openImportMateriasModal = () => {
    setImportMateriasModalOpen(true);
    setImportMateriasFile(null);
    setImportMateriasMessage('');
  };

  const closeImportMateriasModal = () => {
    if (importMateriasLoading) return;
    setImportMateriasModalOpen(false);
    setImportMateriasFile(null);
    setImportMateriasMessage('');
  };

  const handleImportMaterias = async () => {
    if (!activeColegioId) {
      setImportMateriasMessage('Selecciona un colegio antes de importar materias.');
      return;
    }

    if (!importMateriasFile) {
      setImportMateriasMessage('Debes seleccionar un archivo .xlsx.');
      return;
    }

    setImportMateriasLoading(true);
    setImportMateriasMessage('');

    try {
      const response = await procesosAsincronicosService.importarMateriasExcel({
        idColegio: activeColegioId,
        archivo: importMateriasFile
      });

      const registros = response?.cantidadDeRegistros ?? response?.CantidadDeRegistros ?? 0;
      const erroresIniciales = response?.cantidadConErrorInicial ?? response?.CantidadConErrorInicial ?? 0;
      const mensaje = response?.mensaje || response?.Mensaje || 'La solicitud de importación fue creada correctamente.';

      setProcesosAsincronicosNotice(
        `${mensaje} Registros detectados: ${registros}. Errores iniciales: ${erroresIniciales}.`
      );
      setImportMateriasModalOpen(false);
      setImportMateriasFile(null);
      setImportMateriasMessage('');
      setActiveMenuItem('procesos-asincronicos');

      await loadMaterias();
      await loadProcesosAsincronicos();

      const processId = response?.procesoId || response?.ProcesoId;
      if (processId) {
        setSelectedProcesoId(processId);
        await loadProcesoDetalle(processId);
      }
    } catch (error) {
      setImportMateriasMessage(error.message || 'No pudimos crear la solicitud de importación.');
    } finally {
      setImportMateriasLoading(false);
    }
  };

  const openAssociateModal = () => {
    setAssociateModalOpen(true);
    setAssociateEmail('');
    setAssociateMessage('');
    setAssociateSuccess(false);
  };

  const closeAssociateModal = () => {
    setAssociateModalOpen(false);
    setAssociateLoading(false);
    setAssociateMessage('');
    setAssociateSuccess(false);
  };

  const handleValidateAndAssociate = async () => {
    if (!associateEmail.trim()) {
      setAssociateMessage('Debes ingresar un email.');
      return;
    }
    if (!activeColegioId) {
      setAssociateMessage('Selecciona un colegio antes de asociar profesores.');
      return;
    }

    setAssociateLoading(true);
    setAssociateMessage('');

    try {
      const result = await usuarioService.validateProfesorPorEmail(associateEmail.trim(), activeColegioId);
      const message = getResultMessage(result) || 'Validación completada.';
      const asociado = result.asociado ?? result.Asociado ?? false;
      const asociacionCreada = result.asociacionCreada ?? result.AsociacionCreada ?? false;

      if (!asociado && !result.profesorExiste && !result.ProfesorExiste) {
        closeAssociateModal();
        setMissingModal({ open: true, email: associateEmail.trim() });
        return;
      }

      if (asociado && asociacionCreada) {
        setAssociateMessage('Profesor asociado exitosamente');
        setAssociateSuccess(true);
        await loadProfesores();
      } else if (asociado && !asociacionCreada) {
        setAssociateMessage(message || 'El profesor ya está asociado al colegio.');
        setAssociateSuccess(true);
      } else {
        setAssociateMessage(message);
      }
    } catch (error) {
      setAssociateMessage(error.message || 'No pudimos validar al profesor.');
    } finally {
      setAssociateLoading(false);
    }
  };

  const handleTestAssistant = (assistant) => {
    setSelectedAssistant(assistant);
    setShowChatModal(true);
  };

  const handleViewAsistenteReport = async (asistente) => {
    if (!asistente) return;
    const id = asistente.idOpenAIAssistant || asistente.IdOpenAIAssistant || asistente.id || asistente.Id;
    if (!id) return;
    setReportesError('');
    setReportesLoadingId(id);
    try {
      const report = await adminAsistenteService.getReportesAsistente(id, {
        filtroRol: 'user',
        pagina: 1,
        tamañoPagina: 50,
        limiteMensajesPorThread: 100
      });
      navigate('/reports', { state: { assistant: asistente, preloadReport: report } });
    } catch (error) {
      setReportesError(error.message || 'No pudimos obtener los reportes del asistente.');
    } finally {
      setReportesLoadingId(null);
    }
  };

  const handleCloseChatModal = () => {
    setShowChatModal(false);
    setSelectedAssistant(null);
  };

  const handleOpenAsistenteToggle = (asistente) => {
    const activo = asistente.activo ?? asistente.Activo ?? true;
    setAsistenteToggleModal({ open: true, asistente, nextActivo: !activo, error: '' });
  };

  const handleConfirmToggleAsistente = async () => {
    setAsistenteToggleLoading(true);
    try {
      const asistente = asistenteToggleModal.asistente;
      if (!asistente) throw new Error('No se encontró el asistente.');
      const id = asistente.idOpenAIAssistant || asistente.IdOpenAIAssistant || asistente.id || asistente.Id;
      if (!id) throw new Error('Falta el identificador del asistente.');
      const res = await adminAsistenteService.updateAsistenteActivo(id, asistenteToggleModal.nextActivo);
      const nuevoEstado = res.activo ?? res.Activo ?? asistenteToggleModal.nextActivo;
      setAsistentes((prev) =>
        (prev || []).map((a) =>
          (a.idOpenAIAssistant || a.IdOpenAIAssistant || a.id || a.Id) === id
            ? { ...a, activo: nuevoEstado, Activo: nuevoEstado }
            : a
        )
      );
      setAsistenteToggleModal({ open: false, asistente: null, nextActivo: true });
    } catch (error) {
      setAsistenteToggleModal((prev) => ({ ...prev, error: error.message || 'No pudimos cambiar el estado.' }));
    } finally {
      setAsistenteToggleLoading(false);
    }
  };

  const renderDashboardSection = () => {
    const data = dashboardData || {};
    const topAsistentesRaw = data.topAsistentes || data.TopAsistentes || [];
    const threadsRecientesRaw = data.threadsRecientes || data.ThreadsRecientes || [];
    const topAsistentes = Array.isArray(topAsistentesRaw) ? topAsistentesRaw : [];
    const threadsRecientes = Array.isArray(threadsRecientesRaw) ? threadsRecientesRaw : [];

    const totalUsuarios = data.totalUsuarios ?? data.TotalUsuarios ?? 0;
    const totalProfesores = data.totalProfesores ?? data.TotalProfesores ?? 0;
    const totalAlumnos = data.totalAlumnos ?? data.TotalAlumnos ?? 0;
    const totalAdminsArea = data.totalAdminsArea ?? data.TotalAdminsArea ?? 0;
    const totalCursos = data.totalCursos ?? data.TotalCursos ?? 0;
    const totalCursosActivos = data.totalCursosActivos ?? data.TotalCursosActivos ?? 0;
    const totalDivisiones = data.totalDivisiones ?? data.TotalDivisiones ?? 0;
    const totalDivisionesActivas = data.totalDivisionesActivas ?? data.TotalDivisionesActivas ?? 0;
    const totalAreas = data.totalAreas ?? data.TotalAreas ?? 0;
    const totalAreasActivas = data.totalAreasActivas ?? data.TotalAreasActivas ?? 0;
    const totalMaterias = data.totalMaterias ?? data.TotalMaterias ?? 0;
    const totalMateriasActivas = data.totalMateriasActivas ?? data.TotalMateriasActivas ?? 0;
    const totalAulas = data.totalAulas ?? data.TotalAulas ?? 0;
    const totalAulasActivas = data.totalAulasActivas ?? data.TotalAulasActivas ?? 0;
    const totalArchivosAula = data.totalArchivosAula ?? data.TotalArchivosAula ?? 0;
    const totalArchivosAulaHoy = data.totalArchivosAulaHoy ?? data.TotalArchivosAulaHoy ?? 0;
    const totalMensajesForo = data.totalMensajesForo ?? data.TotalMensajesForo ?? 0;
    const totalMensajesForoHoy = data.totalMensajesForoHoy ?? data.TotalMensajesForoHoy ?? 0;
    const totalRespuestasForo = data.totalRespuestasForo ?? data.TotalRespuestasForo ?? 0;
    const totalAsistentesAsignados = data.totalAsistentesAsignados ?? data.TotalAsistentesAsignados ?? 0;
    const totalAsistentesActivos = data.totalAsistentesActivos ?? data.TotalAsistentesActivos ?? 0;
    const totalAsistentesConMateria = data.totalAsistentesConMateria ?? data.TotalAsistentesConMateria ?? 0;
    const totalAsistentesSinMateria = data.totalAsistentesSinMateria ?? data.TotalAsistentesSinMateria ?? 0;
    const totalAsistentesOpenAI = data.totalAsistentesOpenAI ?? data.TotalAsistentesOpenAI ?? 0;
    const totalAsistentesGPTClasicos = data.totalAsistentesGPTClasicos ?? data.TotalAsistentesGPTClasicos ?? 0;
    const totalConversacionesOpenAI = data.totalConversacionesOpenAI ?? data.TotalConversacionesOpenAI ?? 0;
    const totalConversacionesGPTClasico = data.totalConversacionesGPTClasico ?? data.TotalConversacionesGPTClasico ?? 0;
    const totalThreads = data.totalThreads ?? data.TotalThreads ?? 0;
    const threadsHoy = data.threadsHoy ?? data.ThreadsHoy ?? 0;
    const threadsSemana = data.threadsSemana ?? data.ThreadsSemana ?? 0;
    const threadsMes = data.threadsMes ?? data.ThreadsMes ?? 0;
    const totalDispositivosPushActivos = data.totalDispositivosPushActivos ?? data.TotalDispositivosPushActivos ?? 0;
    const totalUsuariosConPush = data.totalUsuariosConPush ?? data.TotalUsuariosConPush ?? 0;
    const totalDispositivosIos = data.totalDispositivosIos ?? data.TotalDispositivosIos ?? 0;
    const totalDispositivosAndroid = data.totalDispositivosAndroid ?? data.TotalDispositivosAndroid ?? 0;
    const totalDispositivosPc = data.totalDispositivosPc ?? data.TotalDispositivosPc ?? 0;
    const totalDispositivosOtros = data.totalDispositivosOtros ?? data.TotalDispositivosOtros ?? 0;
    const ultimaActivacionPushUtc = data.ultimaActivacionPushUtc ?? data.UltimaActivacionPushUtc;
    const totalMensajesInternosHoy = data.totalMensajesInternosHoy ?? data.TotalMensajesInternosHoy ?? 0;
    const totalReceptoresMensajesInternosHoy = data.totalReceptoresMensajesInternosHoy ?? data.TotalReceptoresMensajesInternosHoy ?? 0;
    const totalMensajesInternosMes = data.totalMensajesInternosMes ?? data.TotalMensajesInternosMes ?? 0;
    const totalReceptoresMensajesInternosMes = data.totalReceptoresMensajesInternosMes ?? data.TotalReceptoresMensajesInternosMes ?? 0;
    const totalMensajesInternosNoLeidos = data.totalMensajesInternosNoLeidos ?? data.TotalMensajesInternosNoLeidos ?? 0;
    const totalPushErroresMensajesInternosHoy = data.totalPushErroresMensajesInternosHoy ?? data.TotalPushErroresMensajesInternosHoy ?? 0;
    const totalPushErroresMensajesInternosMes = data.totalPushErroresMensajesInternosMes ?? data.TotalPushErroresMensajesInternosMes ?? 0;

    const ratio = (a, b) => {
      if (!b || b <= 0) return 0;
      const v = Math.round((a / b) * 100);
      return Number.isFinite(v) ? v : 0;
    };

    const colegioNombre = data.nombreColegio || data.NombreColegio || activeColegioName;
    const totalAsistentesDisponibles = totalAsistentesOpenAI + totalAsistentesGPTClasicos;
    const totalActividadHoy = threadsHoy + totalMensajesForoHoy + totalArchivosAulaHoy + totalMensajesInternosHoy;
    const alumnosPorProfesor = totalProfesores > 0 ? (totalAlumnos / totalProfesores).toFixed(1) : '0';
    const conversationPeak = Math.max(threadsHoy, threadsSemana, threadsMes, totalMensajesForoHoy, totalArchivosAulaHoy, 1);

    const quickCards = [
      {
        label: 'Profesores',
        value: totalProfesores,
        icon: 'fa-chalkboard-teacher',
        color: 'primary',
        subtitle: `${totalAdminsArea} admin${totalAdminsArea === 1 ? '' : 's'} de colegio`
      },
      hasModuloAlumnos
        ? {
            label: 'Alumnos',
            value: totalAlumnos,
            icon: 'fa-user-graduate',
            color: 'secondary',
            subtitle: `${alumnosPorProfesor} alumnos por profesor`
          }
        : null,
      {
        label: 'Cursos',
        value: totalCursos,
        icon: 'fa-school',
        color: 'success',
        subtitle: `${totalDivisiones} divisiones registradas`
      },
      {
        label: 'Materias',
        value: totalMaterias,
        icon: 'fa-book',
        color: 'accent',
        subtitle: `${totalAreas} áreas académicas`
      },
      hasModuloAlumnos
        ? {
            label: 'Aulas',
            value: totalAulas,
            icon: 'fa-door-open',
            color: 'primary',
            subtitle: `${totalArchivosAula} archivos publicados`
          }
        : null,
      {
        label: 'Usuarios totales',
        value: totalUsuarios,
        icon: 'fa-users',
        color: 'success',
        subtitle: `${totalAsistentesDisponibles} asistentes disponibles`
      }
    ].filter(Boolean);

    const pulseCards = [
      {
        title: 'Actividad de hoy',
        value: totalActividadHoy,
        icon: 'fas fa-wave-square',
        color: 'primary',
        subtitle: 'asistentes + foro + archivos'
      },
      {
        title: 'Conversaciones hoy',
        value: threadsHoy,
        icon: 'fas fa-comments',
        color: 'secondary',
        subtitle: `${threadsSemana} en los últimos 7 días`
      },
      {
        title: 'Foro hoy',
        value: totalMensajesForoHoy,
        icon: 'fas fa-comment-dots',
        color: 'success',
        subtitle: `${totalMensajesForo} mensajes acumulados`
      },
      {
        title: 'Archivos hoy',
        value: totalArchivosAulaHoy,
        icon: 'fas fa-file-circle-plus',
        color: 'accent',
        subtitle: `${totalArchivosAula} publicados en total`
      },
      {
        title: 'Asistentes asignados',
        value: totalAsistentesAsignados,
        icon: 'fas fa-link',
        color: 'primary',
        subtitle: `${totalAsistentesConMateria} vinculados a materias`
      },
      {
        title: 'Respuestas en foro',
        value: totalRespuestasForo,
        icon: 'fas fa-reply-all',
        color: 'secondary',
        subtitle: 'seguimiento de interacción'
      },
      {
        title: 'Mensajes internos hoy',
        value: totalMensajesInternosHoy,
        icon: 'fas fa-envelope',
        color: 'success',
        subtitle: `${totalReceptoresMensajesInternosHoy} receptores del día`
      }
    ];

    const messagingCards = [
      {
        title: 'Mensajes hoy',
        value: totalMensajesInternosHoy,
        icon: 'fas fa-paper-plane',
        color: 'primary',
        subtitle: `${totalReceptoresMensajesInternosHoy} receptores creados hoy`
      },
      {
        title: 'Mensajes mes',
        value: totalMensajesInternosMes,
        icon: 'fas fa-calendar-alt',
        color: 'secondary',
        subtitle: `${totalReceptoresMensajesInternosMes} receptores del mes en curso`
      },
      {
        title: 'Sin leer',
        value: totalMensajesInternosNoLeidos,
        icon: 'fas fa-envelope-open',
        color: 'accent',
        subtitle: 'receptores pendientes de lectura'
      },
      {
        title: 'Push con error',
        value: totalPushErroresMensajesInternosHoy,
        icon: 'fas fa-exclamation-triangle',
        color: totalPushErroresMensajesInternosHoy > 0 ? 'accent' : 'success',
        subtitle: `${totalPushErroresMensajesInternosMes} en el mes en curso`
      }
    ];

    const deviceCards = [
      {
        title: 'Dispositivos activos',
        value: totalDispositivosPushActivos,
        icon: 'fas fa-mobile-alt',
        tone: 'openai',
        subtitle: `${totalUsuariosConPush} usuarios con push enrolada`
      },
      {
        title: 'Android',
        value: totalDispositivosAndroid,
        icon: 'fas fa-mobile-alt',
        tone: 'foro',
        subtitle: `${ratio(totalDispositivosAndroid, totalDispositivosPushActivos)}% del total`
      },
      {
        title: 'iOS',
        value: totalDispositivosIos,
        icon: 'fas fa-tablet-alt',
        tone: 'neutral',
        subtitle: `${ratio(totalDispositivosIos, totalDispositivosPushActivos)}% del total`
      },
      {
        title: 'PC',
        value: totalDispositivosPc,
        icon: 'fas fa-desktop',
        tone: 'clasico',
        subtitle: `${ratio(totalDispositivosPc, totalDispositivosPushActivos)}% del total`
      },
      totalDispositivosOtros > 0
        ? {
            title: 'Otros',
            value: totalDispositivosOtros,
            icon: 'fas fa-question-circle',
            tone: 'archivos',
            subtitle: 'dispositivos sin clasificación estándar'
          }
        : null
    ].filter(Boolean);

    const systemCards = [
      hasOpenAIChats
        ? {
            title: 'OpenAI',
            value: totalConversacionesOpenAI,
            icon: 'fas fa-microchip',
            tone: 'openai',
            subtitle: `${totalAsistentesOpenAI} asistente(s) habilitados`
          }
        : null,
      hasAgentesClasicos
        ? {
            title: 'Asistente IA',
            value: totalConversacionesGPTClasico,
            icon: 'fas fa-wand-magic-sparkles',
            tone: 'clasico',
            subtitle: `${totalAsistentesGPTClasicos} asistente(s) habilitados`
          }
        : null,
      {
        title: 'Foro institucional',
        value: totalMensajesForo,
        icon: 'fas fa-comments',
        tone: 'foro',
        subtitle: `${totalRespuestasForo} respuestas registradas`
      },
      {
        title: 'Biblioteca del aula',
        value: totalArchivosAula,
        icon: 'fas fa-folder-open',
        tone: 'archivos',
        subtitle: `${totalArchivosAulaHoy} archivos publicados hoy`
      }
    ].filter(Boolean);

    const healthCards = [
      { label: 'Cursos activos', value: `${totalCursosActivos} / ${totalCursos}`, percent: ratio(totalCursosActivos, totalCursos), colorClass: 'primary' },
      { label: 'Divisiones activas', value: `${totalDivisionesActivas} / ${totalDivisiones}`, percent: ratio(totalDivisionesActivas, totalDivisiones), colorClass: 'secondary' },
      { label: 'Áreas activas', value: `${totalAreasActivas} / ${totalAreas}`, percent: ratio(totalAreasActivas, totalAreas), colorClass: 'success' },
      { label: 'Materias activas', value: `${totalMateriasActivas} / ${totalMaterias}`, percent: ratio(totalMateriasActivas, totalMaterias), colorClass: 'accent' },
      hasModuloAlumnos
        ? { label: 'Aulas activas', value: `${totalAulasActivas} / ${totalAulas}`, percent: ratio(totalAulasActivas, totalAulas), colorClass: 'primary' }
        : null,
      { label: 'Asistentes activos', value: `${totalAsistentesActivos} / ${totalAsistentesDisponibles}`, percent: ratio(totalAsistentesActivos, totalAsistentesDisponibles), colorClass: 'success' }
    ].filter(Boolean);

    const topAsistentesVisible = topAsistentes
      .map((item, idx) => {
        const name = item.nombre || item.Nombre || item.displayName || item.DisplayName || item.asistente || item.Asistente || `Asistente ${idx + 1}`;
        const esGPTClasico = item.esGPTClasico ?? item.EsGPTClasico ?? false;
        const ultimoUsoRaw = item.ultimoThread || item.UltimoThread || item.ultimoUso || item.UltimoUso;
        const total = item.totalThreads || item.TotalThreads || item.threads || item.Threads || 0;
        return {
          id: item.idOpenAIAssistant || item.IdOpenAIAssistant || `assistant-${idx}`,
          name,
          esGPTClasico,
          total,
          ultimoUso: ultimoUsoRaw
        };
      })
      .filter((item) => (item.esGPTClasico ? hasAgentesClasicos : hasOpenAIChats));

    const topAssistantMax = Math.max(...topAsistentesVisible.map((item) => item.total), 1);

    const recentItems = threadsRecientes
      .map((thread, idx) => {
        const asistente = thread.nombreAsistente || thread.NombreAsistente || thread.asistente || thread.Asistente || 'Asistente';
        const esGPTClasico = thread.esGPTClasico ?? thread.EsGPTClasico ?? false;
        const idThread = thread.idOpenAIThread || thread.IdOpenAIThread || thread.threadId || thread.ThreadId || `conversacion-${idx + 1}`;
        const fechaRaw = thread.fecha || thread.Fecha || thread.fechaCreacion || thread.FechaCreacion || thread.createdAt || thread.CreatedAt;
        const shortId = typeof idThread === 'string' && idThread.length > 18 ? `${idThread.slice(0, 18)}…` : idThread;
        return {
          id: idThread,
          tipo: esGPTClasico ? 'clasico' : 'openai',
          titulo: asistente,
          descripcion: `Conversación ${esGPTClasico ? 'Asistente IA' : 'OpenAI'} registrada en ${colegioNombre}.`,
          meta: `ID ${shortId}`,
          fecha: fechaRaw
        };
      })
      .filter((item) => (item.tipo === 'clasico' ? hasAgentesClasicos : hasOpenAIChats))
      .slice(0, 8);

    const actionCards = [
      {
        key: 'profesores',
        title: 'Gestionar profesores',
        description: 'Altas, permisos, reseteos y asociaciones.',
        icon: 'fas fa-chalkboard-teacher',
        onClick: () => setActiveMenuItem('profesores')
      },
      {
        key: 'materias',
        title: 'Ordenar materias',
        description: 'Importación masiva y revisión curricular.',
        icon: 'fas fa-book-open',
        onClick: () => setActiveMenuItem('materias')
      },
      hasModuloAlumnos
        ? {
            key: 'cursos',
            title: 'Revisar cursos',
            description: 'Distribución por año, división y estructura.',
            icon: 'fas fa-school',
            onClick: () => setActiveMenuItem('cursos')
          }
        : null,
      hasModuloAlumnos
        ? {
            key: 'aulas',
            title: 'Monitorear aulas',
            description: 'Estado, actividad y estadísticas por aula.',
            icon: 'fas fa-chalkboard',
            onClick: () => setActiveMenuItem('aulas')
          }
        : null,
      {
        key: 'metricas',
        title: 'Métricas generales',
        description: 'Abrir el panel analítico completo del colegio.',
        icon: 'fas fa-chart-line',
        onClick: () => navigate('/admin/metricas-generales')
      },
      isGeneralAdmin
        ? {
            key: 'colegios',
            title: 'Cambiar de colegio',
            description: 'Volver al selector de colegios administrados.',
            icon: 'fas fa-building',
            onClick: handleBackToSelectColegio
          }
        : null
    ].filter(Boolean);

    return (
      <div className="dashboard-home">
        <section className="dashboard-hero admin-dashboard-hero">
          <div className="hero-copy">
            <span className="hero-eyebrow">{isGeneralAdmin ? 'Administrador general' : 'Administrador de colegio'}</span>
            <h2>Centro de control de {colegioNombre}</h2>
            <p>
              Vista ejecutiva del colegio con foco en comunidad, operación académica, aulas y actividad de asistentes.
              {hasOpenAIChats || hasAgentesClasicos ? ' Incluye OpenAI y asistentes IA según las feature flags activas.' : ''}
            </p>
            <div className="hero-chips">
              <span className="hero-chip">
                <i className="fas fa-users" />
                {totalUsuarios} usuarios registrados
              </span>
              <span className="hero-chip">
                <i className="fas fa-school" />
                {totalCursosActivos}/{totalCursos} cursos activos
              </span>
              <span className="hero-chip">
                <i className="fas fa-graduation-cap" />
                {totalDivisiones} divisiones operativas
              </span>
              <span className="hero-chip">
                <i className="fas fa-robot" />
                {totalAsistentesActivos}/{totalAsistentesDisponibles} asistentes activos
              </span>
              <span className="hero-chip">
                <i className="fas fa-folder-open" />
                {totalArchivosAula} archivos en aulas
              </span>
              <span className="hero-chip">
                <i className="fas fa-mobile-alt" />
                {totalDispositivosPushActivos} dispositivos PWA
              </span>
            </div>
          </div>
          <div className="hero-highlight admin-hero-highlight">
            <span className="hero-highlight-label">Pulso operativo</span>
            <strong>{totalActividadHoy}</strong>
            <p>acciones de hoy entre conversaciones, foro, mensajes internos y materiales publicados</p>
            <button className="dashboard-refresh-btn admin-hero-refresh" type="button" onClick={loadDashboard} disabled={dashboardLoading}>
              <i className={`fas ${dashboardLoading ? 'fa-spinner fa-spin' : 'fa-rotate-right'}`} />
              Actualizar
            </button>
          </div>
        </section>

        <div className="stats-grid admin-overview-stats">
          {quickCards.map((card) => (
            <div key={card.label} className={`stats-card ${card.color}`}>
              <div className="card-icon">
                <i className={`fas ${card.icon}`}></i>
              </div>
              <div className="card-content">
                <h3 className="card-value">{card.value}</h3>
                <p className="card-title">{card.label}</p>
                <p className="card-subtitle admin-card-subtitle">{card.subtitle}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="dashboard-split-grid">
          <section className="dashboard-section">
            <div className="section-header">
              <h2 className="section-title">
                <i className="fas fa-gauge-high" />
                Pulso operativo
              </h2>
            </div>
            <div className="mini-stats-grid">
              {pulseCards.map((card) => (
                <div key={card.title} className={`mini-stat-card ${card.color}`}>
                  <div className="mini-stat-top">
                    <span className="mini-stat-icon">
                      <i className={card.icon}></i>
                    </span>
                    <span className="mini-stat-title">{card.title}</span>
                  </div>
                  <strong className="mini-stat-value">{card.value}</strong>
                  <p className="mini-stat-subtitle">{card.subtitle}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="dashboard-section">
            <div className="section-header">
              <h2 className="section-title">
                <i className="fas fa-chart-pie" />
                Canales del sistema
              </h2>
            </div>
            <div className="assistant-cards-grid">
              {systemCards.map((card) => (
                <div key={card.title} className={`assistant-summary-card ${card.tone}`}>
                  <div className="assistant-summary-icon">
                    <i className={card.icon}></i>
                  </div>
                  <div>
                    <span className="assistant-summary-title">{card.title}</span>
                    <strong className="assistant-summary-value">{card.value}</strong>
                    <p className="assistant-summary-subtitle">{card.subtitle}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="admin-conversation-tracks">
              {[
                { label: 'Hoy', value: threadsHoy, tone: 'openai' },
                { label: 'Semana', value: threadsSemana, tone: 'clasico' },
                { label: 'Mes', value: threadsMes, tone: 'foro' },
                { label: 'Foro hoy', value: totalMensajesForoHoy, tone: 'archivos' }
              ].map((item) => (
                <div key={item.label} className="admin-track-row">
                  <div className="admin-track-meta">
                    <span>{item.label}</span>
                    <strong>{item.value}</strong>
                  </div>
                  <div className="ranking-bar">
                    <span
                      className={`ranking-fill ${item.tone}`}
                      style={{ width: `${Math.min(100, Math.round((item.value / conversationPeak) * 100))}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        <div className="dashboard-split-grid">
          <section className="dashboard-section">
            <div className="section-header">
              <h2 className="section-title">
                <i className="fas fa-envelope-open-text" />
                Mensajería interna
              </h2>
            </div>
            <div className="mini-stats-grid">
              {messagingCards.map((card) => (
                <div key={card.title} className={`mini-stat-card ${card.color}`}>
                  <div className="mini-stat-top">
                    <span className="mini-stat-icon">
                      <i className={card.icon}></i>
                    </span>
                    <span className="mini-stat-title">{card.title}</span>
                  </div>
                  <strong className="mini-stat-value">{card.value}</strong>
                  <p className="mini-stat-subtitle">{card.subtitle}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="dashboard-section">
            <div className="section-header">
              <h2 className="section-title">
                <i className="fas fa-mobile-alt" />
                Dispositivos PWA
              </h2>
            </div>
            <div className="assistant-cards-grid admin-device-cards-grid">
              {deviceCards.map((card) => (
                <div key={card.title} className={`assistant-summary-card ${card.tone}`}>
                  <div className="assistant-summary-icon">
                    <i className={card.icon}></i>
                  </div>
                  <div>
                    <span className="assistant-summary-title">{card.title}</span>
                    <strong className="assistant-summary-value">{card.value}</strong>
                    <p className="assistant-summary-subtitle">{card.subtitle}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="admin-device-footnote">
              <i className="fas fa-clock" />
              <span>Última activación push: {formatDashboardDateTime(ultimaActivacionPushUtc)}</span>
            </div>
          </section>
        </div>

        <div className="dashboard-split-grid">
          <section className="dashboard-section">
            <div className="section-header">
              <h2 className="section-title">
                <i className="fas fa-heart-pulse" />
                Salud del colegio
              </h2>
            </div>
            <div className="admin-health-grid">
              {healthCards.map((item) => (
                <div key={item.label} className="admin-health-card">
                  <div className="admin-health-top">
                    <span>{item.label}</span>
                    <strong>{item.value}</strong>
                  </div>
                  <div className="ranking-bar">
                    <span className={`ranking-fill ${item.colorClass}`} style={{ width: `${Math.min(100, item.percent)}%` }} />
                  </div>
                  <small>{item.percent}% de disponibilidad</small>
                </div>
              ))}
            </div>
          </section>

          <section className="dashboard-section">
            <div className="section-header">
              <h2 className="section-title">
                <i className="fas fa-bolt" />
                Acciones rápidas
              </h2>
            </div>
            <div className="admin-action-grid">
              {actionCards.map((action) => (
                <button key={action.key} type="button" className="admin-action-card" onClick={action.onClick}>
                  <span className="admin-action-icon">
                    <i className={action.icon}></i>
                  </span>
                  <span className="admin-action-copy">
                    <strong>{action.title}</strong>
                    <small>{action.description}</small>
                  </span>
                  <i className="fas fa-arrow-right admin-action-arrow"></i>
                </button>
              ))}
            </div>
          </section>
        </div>

        <div className="dashboard-split-grid">
          <section className="dashboard-section">
            <div className="section-header">
              <h2 className="section-title">
                <i className="fas fa-robot" />
                Top asistentes
              </h2>
            </div>
            <div className="ranking-list">
              {topAsistentesVisible.length > 0 ? topAsistentesVisible.map((item, idx) => (
                <div key={item.id} className="ranking-item">
                  <div className="ranking-main">
                    <span className="ranking-badge">{idx + 1}</span>
                    <div>
                      <h4>{item.name}</h4>
                      <p>{item.esGPTClasico ? 'Asistente IA' : 'OpenAI'} · {item.total} conversaciones</p>
                    </div>
                  </div>
                  <div className="ranking-bar">
                    <span
                      className={`ranking-fill ${item.esGPTClasico ? 'clasico' : 'openai'}`}
                      style={{ width: `${Math.min(100, Math.round((item.total / topAssistantMax) * 100))}%` }}
                    />
                  </div>
                  <div className="ranking-side">
                    <strong>{item.total}</strong>
                    <small>{formatDashboardDateTime(item.ultimoUso)}</small>
                  </div>
                </div>
              )) : (
                <div className="dashboard-empty-state">
                  <i className="fas fa-robot"></i>
                  <p>No hay asistentes con actividad reciente para mostrar.</p>
                </div>
              )}
            </div>
          </section>

          <section className="dashboard-section">
            <div className="section-header">
              <h2 className="section-title">
                <i className="fas fa-stream" />
                Actividad reciente del sistema
              </h2>
            </div>
            <div className="recent-activity-list">
              {recentItems.length > 0 ? recentItems.map((item) => (
                <div key={item.id} className="recent-activity-item">
                  <div className={`recent-activity-icon ${item.tipo}`}>
                    <i className={`fas ${item.tipo === 'clasico' ? 'fa-wand-magic-sparkles' : 'fa-microchip'}`}></i>
                  </div>
                  <div className="recent-activity-content">
                    <div className="recent-activity-header">
                      <strong>{item.titulo}</strong>
                      <span>{formatDashboardDateTime(item.fecha)}</span>
                    </div>
                    <p>{item.descripcion}</p>
                    <small>{item.meta}</small>
                  </div>
                </div>
              )) : (
                <div className="dashboard-empty-state">
                  <i className="fas fa-stream"></i>
                  <p>No hay conversaciones recientes registradas todavía.</p>
                </div>
              )}
            </div>
          </section>
        </div>

        <div className="content-card admin-summary-card">
          <div className="card-header">
            <div className="card-title">
              <i className="fas fa-layer-group" />
              <span>Lecturas rápidas del entorno</span>
            </div>
          </div>
          <div className="card-content admin-summary-grid">
            {[
              {
                label: 'Cobertura de asistentes por materia',
                value: `${ratio(totalAsistentesConMateria, totalMaterias || totalAsistentesConMateria)}%`,
                helper: `${totalAsistentesConMateria} asistentes vinculados a materias`
              },
              {
                label: 'Asistentes sin materia',
                value: totalAsistentesSinMateria,
                helper: 'pendientes de clasificación curricular'
              },
              {
                label: 'Mensajes de foro',
                value: totalMensajesForo,
                helper: `${totalMensajesForoHoy} generados hoy`
              },
              {
                label: 'Materiales de aula',
                value: totalArchivosAula,
                helper: `${totalArchivosAulaHoy} publicados hoy`
              },
              {
                label: 'Comunidad docente',
                value: totalProfesores,
                helper: `${totalAdminsArea} administradores asociados`
              },
              {
                label: 'Consolidado de conversaciones',
                value: totalThreads,
                helper: `${threadsMes} en el último mes`
              }
            ].map((item) => (
              <div key={item.label} className="admin-summary-tile">
                <span>{item.label}</span>
                <strong>{item.value}</strong>
                <small>{item.helper}</small>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const formatDateTime = (value) => {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '—';
    return parsed.toLocaleString();
  };

  const formatProcesoTipo = (value) => {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'importacionalumnos') return 'Importación de alumnos';
    if (normalized === 'importacionprofesores') return 'Importación de profesores';
    if (normalized === 'importacionareas') return 'Importación de áreas';
    if (normalized === 'importacionmaterias') return 'Importación de materias';
    if (normalized === 'importacioncuros' || normalized === 'importacioncursos') return 'Importación de cursos';
    return value || 'Proceso';
  };

  const renderProcesosAsincronicosSection = () => {
    const selectedProceso = selectedProcesoDetalle?.proceso || selectedProcesoDetalle?.Proceso || null;
    const detalleRows = selectedProcesoDetalle?.detalles || selectedProcesoDetalle?.Detalles || [];
    const selectedProcesoTipo = selectedProceso?.tipoProceso || selectedProceso?.TipoProceso || '';
    const normalizedProcesoTipo = String(selectedProcesoTipo).trim().toLowerCase();
    const isAlumnoProcess = normalizedProcesoTipo === 'importacionalumnos';
    const isAreaProcess = normalizedProcesoTipo === 'importacionareas';
    const isMateriaProcess = normalizedProcesoTipo === 'importacionmaterias';
    const isCursoProcess = normalizedProcesoTipo === 'importacioncuros' || normalizedProcesoTipo === 'importacioncursos';

    return (
      <div className="profesores-section">
        <div className="section-header">
          <div>
            <p className="eyebrow">Solicitudes de procesos</p>
            <h2>Procesos asíncronos de {activeColegioName}</h2>
            <p className="subtitle">Consulta el avance de las importaciones solicitadas por tu usuario.</p>
          </div>
          <div className="profesores-actions-header">
            <div className="profesor-count-card">
              <div className="count-icon">
                <i className="fas fa-tasks"></i>
              </div>
              <div>
                <p className="count-label">Solicitudes</p>
                <p className="count-number">{procesosAsincronicos.length}</p>
              </div>
            </div>
            <button
              className="primary-btn"
              type="button"
              onClick={async () => {
                await loadProcesosAsincronicos();
                await refreshSelectedProcesoDetalle();
              }}
              disabled={procesosAsincronicosLoading}
            >
              <i className={`fas ${procesosAsincronicosLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} />
              <span>Actualizar</span>
            </button>
          </div>
        </div>

        {procesosAsincronicosNotice && (
          <div className="content-card" style={{ marginBottom: '1rem', borderColor: '#bbf7d0', background: '#f0fdf4' }}>
            <div className="card-content" style={{ color: '#166534', fontWeight: 600 }}>
              <i className="fas fa-check-circle" style={{ marginRight: '0.5rem' }} />
              {procesosAsincronicosNotice}
            </div>
          </div>
        )}

        <div className="content-card">
          <div className="card-header">
            <div className="card-title">
              <i className="fas fa-list"></i>
              <span>Listado de solicitudes</span>
            </div>
          </div>
          <div className="card-content">
            {procesosAsincronicosLoading && (
              <div className="data-table-loading">
                <div className="loading-spinner">
                  <i className="fas fa-spinner fa-spin"></i>
                </div>
                <p>Cargando solicitudes de procesos...</p>
              </div>
            )}

            {!procesosAsincronicosLoading && procesosAsincronicosError && (
              <div className="data-table-empty">
                <div className="empty-icon">
                  <i className="fas fa-exclamation-triangle"></i>
                </div>
                <p>{procesosAsincronicosError}</p>
              </div>
            )}

            {!procesosAsincronicosLoading && !procesosAsincronicosError && procesosAsincronicos.length === 0 && (
              <div className="data-table-empty">
                <div className="empty-icon">
                  <i className="fas fa-inbox"></i>
                </div>
                <p>No hay solicitudes de procesos para este colegio.</p>
              </div>
            )}

            {!procesosAsincronicosLoading && !procesosAsincronicosError && procesosAsincronicos.length > 0 && (
              <DataTable
                data={procesosAsincronicos.map((item) => {
                  const id = item.id || item.Id;
                  const total = item.cantidadDeRegistros ?? item.CantidadDeRegistros ?? 0;
                  const procesados = item.cantidadProcesados ?? item.CantidadProcesados ?? 0;
                  const errores = item.cantidadErrores ?? item.CantidadErrores ?? 0;
                  const porcentaje = item.porcentajeAvance ?? item.PorcentajeAvance ?? 0;
                  const finalizado = item.finalizado ?? item.Finalizado ?? false;
                  const iniciado = item.iniciado ?? item.Iniciado ?? false;
                  let estado = 'Pendiente';
                  if (finalizado) {
                    estado = errores > 0 ? 'Finalizado con errores' : 'Finalizado';
                  } else if (iniciado) {
                    estado = 'En proceso';
                  }

                  return {
                    ...item,
                    idNormalizado: id,
                    tipoProcesoFmt: formatProcesoTipo(item.tipoProceso || item.TipoProceso || 'Proceso'),
                    archivoFmt: item.nombreArchivo || item.NombreArchivo || '—',
                    estadoFmt: estado,
                    progresoFmt: `${porcentaje}%`,
                    registrosFmt: `${procesados}/${total}`,
                    erroresFmt: errores,
                    fechaFmt: formatDateTime(item.fechaSolicitud || item.FechaSolicitud),
                    selected: selectedProcesoId === id
                  };
                })}
                columns={[
                  { key: 'tipoProcesoFmt', title: 'Tipo' },
                  { key: 'archivoFmt', title: 'Archivo' },
                  {
                    key: 'estadoFmt',
                    title: 'Estado',
                    render: (value, item) => (
                      <span className={`status-chip ${
                        (item.finalizado ?? item.Finalizado)
                          ? ((item.cantidadErrores ?? item.CantidadErrores) > 0 ? 'warning' : 'success')
                          : ((item.iniciado ?? item.Iniciado) ? 'info' : 'pending')
                      }`}>
                        {value}
                      </span>
                    )
                  },
                  { key: 'progresoFmt', title: 'Avance', align: 'center' },
                  { key: 'registrosFmt', title: 'Procesados', align: 'center' },
                  { key: 'erroresFmt', title: 'Errores', align: 'center' },
                  { key: 'fechaFmt', title: 'Solicitud', align: 'center' },
                  {
                    key: 'acciones',
                    title: 'Acciones',
                    align: 'center',
                    sortable: false,
                    render: (_, item) => (
                      <button
                        type="button"
                        className="action-edit-btn"
                        onClick={() => {
                          const id = item.idNormalizado;
                          setSelectedProcesoId(id);
                          loadProcesoDetalle(id);
                        }}
                        title="Ver detalle"
                      >
                        <i className="fas fa-eye"></i>
                      </button>
                    )
                  }
                ]}
                emptyMessage="No hay solicitudes para mostrar."
              />
            )}
          </div>
        </div>

        {selectedProcesoId && (
          <div className="content-card" style={{ marginTop: '1rem' }}>
            <div className="card-header">
              <div className="card-title">
                <i className="fas fa-file-alt"></i>
                <span>Detalle del proceso</span>
              </div>
              <div className="professor-actions" style={{ gap: '0.5rem' }}>
                <button
                  className="refresh-btn"
                  type="button"
                  onClick={() => loadProcesoDetalle(selectedProcesoId)}
                  disabled={selectedProcesoDetalleLoading}
                >
                  <i className={`fas ${selectedProcesoDetalleLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} />
                </button>
              </div>
            </div>
            <div className="card-content">
              {selectedProcesoDetalleLoading && (
                <div className="data-table-loading">
                  <div className="loading-spinner">
                    <i className="fas fa-spinner fa-spin"></i>
                  </div>
                  <p>Cargando detalle del proceso...</p>
                </div>
              )}

              {!selectedProcesoDetalleLoading && selectedProcesoDetalleError && (
                <div className="data-table-empty">
                  <div className="empty-icon">
                    <i className="fas fa-exclamation-triangle"></i>
                  </div>
                  <p>{selectedProcesoDetalleError}</p>
                </div>
              )}

              {!selectedProcesoDetalleLoading && !selectedProcesoDetalleError && selectedProceso && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
                      {[
                      { label: 'Tipo', value: formatProcesoTipo(selectedProceso.tipoProceso || selectedProceso.TipoProceso || '—') },
                      { label: 'Archivo', value: selectedProceso.nombreArchivo || selectedProceso.NombreArchivo || '—' },
                      { label: 'Avance', value: `${selectedProceso.porcentajeAvance ?? selectedProceso.PorcentajeAvance ?? 0}%` },
                      { label: 'Registros', value: `${selectedProceso.cantidadProcesados ?? selectedProceso.CantidadProcesados ?? 0}/${selectedProceso.cantidadDeRegistros ?? selectedProceso.CantidadDeRegistros ?? 0}` },
                      { label: 'Errores', value: selectedProceso.cantidadErrores ?? selectedProceso.CantidadErrores ?? 0 },
                      { label: 'Finalización', value: formatDateTime(selectedProceso.fechaFinalizacion || selectedProceso.FechaFinalizacion) }
                    ].map((item) => (
                      <div key={item.label} style={{ border: '1px solid var(--gray-200)', borderRadius: '12px', padding: '0.75rem', background: '#f8fafc' }}>
                        <p style={{ margin: 0, color: '#64748b', fontSize: '0.85rem' }}>{item.label}</p>
                        <p style={{ margin: '0.2rem 0 0', color: '#0f172a', fontWeight: 700 }}>{item.value}</p>
                      </div>
                    ))}
                  </div>

                  <DataTable
                    data={Array.isArray(detalleRows) ? detalleRows : []}
                    columns={
                      isAlumnoProcess
                        ? [
                            { key: 'numeroFila', title: 'Fila', align: 'center' },
                            { key: 'nombre', title: 'Nombre', render: (value, item) => value || item.Nombre || '—' },
                            { key: 'apellido', title: 'Apellido', render: (value, item) => value || item.Apellido || '—' },
                            { key: 'email', title: 'Email', render: (value, item) => value || item.Email || '—' },
                            {
                              key: 'estado',
                              title: 'Estado',
                              align: 'center',
                              render: (value, item) => (
                                <span className={`status-chip ${
                                  (item.exitoso ?? item.Exitoso) ? 'success' : ((item.procesado ?? item.Procesado) ? 'danger' : 'pending')
                                }`}>
                                  {value || item.Estado || 'Pendiente'}
                                </span>
                              )
                            },
                            {
                              key: 'mensajeResultado',
                              title: 'Resultado',
                              render: (value, item) => item.error || item.Error || value || item.MensajeResultado || '—'
                            }
                          ]
                        : isAreaProcess
                        ? [
                            { key: 'numeroFila', title: 'Fila', align: 'center' },
                            { key: 'nombre', title: 'Nombre', render: (value, item) => value || item.Nombre || '—' },
                            { key: 'apellido', title: 'Descripción', render: (value, item) => value || item.Apellido || '—' },
                            {
                              key: 'estado',
                              title: 'Estado',
                              align: 'center',
                              render: (value, item) => (
                                <span className={`status-chip ${
                                  (item.exitoso ?? item.Exitoso) ? 'success' : ((item.procesado ?? item.Procesado) ? 'danger' : 'pending')
                                }`}>
                                  {value || item.Estado || 'Pendiente'}
                                </span>
                              )
                            },
                            {
                              key: 'mensajeResultado',
                              title: 'Resultado',
                              render: (value, item) => item.error || item.Error || value || item.MensajeResultado || '—'
                            }
                          ]
                        : isCursoProcess
                          ? [
                              { key: 'numeroFila', title: 'Fila', align: 'center' },
                              { key: 'nombre', title: 'Ciclo lectivo', render: (value, item) => value || item.Nombre || '—' },
                              { key: 'apellido', title: 'Año', render: (value, item) => value || item.Apellido || '—' },
                              { key: 'dni', title: 'División', render: (value, item) => value || item.Dni || '—' },
                              { key: 'email', title: 'Profesores', render: (value, item) => value || item.Email || 'Sin profesores' },
                              {
                                key: 'estado',
                                title: 'Estado',
                                align: 'center',
                                render: (value, item) => (
                                  <span className={`status-chip ${
                                    (item.exitoso ?? item.Exitoso) ? 'success' : ((item.procesado ?? item.Procesado) ? 'danger' : 'pending')
                                  }`}>
                                    {value || item.Estado || 'Pendiente'}
                                  </span>
                                )
                              },
                              {
                                key: 'mensajeResultado',
                                title: 'Resultado',
                                render: (value, item) => item.error || item.Error || value || item.MensajeResultado || '—'
                              }
                            ]
                        : isMateriaProcess
                          ? [
                              { key: 'numeroFila', title: 'Fila', align: 'center' },
                              { key: 'nombre', title: 'Nombre', render: (value, item) => value || item.Nombre || '—' },
                              { key: 'dni', title: 'Código', render: (value, item) => value || item.Dni || '—' },
                              { key: 'apellido', title: 'Descripción', render: (value, item) => value || item.Apellido || '—' },
                              { key: 'email', title: 'Área / Año / Profesores', render: (value, item) => value || item.Email || '—' },
                              {
                                key: 'estado',
                                title: 'Estado',
                                align: 'center',
                                render: (value, item) => (
                                  <span className={`status-chip ${
                                    (item.exitoso ?? item.Exitoso) ? 'success' : ((item.procesado ?? item.Procesado) ? 'danger' : 'pending')
                                  }`}>
                                    {value || item.Estado || 'Pendiente'}
                                  </span>
                                )
                              },
                              {
                                key: 'mensajeResultado',
                                title: 'Resultado',
                                render: (value, item) => item.error || item.Error || value || item.MensajeResultado || '—'
                              }
                            ]
                        : [
                            { key: 'numeroFila', title: 'Fila', align: 'center' },
                            {
                              key: 'nombre',
                              title: 'Nombre',
                              render: (_, item) => `${item.nombre || item.Nombre || ''} ${item.apellido || item.Apellido || ''}`.trim() || '—'
                            },
                            { key: 'email', title: 'Email', render: (value, item) => value || item.Email || '—' },
                            { key: 'dni', title: 'DNI', render: (value, item) => value || item.Dni || '—' },
                            {
                              key: 'estado',
                              title: 'Estado',
                              align: 'center',
                              render: (value, item) => (
                                <span className={`status-chip ${
                                  (item.exitoso ?? item.Exitoso) ? 'success' : ((item.procesado ?? item.Procesado) ? 'danger' : 'pending')
                                }`}>
                                  {value || item.Estado || 'Pendiente'}
                                </span>
                              )
                            },
                            {
                              key: 'mensajeResultado',
                              title: 'Resultado',
                              render: (value, item) => item.error || item.Error || value || item.MensajeResultado || '—'
                            }
                          ]
                    }
                    emptyMessage="No hay filas registradas para este proceso."
                  />
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderProfesoresSection = () => (
    <div className="profesores-section">
      <div className="section-header">
        <div>
          <p className="eyebrow">Profesores</p>
          <h2>Profesores en {activeColegioName}</h2>
          <p className="subtitle">Listado filtrable de docentes asociados a este colegio.</p>
        </div>
        <div className="profesores-actions-header">
          <div className="profesor-count-card">
            <div className="count-icon">
              <i className="fas fa-users"></i>
            </div>
            <div>
              <p className="count-label">Total docentes</p>
              <p className="count-number">{profesoresTotal}</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <a className="secondary-btn" href={profesoresTemplateUrl} download>
              <i className="fas fa-download" />
              <span>Descargar template</span>
            </a>
            <button className="secondary-btn" type="button" onClick={openImportProfesoresModal}>
              <i className="fas fa-file-excel" />
              <span>Importar Excel</span>
            </button>
            <button className="primary-btn" type="button" onClick={openAssociateModal}>
              <i className="fas fa-user-plus" />
              <span>Asociar nuevo profesor</span>
            </button>
          </div>
        </div>
      </div>

      <div className="content-card">
        <div className="card-header">
          <div className="card-title">
            <i className="fas fa-chalkboard-teacher"></i>
            <span>Listado de profesores</span>
          </div>
          <div className="professor-actions">
            <div className="professor-search">
              <i className="fas fa-search" aria-hidden="true" />
              <input
                type="text"
                placeholder="Buscar por nombre o email"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setProfesoresPage(1); }}
              />
            </div>
            <button className="refresh-btn" type="button" onClick={loadProfesores} disabled={profesoresLoading}>
              <i className={`fas ${profesoresLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} />
            </button>
            <TableExportActions
              tableSelector="#admin-dashboard-profesores-table"
              fileName="profesores"
              title="Listado de profesores"
              compact
              disabled={filteredProfesores.length === 0}
            />
          </div>
        </div>
        <div className="card-content professors-table">
          {profesoresLoading && (
            <div className="data-table-loading">
              <div className="loading-spinner">
                <i className="fas fa-spinner fa-spin"></i>
              </div>
              <p>Cargando profesores de {activeColegioName}...</p>
            </div>
          )}

          {!profesoresLoading && profesoresError && (
            <div className="data-table-empty">
              <div className="empty-icon">
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <p>{profesoresError}</p>
              <button className="refresh-btn" type="button" onClick={loadProfesores}>
                <i className="fas fa-sync-alt" /> Reintentar
              </button>
            </div>
          )}

          {!profesoresLoading && !profesoresError && filteredProfesores.length === 0 && (
            <div className="data-table-empty">
              <div className="empty-icon">
                <i className="fas fa-users-slash"></i>
              </div>
              <p>No se encontraron profesores que coincidan con tu búsqueda.</p>
            </div>
          )}

          {!profesoresLoading && !profesoresError && filteredProfesores.length > 0 && (
            <div className="data-table-wrapper">
              <table id="admin-dashboard-profesores-table" className="data-table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Email</th>
                    <th>Materias / cursos ({new Date().getFullYear()})</th>
                    <th className="text-center width-sm">Estado</th>
                    <th className="text-center width-sm">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {paginate(filteredProfesores, profesoresPage).map((prof) => {
                    const nombre = getProfesorNombre(prof);
                    const email = prof.email || prof.Email || '-';
                    const dni = prof.dni || prof.Dni || '-';
                    const activo = isProfesorActivo(prof);
                    const materias = Array.isArray(prof.materias || prof.Materias) ? (prof.materias || prof.Materias) : [];
                    const materiasLabel = materias
                      .map((materia) => {
                        const nombreMateria = materia.nombre || materia.Nombre;
                        const codigoMateria = materia.codigo || materia.Codigo;
                        if (!nombreMateria) return '';
                        return codigoMateria ? `${nombreMateria} (${codigoMateria})` : nombreMateria;
                      })
                      .filter(Boolean)
                      .join(', ');

                    return (
                      <tr key={prof.id || prof.Id || email}>
                        <td>
                          <div className="prof-name">
                            <span className="name">{nombre}</span>
                          </div>
                        </td>
                        <td>{email}</td>
                        <td>
                          <span className="muted">{materiasLabel || '—'}</span>
                        </td>
                        <td className="text-center">
                          <span className={`status-chip ${activo ? 'success' : 'danger'}`}>
                            {activo ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td className="text-center">
                          <div className="action-buttons">
                            <button
                              type="button"
                              className="action-edit-btn"
                              onClick={() => handleEditProfesor(prof)}
                              title="Editar profesor"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button
                              type="button"
                              className="action-reset-btn"
                              onClick={() => openResetProfesorModal(prof)}
                              title="Resetear contraseña"
                            >
                              <i className="fas fa-key"></i>
                            </button>
                            <button
                              type="button"
                              className="action-remove-btn"
                              onClick={() => openDisassociateModal(prof)}
                              title="Desasociar profesor del colegio"
                            >
                              <i className="fas fa-link-slash"></i>
                            </button>
                            <button
                              type="button"
                              className={`action-toggle-btn ${activo ? 'danger' : 'success'}`}
                              onClick={() => openToggleModal(prof)}
                              disabled={toggleLoading}
                              title={activo ? 'Desactivar profesor' : 'Activar profesor'}
                            >
                              <i className={`fas ${activo ? 'fa-user-slash' : 'fa-user-check'}`}></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {!profesoresLoading && !profesoresError && profesoresTotal > 0 && (
            renderPagination(
              profesoresTotal,
              profesoresPage,
              setProfesoresPage,
              profesoresPageSize,
              setProfesoresPageSize
            )
          )}
        </div>
      </div>

      {importProfesoresModalOpen && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className="fas fa-file-excel" />
            </div>
            <h3>Importar profesores</h3>
            <p>
              Sube un archivo <strong>.xlsx</strong> con las columnas <strong>Nombre</strong>, <strong>Apellido</strong>,
              <strong> DNI</strong> y <strong>Email</strong>. La solicitud se enviará a la cola para procesarse en segundo plano.
            </p>
            <div className="modal-message" style={{ marginBottom: '1rem' }}>
              <a href={profesoresTemplateUrl} download style={{ fontWeight: 700 }}>
                <i className="fas fa-download" style={{ marginRight: '0.5rem' }} />
                Descargar template de importación
              </a>
            </div>
            <div className="form-field">
              <label>Archivo Excel</label>
              <input
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  setImportProfesoresFile(file);
                  setImportProfesoresMessage('');
                }}
                disabled={importProfesoresLoading}
              />
            </div>
            {importProfesoresFile && (
              <div className="modal-message">
                Archivo seleccionado: <strong>{importProfesoresFile.name}</strong>
              </div>
            )}
            {importProfesoresMessage && (
              <div className="modal-message">
                {importProfesoresMessage}
              </div>
            )}
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={closeImportProfesoresModal}
                disabled={importProfesoresLoading}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-primary success"
                onClick={handleImportProfesores}
                disabled={importProfesoresLoading}
              >
                {importProfesoresLoading ? (
                  <i className="fas fa-spinner fa-spin" />
                ) : (
                  <i className="fas fa-upload" />
                )}
                <span>{importProfesoresLoading ? 'Enviando...' : 'Crear solicitud'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {associateModalOpen && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className="fas fa-user-plus" />
            </div>
            <h3>Asociar profesor</h3>
            <p>
              Primero validaremos si el docente está registrado y/o asociado al colegio {activeColegioName}.
            </p>
            <div className="form-field">
              <label>Email del profesor</label>
              <input
                type="email"
                placeholder="profesor@ejemplo.com"
                value={associateEmail}
                onChange={(e) => setAssociateEmail(e.target.value)}
              />
            </div>
            {associateMessage && (
              <div className="modal-message">
                {associateMessage}
              </div>
            )}
            <div className="modal-actions">
              {associateSuccess ? (
                <button
                  type="button"
                  className="btn-primary"
                  onClick={closeAssociateModal}
                >
                  <i className="fas fa-check" />
                  <span>Ok</span>
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={closeAssociateModal}
                    disabled={associateLoading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn-primary success"
                    onClick={handleValidateAndAssociate}
                    disabled={associateLoading}
                  >
                    {associateLoading ? (
                      <i className="fas fa-spinner fa-spin" />
                    ) : (
                      <i className="fas fa-check" />
                    )}
                    <span>Validar y asociar</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {confirmModal.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className={`fas ${confirmModal.nextActivo ? 'fa-user-check' : 'fa-user-slash'}`} />
            </div>
            <h3>
              {confirmModal.nextActivo ? 'Activar profesor' : 'Desactivar profesor'}
            </h3>
            <p>
              {confirmModal.nextActivo
                ? `A continuación se activará el profesor ${getProfesorNombre(confirmModal.profesor)}, permitiendo que pueda iniciar sesión en el sistema. ¿Está de acuerdo?`
                : `A continuación se desactivará el profesor ${getProfesorNombre(confirmModal.profesor)}, por lo tanto no podrá iniciar sesión en el sistema. ¿Está de acuerdo?`}
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={closeToggleModal}
                disabled={toggleLoading}
              >
                Cancelar
              </button>
              <button
                type="button"
                className={`btn-primary ${confirmModal.nextActivo ? 'success' : 'danger'}`}
                onClick={handleToggleProfesor}
                disabled={toggleLoading}
              >
                {toggleLoading ? (
                  <i className="fas fa-spinner fa-spin" />
                ) : (
                  <i className={`fas ${confirmModal.nextActivo ? 'fa-user-check' : 'fa-user-slash'}`} />
                )}
                <span>{confirmModal.nextActivo ? 'Sí, activar' : 'Sí, desactivar'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {missingModal.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className="fas fa-user-plus" />
            </div>
            <h3>Profesor no encontrado</h3>
            <p>
              El profesor {missingModal.email} no existe en el sistema. ¿Desea continuar con la carga de dicho profesor?
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setMissingModal({ open: false, email: '' })}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-primary success"
                onClick={() => {
                  const emailToUse = missingModal.email;
                  setMissingModal({ open: false, email: '' });
                  navigate('/admin/profesores/nuevo', {
                    replace: false,
                    state: {
                      email: emailToUse,
                      idColegio: activeColegioId
                    }
                  });
                }}
              >
                <i className="fas fa-arrow-right" />
                <span>Continuar con alta</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {disassociateModal.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className="fas fa-unlink" />
            </div>
            <h3>Desasociar profesor</h3>
            <p>
              El profesor {getProfesorNombre(disassociateModal.profesor)} dejará de estar asociado al colegio {activeColegioName}. No podrá ver ni gestionar contenidos de este colegio hasta que se lo asocie nuevamente.
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={closeDisassociateModal}
                disabled={disassociateLoading}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-primary danger"
                onClick={handleDisassociate}
                disabled={disassociateLoading}
              >
                {disassociateLoading ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-unlink" />}
                <span>Desasociar</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {resetProfesorModal.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className="fas fa-key" />
            </div>
            <h3>Resetear contraseña</h3>
            <p>
              Se va a resetear el password al profesor {getProfesorNombre(resetProfesorModal.profesor)} y será su DNI.
            </p>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={closeResetProfesorModal} disabled={resetProfesorLoading}>
                Cancelar
              </button>
              <button type="button" className="btn-primary" onClick={handleResetProfesorPassword} disabled={resetProfesorLoading}>
                {resetProfesorLoading ? 'Reseteando...' : 'Resetear'}
              </button>
            </div>
          </div>
        </div>
      )}

      {resetProfesorResult.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className={`fas ${resetProfesorResult.ok ? 'fa-check-circle' : 'fa-exclamation-triangle'}`} />
            </div>
            <h3>{resetProfesorResult.ok ? 'Contraseña reseteada' : 'No se pudo resetear'}</h3>
            <p>{resetProfesorResult.message}</p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-primary"
                onClick={() => setResetProfesorResult({ open: false, ok: true, message: '' })}
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderAsistentesSection = () => (
    <div className="profesores-section">
      <div className="section-header">
        <div>
          <p className="eyebrow">Asistentes OpenAI</p>
          <h2>Asistentes OpenAI en {activeColegioName}</h2>
          <p className="subtitle">Gestiona los asistentes OpenAI asociados a este colegio.</p>
        </div>
        <div className="profesores-actions-header">
          <div className="profesor-count-card">
            <div className="count-icon">
              <i className="fas fa-robot"></i>
            </div>
            <div>
              <p className="count-label">Total asistentes OpenAI</p>
              <p className="count-number">{asistentesTotal}</p>
            </div>
          </div>
          <button className="primary-btn" type="button" onClick={() => navigate('/admin/asistentes/nuevo')}>
            <i className="fas fa-plus" />
            <span>Nuevo asistente OpenAI</span>
          </button>
        </div>
      </div>

      <div className="content-card">
        <div className="card-header">
          <div className="card-title">
            <i className="fas fa-robot"></i>
            <span>Listado de asistentes</span>
          </div>
          <div className="professor-actions">
            <div className="professor-search">
              <i className="fas fa-search" aria-hidden="true" />
              <input
                type="text"
                placeholder="Buscar por nombre, área o materia"
                value={asistentesSearch}
                onChange={(e) => { setAsistentesSearch(e.target.value); setAsistentesPage(1); }}
              />
            </div>
            <button className="refresh-btn" type="button" onClick={loadAsistentes} disabled={asistentesLoading}>
              <i className={`fas ${asistentesLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} />
            </button>
            <TableExportActions
              tableSelector="#admin-dashboard-asistentes-table"
              fileName="asistentes-openai"
              title="Listado de asistentes"
              compact
              disabled={filteredAsistentes.length === 0}
            />
          </div>
        </div>
        {reportesError && (
          <div className="data-table-error" style={{ marginTop: '0.75rem' }}>
            <i className="fas fa-exclamation-triangle"></i>
            <p>{reportesError}</p>
            <button className="primary-btn" type="button" onClick={() => setReportesError('')}>
              <i className="fas fa-times"></i>
              <span>Cerrar</span>
            </button>
          </div>
        )}
        <div className="card-content professors-table">
          {asistentesLoading && (
            <div className="data-table-loading">
              <div className="loading-spinner">
                <i className="fas fa-spinner fa-spin"></i>
              </div>
              <p>Cargando asistentes de {activeColegioName}...</p>
            </div>
          )}

          {!asistentesLoading && asistentesError && (
            <div className="data-table-error">
              <i className="fas fa-exclamation-triangle"></i>
              <p>{asistentesError}</p>
              <button className="primary-btn" type="button" onClick={loadAsistentes}>
                <i className="fas fa-sync-alt"></i>
                <span>Reintentar</span>
              </button>
            </div>
          )}

          {!asistentesLoading && !asistentesError && filteredAsistentes.length === 0 && (
            <div className="data-table-empty">
              <i className="fas fa-box-open"></i>
              <p>No hay asistentes registrados en este colegio.</p>
              <button className="primary-btn" type="button" onClick={() => navigate('/admin/asistentes/nuevo')}>
                <i className="fas fa-plus"></i>
                <span>Crear asistente OpenAI</span>
              </button>
            </div>
          )}

          {!asistentesLoading && !asistentesError && filteredAsistentes.length > 0 && (
            <div className="data-table-wrapper">
              <table id="admin-dashboard-asistentes-table" className="data-table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Área/Materia</th>
                    <th>Fecha alta</th>
                    <th>Password</th>
                    <th className="text-center width-sm">Estado</th>
                    <th className="text-center width-sm">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {paginate(filteredAsistentes, asistentesPage).map((asistente, idx) => {
                const nombre = asistente.nombre || asistente.Nombre || 'N/D';
                const area = asistente.nombreArea || asistente.NombreArea || '—';
                const materia = asistente.nombreMateria || asistente.NombreMateria || '—';
                const areaMateria = `${area}/${materia}`;
                const fecha = asistente.fechaAlta || asistente.FechaAlta;
                const password = asistente.passwordAsistente || asistente.PasswordAsistente || '—';
                const activo = asistente.activo ?? asistente.Activo ?? true;
                let fechaFmt = fecha || '';
                if (fecha) {
                  const d = new Date(fecha);
                  if (!isNaN(d.getTime())) {
                    fechaFmt = d.toLocaleDateString();
                      }
                    }
                    const key = asistente.idOpenAIAssistant || asistente.IdOpenAIAssistant || `asistente-${idx}`;
                    return (
                      <tr key={key}>
                        <td>
                          <div className="prof-name">
                            <span className="name">{nombre}</span>
                            <span className="muted">{asistente.idOpenAIAssistant || '—'}</span>
                          </div>
                        </td>
                        <td>{areaMateria}</td>
                        <td>{fechaFmt || '—'}</td>
                        <td>{password}</td>
                        <td className="text-center">
                          <span className={`status-chip ${activo ? 'success' : 'danger'}`}>
                            {activo ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td className="text-center">
                          <div className="table-actions">
                            <button
                              className="table-btn table-btn-edit"
                              title="Editar"
                              onClick={() => navigate(`/admin/asistentes/${asistente.idOpenAIAssistant || asistente.IdOpenAIAssistant}/editar`)}
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button
                              className="table-btn table-btn-test"
                              title="Probar asistente"
                              onClick={() => handleTestAssistant(asistente)}
                            >
                              <i className="fas fa-play"></i>
                            </button>
                            <button
                              className="table-btn table-btn-reports"
                              title="Ver reportes"
                              onClick={() => handleViewAsistenteReport(asistente)}
                              disabled={reportesLoadingId === key}
                            >
                              <i className={`fas ${reportesLoadingId === key ? 'fa-spinner fa-spin' : 'fa-chart-bar'}`}></i>
                            </button>
                            <button
                              className={`table-btn ${activo ? 'table-btn-toggle-inactive' : 'table-btn-toggle-active'}`}
                              title={activo ? 'Desactivar asistente' : 'Activar asistente'}
                              onClick={() => handleOpenAsistenteToggle(asistente)}
                            >
                              <i className={`fas ${activo ? 'fa-toggle-on' : 'fa-toggle-off'}`}></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {!asistentesLoading && !asistentesError && asistentesTotal > 0 && (
            renderPagination(asistentesTotal, asistentesPage, setAsistentesPage, asistentesPageSize, setAsistentesPageSize)
          )}
        </div>
      </div>
    </div>
  );

  const renderAlumnosSection = () => (
    <div className="profesores-section">
      <div className="section-header">
        <div>
          <p className="eyebrow">Alumnos</p>
          <h2>Alumnos en {activeColegioName}</h2>
          <p className="subtitle">Listado filtrable de alumnos asociados a este colegio.</p>
        </div>
        <div className="profesores-actions-header">
          <div className="profesor-count-card">
            <div className="count-icon">
              <i className="fas fa-user-graduate"></i>
            </div>
            <div>
              <p className="count-label">Total alumnos</p>
              <p className="count-number">{alumnosTotal}</p>
            </div>
          </div>
          <button className="primary-btn" type="button" onClick={openAlumnoAssociateModal}>
            <i className="fas fa-user-plus" />
            <span>Asociar nuevo alumno</span>
          </button>
        </div>
      </div>

      <div className="content-card">
        <div className="card-header">
          <div className="card-title">
            <i className="fas fa-user-graduate"></i>
            <span>Listado de alumnos</span>
          </div>
          <div className="professor-actions">
            <div className="professor-search">
              <i className="fas fa-search" aria-hidden="true" />
              <input
                type="text"
                placeholder="Buscar por nombre, email o legajo"
                value={alumnosSearch}
                onChange={(e) => { setAlumnosSearch(e.target.value); setAlumnosPage(1); }}
              />
            </div>
            <button className="refresh-btn" type="button" onClick={loadAlumnos} disabled={alumnosLoading}>
              <i className={`fas ${alumnosLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} />
            </button>
            <TableExportActions
              tableSelector="#admin-dashboard-alumnos-table"
              fileName="alumnos"
              title="Listado de alumnos"
              compact
              disabled={filteredAlumnos.length === 0}
            />
          </div>
        </div>
        <div className="card-content professors-table">
          {alumnosLoading && (
            <div className="data-table-loading">
              <div className="loading-spinner">
                <i className="fas fa-spinner fa-spin"></i>
              </div>
              <p>Cargando alumnos de {activeColegioName}...</p>
            </div>
          )}

          {!alumnosLoading && alumnosError && (
            <div className="data-table-empty">
              <div className="empty-icon">
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <p>{alumnosError}</p>
              <button className="refresh-btn" type="button" onClick={loadAlumnos}>
                <i className="fas fa-sync-alt" /> Reintentar
              </button>
            </div>
          )}

          {!alumnosLoading && !alumnosError && filteredAlumnos.length === 0 && (
            <div className="data-table-empty">
              <div className="empty-icon">
                <i className="fas fa-users-slash"></i>
              </div>
              <p>No se encontraron alumnos que coincidan con tu búsqueda.</p>
            </div>
          )}

          {!alumnosLoading && !alumnosError && filteredAlumnos.length > 0 && (
            <div className="data-table-wrapper">
              <table id="admin-dashboard-alumnos-table" className="data-table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Email</th>
                    <th>Legajo</th>
                    <th>Teléfono</th>
                    <th className="text-center width-sm">Estado</th>
                    <th className="text-center width-sm">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {paginate(filteredAlumnos, alumnosPage).map((al) => {
                    const nombre = getAlumnoNombre(al);
                    const email = al.email || al.Email || '-';
                    const legajo = al.legajo || al.Legajo || '-';
                    const telefono = al.telefono || al.Telefono || '-';
                    const activo = isAlumnoActivo(al);

                    return (
                      <tr key={al.id || al.Id || email}>
                        <td>
                          <div className="prof-name">
                            <span className="name">{nombre}</span>
                            {(legajo && legajo !== '-') && <span className="muted">Legajo {legajo}</span>}
                          </div>
                        </td>
                        <td>{email}</td>
                        <td>{legajo}</td>
                        <td>{telefono}</td>
                        <td className="text-center">
                          <span className={`status-chip ${activo ? 'success' : 'danger'}`}>
                            {activo ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td className="text-center">
                          <div className="action-buttons">
                            <button
                              type="button"
                              className="action-family-btn"
                              onClick={() => openAlumnoFamiliaresModal(al)}
                              title="Gestionar familiares"
                            >
                              <i className="fas fa-users" />
                              <span>Familiares</span>
                            </button>
                            <button
                              type="button"
                              className="action-edit-btn"
                              onClick={() => handleEditAlumno(al)}
                              title="Editar alumno"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button
                              type="button"
                              className="action-reset-btn"
                              onClick={() => openResetAlumnoModal(al)}
                              title="Resetear contraseña"
                            >
                              <i className="fas fa-key"></i>
                            </button>
                            <button
                              type="button"
                              className="action-remove-btn"
                              onClick={() => openAlumnoDisassociateModal(al)}
                              title="Desasociar alumno del colegio"
                            >
                              <i className="fas fa-link-slash"></i>
                            </button>
                            <button
                              type="button"
                              className={`action-toggle-btn ${activo ? 'danger' : 'success'}`}
                              onClick={() => openAlumnoToggleModal(al)}
                              disabled={alumnoToggleLoading}
                              title={activo ? 'Desactivar alumno' : 'Activar alumno'}
                            >
                              <i className={`fas ${activo ? 'fa-user-slash' : 'fa-user-check'}`}></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {!alumnosLoading && !alumnosError && alumnosTotal > 0 && (
            renderPagination(
              alumnosTotal,
              alumnosPage,
              setAlumnosPage,
              alumnosPageSize,
              setAlumnosPageSize
            )
          )}
        </div>
      </div>

      {alumnoAssociateModalOpen && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className="fas fa-user-plus" />
            </div>
            <h3>Asociar alumno</h3>
            <p>
              Primero validaremos si el alumno está registrado y/o asociado al colegio {activeColegioName}.
            </p>
            <div className="form-field">
              <label>Email del alumno</label>
              <input
                type="email"
                placeholder="alumno@ejemplo.com"
                value={alumnoAssociateEmail}
                onChange={(e) => setAlumnoAssociateEmail(e.target.value)}
              />
            </div>
            {alumnoAssociateMessage && (
              <div className="modal-message">
                {alumnoAssociateMessage}
              </div>
            )}
            <div className="modal-actions">
              {alumnoAssociateSuccess ? (
                <button
                  type="button"
                  className="btn-primary"
                  onClick={closeAlumnoAssociateModal}
                >
                  <i className="fas fa-check" />
                  <span>Ok</span>
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={closeAlumnoAssociateModal}
                    disabled={alumnoAssociateLoading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn-primary success"
                    onClick={handleValidateAndAssociateAlumno}
                    disabled={alumnoAssociateLoading}
                  >
                    {alumnoAssociateLoading ? (
                      <i className="fas fa-spinner fa-spin" />
                    ) : (
                      <i className="fas fa-check" />
                    )}
                    <span>Validar y asociar</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {alumnoMissingModal.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className="fas fa-user-plus" />
            </div>
            <h3>Alumno no encontrado</h3>
            <p>
              El alumno {alumnoMissingModal.email} no existe en el sistema. ¿Desea continuar con la carga de dicho alumno?
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setAlumnoMissingModal({ open: false, email: '' })}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-primary success"
                onClick={() => {
                  const emailToUse = alumnoMissingModal.email;
                  setAlumnoMissingModal({ open: false, email: '' });
                  navigate('/admin/alumnos/nuevo', {
                    replace: false,
                    state: {
                      email: emailToUse,
                      idColegio: activeColegioId
                    }
                  });
                }}
              >
                <i className="fas fa-arrow-right" />
                <span>Continuar con alta</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {alumnoDisassociateModal.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className="fas fa-unlink" />
            </div>
            <h3>Desasociar alumno</h3>
            <p>
              El alumno {getAlumnoNombre(alumnoDisassociateModal.alumno)} dejará de estar asociado al colegio {activeColegioName}. No podrá ver ni gestionar contenidos de este colegio hasta que se lo asocie nuevamente.
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={closeAlumnoDisassociateModal}
                disabled={alumnoDisassociateLoading}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-primary danger"
                onClick={handleAlumnoDisassociate}
                disabled={alumnoDisassociateLoading}
              >
                {alumnoDisassociateLoading ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-unlink" />}
                <span>Desasociar</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {resetAlumnoModal.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className="fas fa-key" />
            </div>
            <h3>Resetear contraseña</h3>
            <p>
              Se va a resetear el password del alumno {getAlumnoNombre(resetAlumnoModal.alumno)}. El nuevo password será <strong>123456</strong> y el alumno luego deberá cambiarlo.
            </p>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={closeResetAlumnoModal} disabled={resetAlumnoLoading}>
                Cancelar
              </button>
              <button type="button" className="btn-primary" onClick={handleResetAlumnoPassword} disabled={resetAlumnoLoading}>
                {resetAlumnoLoading ? 'Reseteando...' : 'Resetear'}
              </button>
            </div>
          </div>
        </div>
      )}

      {resetAlumnoResult.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className={`fas ${resetAlumnoResult.ok ? 'fa-check-circle' : 'fa-exclamation-triangle'}`} />
            </div>
            <h3>{resetAlumnoResult.ok ? 'Contraseña reseteada' : 'No se pudo resetear'}</h3>
            <p>{resetAlumnoResult.message}</p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-primary"
                onClick={() => setResetAlumnoResult({ open: false, ok: true, message: '' })}
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}

      {alumnoToggleModal.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className={`fas ${alumnoToggleModal.nextActivo ? 'fa-user-check' : 'fa-user-slash'}`} />
            </div>
            <h3>
              {alumnoToggleModal.nextActivo ? 'Activar alumno' : 'Desactivar alumno'}
            </h3>
            <p>
              {alumnoToggleModal.nextActivo
                ? `A continuación se activará el alumno ${getAlumnoNombre(alumnoToggleModal.alumno)}, permitiendo que pueda iniciar sesión en el sistema. ¿Está de acuerdo?`
                : `A continuación se desactivará el alumno ${getAlumnoNombre(alumnoToggleModal.alumno)}, por lo tanto no podrá iniciar sesión en el sistema. ¿Está de acuerdo?`}
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={closeAlumnoToggleModal}
                disabled={alumnoToggleLoading}
              >
                Cancelar
              </button>
              <button
                type="button"
                className={`btn-primary ${alumnoToggleModal.nextActivo ? 'success' : 'danger'}`}
                onClick={handleToggleAlumno}
                disabled={alumnoToggleLoading}
              >
                {alumnoToggleLoading ? (
                  <i className="fas fa-spinner fa-spin" />
                ) : (
                  <i className={`fas ${alumnoToggleModal.nextActivo ? 'fa-user-check' : 'fa-user-slash'}`} />
                )}
                <span>{alumnoToggleModal.nextActivo ? 'Sí, activar' : 'Sí, desactivar'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderAdminAreaSection = () => (
    <div className="profesores-section">
      <div className="section-header">
        <div>
          <p className="eyebrow">Admins de área</p>
          <h2>Administradores por colegio</h2>
          <p className="subtitle">Gestiona los usuarios administradores de área asociados a este colegio.</p>
        </div>
        <div className="profesores-actions-header">
          <div className="profesor-count-card">
            <div className="count-icon">
              <i className="fas fa-user-tie"></i>
            </div>
            <div>
              <p className="count-label">Total admins</p>
              <p className="count-number">{adminAreaTotal}</p>
            </div>
          </div>
          <button className="primary-btn" type="button" onClick={handleCreateAdminArea}>
            <i className="fas fa-user-plus" />
            <span>Agregar admin de área</span>
          </button>
        </div>
      </div>

      <div className="content-card">
            <div className="card-header">
              <div className="card-title">
                <i className="fas fa-user-tie"></i>
                <span>Listado de admins</span>
              </div>
              <div className="professor-actions">
                <div className="professor-search">
                  <i className="fas fa-search" aria-hidden="true" />
                  <input
                type="text"
                placeholder="Buscar por nombre o email"
                value={adminAreaSearch}
                onChange={(e) => { setAdminAreaSearch(e.target.value); setAdminAreaPage(1); }}
              />
            </div>
            <button className="refresh-btn" type="button" onClick={loadAdminAreaUsers} disabled={adminAreaLoading}>
              <i className={`fas ${adminAreaLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} />
            </button>
            <TableExportActions
              tableSelector="#admin-dashboard-admin-area-table"
              fileName="admins-area"
              title="Listado de admins de área"
              compact
              disabled={filteredAdminAreaUsers.length === 0}
            />
              </div>
            </div>
        <div className="card-content professors-table">
          {adminAreaLoading && (
            <div className="data-table-loading">
              <div className="loading-spinner">
                <i className="fas fa-spinner fa-spin"></i>
              </div>
              <p>Cargando admins de área...</p>
            </div>
          )}

          {!adminAreaLoading && adminAreaError && (
            <div className="data-table-empty">
              <div className="empty-icon">
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <p>{adminAreaError}</p>
              <button className="refresh-btn" type="button" onClick={loadAdminAreaUsers}>
                <i className="fas fa-sync-alt" /> Reintentar
              </button>
            </div>
          )}

          {!adminAreaLoading && !adminAreaError && filteredAdminAreaUsers.length === 0 && (
            <div className="data-table-empty">
              <div className="empty-icon">
                <i className="fas fa-users-slash"></i>
              </div>
              <p>No hay admins de área asociados a este colegio.</p>
            </div>
          )}

          {!adminAreaLoading && !adminAreaError && filteredAdminAreaUsers.length > 0 && (
            <div className="data-table-wrapper">
              <table id="admin-dashboard-admin-area-table" className="data-table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Email</th>
                    <th className="text-center width-sm">Estado</th>
                    <th className="text-center width-sm">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {paginate(filteredAdminAreaUsers, adminAreaPage).map((u) => {
                    const nombre = u.displayName || u.DisplayName || `${u.nombre || u.Nombre || ''} ${u.apellido || u.Apellido || ''}`.trim() || u.email || u.Email || 'Sin nombre';
                    const email = u.email || u.Email || '-';
                    const activo = isAdminAreaActivo(u);

                    return (
                      <tr key={u.id || u.Id || email}>
                        <td>
                          <div className="prof-name">
                            <span className="name">{nombre}</span>
                          </div>
                        </td>
                        <td>{email}</td>
                        <td className="text-center">
                          <span className={`status-chip ${activo ? 'success' : 'danger'}`}>
                            {activo ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td className="text-center">
                          <div className="action-buttons">
                            <button
                              type="button"
                              className="action-edit-btn"
                              onClick={() => handleEditAdminArea(u)}
                              title="Editar admin de área"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button
                              type="button"
                              className={`action-toggle-btn ${activo ? 'danger' : 'success'}`}
                              onClick={() => openAdminAreaToggleModal(u)}
                              disabled={adminAreaToggleLoading}
                              title={activo ? 'Desactivar admin' : 'Activar admin'}
                            >
                              <i className={`fas ${activo ? 'fa-user-slash' : 'fa-user-check'}`}></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {!adminAreaLoading && !adminAreaError && adminAreaTotal > 0 && (
            renderPagination(
              adminAreaTotal,
              adminAreaPage,
              setAdminAreaPage,
              adminAreaPageSize,
              setAdminAreaPageSize
            )
          )}
        </div>
      </div>

      {adminAreaToggleModal.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className={`fas ${adminAreaToggleModal.nextActivo ? 'fa-user-check' : 'fa-user-slash'}`} />
            </div>
            <h3>
              {adminAreaToggleModal.nextActivo ? 'Activar admin de área' : 'Desactivar admin de área'}
            </h3>
            <p>
              {adminAreaToggleModal.nextActivo
                ? `Se activará el admin ${adminAreaToggleModal.user?.displayName || adminAreaToggleModal.user?.email || ''}.`
                : `Se desactivará el admin ${adminAreaToggleModal.user?.displayName || adminAreaToggleModal.user?.email || ''}.`}
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={closeAdminAreaToggleModal}
                disabled={adminAreaToggleLoading}
              >
                Cancelar
              </button>
              <button
                type="button"
                className={`btn-primary ${adminAreaToggleModal.nextActivo ? 'success' : 'danger'}`}
                onClick={handleToggleAdminArea}
                disabled={adminAreaToggleLoading}
              >
                {adminAreaToggleLoading ? (
                  <i className="fas fa-spinner fa-spin" />
                ) : (
                  <i className={`fas ${adminAreaToggleModal.nextActivo ? 'fa-user-check' : 'fa-user-slash'}`} />
                )}
                <span>{adminAreaToggleModal.nextActivo ? 'Sí, activar' : 'Sí, desactivar'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderMateriasSection = () => (
    <div className="profesores-section">
      <div className="section-header">
        <div>
          <p className="eyebrow">Materias</p>
          <h2>Materias del colegio</h2>
          <p className="subtitle">Gestiona las materias asociadas al área para este colegio.</p>
        </div>
        <div className="profesores-actions-header">
          <div className="profesor-count-card">
            <div className="count-icon">
              <i className="fas fa-book"></i>
            </div>
            <div>
              <p className="count-label">Total materias</p>
              <p className="count-number">{materiasTotal}</p>
            </div>
          </div>
          <div className="profesor-count-card">
            <div className="count-icon">
              <i className="fas fa-unlink"></i>
            </div>
            <div>
              <p className="count-label">Sin cursos</p>
              <p className="count-number">{materiasSinCursosTotal}</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <a className="secondary-btn" href={materiasTemplateUrl} download>
              <i className="fas fa-download" />
              <span>Descargar template</span>
            </a>
            <button className="secondary-btn" type="button" onClick={openImportMateriasModal}>
              <i className="fas fa-file-excel" />
              <span>Importar Excel</span>
            </button>
            <button
              className="secondary-btn danger-action"
              type="button"
              onClick={openDeleteUnlinkedMateriasModal}
              disabled={materiasLoading || materiasSinCursosTotal === 0}
              title={materiasSinCursosTotal === 0 ? 'No hay materias sin cursos asociados para eliminar.' : 'Eliminar materias sin cursos asociados.'}
            >
              <i className="fas fa-trash-alt" />
              <span>Eliminar materias no asociadas</span>
            </button>
            <button className="primary-btn" type="button" onClick={handleCreateMateria}>
              <i className="fas fa-plus" />
              <span>Nueva materia</span>
            </button>
          </div>
        </div>
      </div>

      <div className="content-card">
        <div className="card-header">
          <div className="card-title">
            <i className="fas fa-book-open"></i>
            <span>Listado de materias</span>
          </div>
          <div className="professor-actions">
            <div className="professor-search">
              <i className="fas fa-search" aria-hidden="true" />
              <input
                type="text"
                placeholder="Buscar por nombre"
                value={materiasSearch}
                onChange={(e) => { setMateriasSearch(e.target.value); setMateriasPage(1); }}
              />
            </div>
            <div className="cursos-filter-select">
              <i className="fas fa-filter" aria-hidden="true" />
              <select
                aria-label="Filtrar materias por cursos asociados"
                value={materiasCursoFilter}
                onChange={(e) => {
                  setMateriasCursoFilter(e.target.value);
                  setMateriasPage(1);
                }}
              >
                <option value={MATERIA_CURSO_FILTER.CON_CURSO}>Con curso</option>
                <option value={MATERIA_CURSO_FILTER.SIN_CURSO}>Sin curso</option>
                <option value={MATERIA_CURSO_FILTER.TODAS}>Todas</option>
              </select>
            </div>
            <button className="refresh-btn" type="button" onClick={loadMaterias} disabled={materiasLoading}>
              <i className={`fas ${materiasLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} />
            </button>
            <TableExportActions
              tableSelector="#admin-dashboard-materias-table"
              fileName="materias"
              title="Listado de materias"
              compact
              disabled={filteredMaterias.length === 0}
            />
          </div>
        </div>
        <div className="card-content professors-table">
          {!materiasLoading && !materiasError && materiasNotice && (
            <div className="curso-delete-feedback" role="status">
              <i className="fas fa-check-circle" />
              <span>{materiasNotice}</span>
            </div>
          )}

          {materiasLoading && (
            <div className="data-table-loading">
              <div className="loading-spinner">
                <i className="fas fa-spinner fa-spin"></i>
              </div>
              <p>Cargando materias...</p>
            </div>
          )}

          {!materiasLoading && materiasError && (
            <div className="data-table-empty">
              <div className="empty-icon">
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <p>{materiasError}</p>
              <button className="refresh-btn" type="button" onClick={loadMaterias}>
                <i className="fas fa-sync-alt" /> Reintentar
              </button>
            </div>
          )}

          {!materiasLoading && !materiasError && filteredMaterias.length === 0 && (
            <div className="data-table-empty">
              <div className="empty-icon">
                <i className="fas fa-users-slash"></i>
              </div>
              <p>
                {materiasCursoFilter === MATERIA_CURSO_FILTER.CON_CURSO
                  ? 'No hay materias con curso asociado.'
                  : materiasCursoFilter === MATERIA_CURSO_FILTER.SIN_CURSO
                    ? 'No hay materias sin curso asociado.'
                    : 'No hay materias cargadas.'}
              </p>
            </div>
          )}

          {!materiasLoading && !materiasError && filteredMaterias.length > 0 && (
            <div className="data-table-wrapper">
              <table id="admin-dashboard-materias-table" className="data-table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Código</th>
                    <th>Área</th>
                    <th className="text-center">Cursos</th>
                    <th className="text-center width-sm">Estado</th>
                    <th className="text-center width-sm">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {paginate(filteredMaterias, materiasPage).map((m) => {
                    const nombre = getMateriaNombre(m);
                    const codigo = m.codigo || m.Codigo || '-';
                    const area = m.nombreArea || m.areaNombre || m.NombreArea || m.AreaNombre || m.area || m.Area || '-';
                    const cantidadCursosRaw = m.cantidadCursosAsociados ?? m.CantidadCursosAsociados ?? 0;
                    const cantidadCursosAsociados = Number.isFinite(Number(cantidadCursosRaw)) ? Number(cantidadCursosRaw) : 0;
                    const tieneCursosAsociadosRaw = m.tieneCursosAsociados ?? m.TieneCursosAsociados;
                    const tieneCursosAsociados = typeof tieneCursosAsociadosRaw === 'boolean'
                      ? tieneCursosAsociadosRaw
                      : cantidadCursosAsociados > 0;
                    const activo = typeof m.activo === 'boolean' ? m.activo : typeof m.Activo === 'boolean' ? m.Activo : true;

                    return (
                      <tr key={m.id || m.Id || nombre}>
                        <td>{nombre}</td>
                        <td>{codigo}</td>
                        <td>{area}</td>
                        <td className="text-center">
                          <span className={`status-chip ${tieneCursosAsociados ? 'info' : 'warning'}`}>
                            {tieneCursosAsociados
                              ? `${cantidadCursosAsociados} curso${cantidadCursosAsociados === 1 ? '' : 's'}`
                              : 'Sin cursos'}
                          </span>
                        </td>
                        <td className="text-center">
                          <span className={`status-chip ${activo ? 'success' : 'danger'}`}>
                            {activo ? 'Activa' : 'Inactiva'}
                          </span>
                        </td>
                        <td className="text-center">
                          <div className="action-buttons">
                            <button
                              type="button"
                              className="action-edit-btn"
                              onClick={() => handleEditMateria(m)}
                              title="Editar materia"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button
                              type="button"
                              className={`action-toggle-btn ${activo ? 'danger' : 'success'}`}
                              onClick={() => openMateriaToggleModal(m)}
                              disabled={materiaToggleLoading}
                              title={activo ? 'Desactivar materia' : 'Activar materia'}
                            >
                              <i className={`fas ${activo ? 'fa-ban' : 'fa-check-circle'}`} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {!materiasLoading && !materiasError && materiasTotal > 0 && (
            renderPagination(materiasTotal, materiasPage, setMateriasPage, materiasPageSize, setMateriasPageSize)
          )}
        </div>
      </div>

      {importMateriasModalOpen && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className="fas fa-file-excel" />
            </div>
            <h3>Importar materias</h3>
            <p>
              Sube un archivo <strong>.xlsx</strong> con las columnas <strong>Nombre</strong>, <strong>Codigo</strong>,
              <strong>Descripcion</strong>, <strong>Area</strong>, <strong>Año</strong>, <strong>Division</strong> y
              <strong>Profesores</strong>. En profesores usa mails separados por coma. Si el área viene vacía, se usará
              <strong> Institucional</strong> por defecto.
            </p>
            <div className="modal-message" style={{ marginBottom: '1rem' }}>
              <a href={materiasTemplateUrl} download style={{ fontWeight: 700 }}>
                <i className="fas fa-download" style={{ marginRight: '0.5rem' }} />
                Descargar template de importación
              </a>
            </div>
            <div className="form-field">
              <label>Archivo Excel</label>
              <input
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  setImportMateriasFile(file);
                  setImportMateriasMessage('');
                }}
                disabled={importMateriasLoading}
              />
            </div>
            {importMateriasFile && (
              <div className="modal-message">
                Archivo seleccionado: <strong>{importMateriasFile.name}</strong>
              </div>
            )}
            {importMateriasMessage && (
              <div className="modal-message">
                {importMateriasMessage}
              </div>
            )}
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={closeImportMateriasModal}
                disabled={importMateriasLoading}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-primary success"
                onClick={handleImportMaterias}
                disabled={importMateriasLoading}
              >
                {importMateriasLoading ? (
                  <i className="fas fa-spinner fa-spin" />
                ) : (
                  <i className="fas fa-upload" />
                )}
                <span>{importMateriasLoading ? 'Enviando...' : 'Crear solicitud'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteUnlinkedMateriasModal.open && (
        <div className="modal-overlay" onClick={closeDeleteUnlinkedMateriasModal}>
          <div className="modal-card curso-delete-modal" onClick={(event) => event.stopPropagation()}>
            <div className="curso-delete-modal-header">
              <div className="curso-delete-modal-heading">
                <span className="curso-delete-modal-eyebrow">Limpieza de catálogo</span>
                <h3>
                  <i className="fas fa-trash-alt" />
                  Eliminar materias no asociadas
                </h3>
              </div>
              <button
                type="button"
                className="close-btn"
                onClick={closeDeleteUnlinkedMateriasModal}
                disabled={deleteUnlinkedMateriasModal.loading}
              >
                <i className="fas fa-times" />
              </button>
            </div>

            <div className="curso-delete-modal-body">
              <p className="curso-delete-modal-intro">
                Esta acción revisará el catálogo del colegio <strong>{activeColegioName}</strong> y eliminará solo las materias
                que hoy no estén asociadas a ningún curso activo. Sirve para limpiar materias huérfanas después de correcciones
                o reimportaciones de Excel.
              </p>

              <div className="curso-delete-summary-grid">
                <div className="curso-delete-summary-card">
                  <span className="curso-delete-summary-label">Colegio</span>
                  <strong>{activeColegioName}</strong>
                </div>
                <div className="curso-delete-summary-card">
                  <span className="curso-delete-summary-label">Total materias</span>
                  <strong>{materiasTotal}</strong>
                </div>
                <div className="curso-delete-summary-card">
                  <span className="curso-delete-summary-label">Sin cursos detectadas</span>
                  <strong>{materiasSinCursosTotal}</strong>
                </div>
              </div>

              <div className="curso-delete-impact">
                <p className="curso-delete-impact-title">Qué significa “no asociadas”</p>
                <ul>
                  <li>La materia no tiene ninguna relación activa con cursos del colegio.</li>
                  <li>Si la materia todavía tiene aulas, asistentes, alumnos o profesores vinculados, se omite y no se elimina.</li>
                  <li>La limpieza es definitiva solo para las materias realmente huérfanas del catálogo.</li>
                </ul>
              </div>

              <div className="curso-delete-confirm-box">
                <label htmlFor="materias-unlinked-delete-confirm-input">
                  Para confirmar, escribe exactamente:
                </label>
                <code>{deleteUnlinkedMateriasExpectedText}</code>
                <input
                  id="materias-unlinked-delete-confirm-input"
                  type="text"
                  value={deleteUnlinkedMateriasModal.confirmText}
                  onChange={(event) => setDeleteUnlinkedMateriasModal((prev) => ({
                    ...prev,
                    confirmText: event.target.value,
                    error: ''
                  }))}
                  placeholder={deleteUnlinkedMateriasExpectedText}
                  autoComplete="off"
                  disabled={deleteUnlinkedMateriasModal.loading}
                />
                {deleteUnlinkedMateriasModal.confirmText.trim() && (
                  <span className={`curso-delete-confirm-hint ${canConfirmDeleteUnlinkedMaterias ? 'valid' : 'invalid'}`}>
                    {canConfirmDeleteUnlinkedMaterias
                      ? 'Confirmación válida. Ya puedes ejecutar la limpieza.'
                      : 'La confirmación no coincide con el texto solicitado.'}
                  </span>
                )}
              </div>

              {deleteUnlinkedMateriasModal.error && (
                <div className="curso-delete-error">
                  <i className="fas fa-exclamation-circle" />
                  <span>{deleteUnlinkedMateriasModal.error}</span>
                </div>
              )}
            </div>

            <div className="confirm-modal-actions curso-delete-modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeDeleteUnlinkedMateriasModal}
                disabled={deleteUnlinkedMateriasModal.loading}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-danger curso-delete-confirm-btn"
                onClick={handleDeleteUnlinkedMaterias}
                disabled={!canConfirmDeleteUnlinkedMaterias || deleteUnlinkedMateriasModal.loading}
              >
                {deleteUnlinkedMateriasModal.loading ? 'Eliminando...' : 'Eliminar materias no asociadas'}
              </button>
            </div>
          </div>
        </div>
      )}

      {materiaToggleModal.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className={`fas ${materiaToggleModal.nextActivo ? 'fa-check-circle' : 'fa-ban'}`} />
            </div>
            <h3>
              {materiaToggleModal.nextActivo ? 'Activar materia' : 'Desactivar materia'}
            </h3>
            <p>
              {materiaToggleModal.nextActivo
                ? `Se activará la materia ${getMateriaNombre(materiaToggleModal.materia)}.`
                : `Se desactivará la materia ${getMateriaNombre(materiaToggleModal.materia)}.`}
            </p>
            <p style={{ color: '#475569' }}>Nota: la acción se actualizará contral backend cuando se definan los endpoints.</p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={closeMateriaToggleModal}
                disabled={materiaToggleLoading}
              >
                Cancelar
              </button>
              <button
                type="button"
                className={`btn-primary ${materiaToggleModal.nextActivo ? 'success' : 'danger'}`}
                onClick={handleToggleMateria}
                disabled={materiaToggleLoading}
              >
                {materiaToggleLoading ? (
                  <i className="fas fa-spinner fa-spin" />
                ) : (
                  <i className={`fas ${materiaToggleModal.nextActivo ? 'fa-check-circle' : 'fa-ban'}`} />
                )}
                <span>{materiaToggleModal.nextActivo ? 'Sí, activar' : 'Sí, desactivar'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderAulasSection = () => (
    <div className="profesores-section">
      <div className="section-header">
        <div>
          <p className="eyebrow">Aulas</p>
          <h2>Aulas del colegio</h2>
          <p className="subtitle">Gestiona las aulas por materia y profesor.</p>
        </div>
        <div className="profesores-actions-header">
          <div className="profesor-count-card">
            <div className="count-icon">
              <i className="fas fa-door-open"></i>
            </div>
            <div>
              <p className="count-label">Total aulas</p>
              <p className="count-number">{aulasTotal}</p>
            </div>
          </div>
          <button className="primary-btn" type="button" onClick={handleCreateAula}>
            <i className="fas fa-plus" />
            <span>Nueva aula</span>
          </button>
        </div>
      </div>

      <div className="aula-list-insights">
        <div className="aula-list-total-card">
          <span className="aula-list-total-icon">
            <i className="fas fa-door-open" />
          </span>
          <div>
            <p>Aulas encontradas</p>
            <strong>{aulasTotal}</strong>
          </div>
        </div>

        <div className="aula-list-top-card">
          <div className="aula-list-top-header">
            <span>
              <i className="fas fa-chalkboard-teacher" />
              Top 10 profesores con más aulas
            </span>
          </div>
          {aulasTopProfesores.length > 0 ? (
            <div className="aula-list-top-table" role="table" aria-label="Top 10 profesores con más aulas">
              <div className="aula-list-top-row header" role="row">
                <span>Profesor</span>
                <span>Aulas</span>
              </div>
              <div className="aula-list-top-body">
                {aulasTopProfesores.map((profesorTop) => (
                  <div
                    className="aula-list-top-row"
                    role="row"
                    key={profesorTop.idProfesor || profesorTop.IdProfesor || getTopProfessorName(profesorTop)}
                  >
                    <span className="aula-list-top-info">
                      <span className="aula-list-top-name">{getTopProfessorName(profesorTop)}</span>
                      {getTopProfessorEmail(profesorTop) && (
                        <span className="aula-list-top-email">{getTopProfessorEmail(profesorTop)}</span>
                      )}
                    </span>
                    <strong>{getTopProfessorTotalAulas(profesorTop)}</strong>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="aula-list-top-empty">Sin profesores con aulas.</p>
          )}
        </div>

        <div className="aula-list-empty-card">
          <div className="aula-list-top-header">
            <span>
              <i className="fas fa-user-slash" />
              Profesores sin aulas para alumnos
            </span>
            <strong className="aula-list-empty-count">{aulasProfesoresSinAulas.length}</strong>
          </div>
          {aulasProfesoresSinAulas.length > 0 ? (
            <div className="aula-list-empty-list">
              {aulasProfesoresSinAulas.map((profesorSinAula) => (
                <div
                  className="aula-list-empty-row"
                  key={getFilterItemId(profesorSinAula) || getFilterItemName(profesorSinAula, 'Profesor')}
                >
                  <span className="aula-list-top-info">
                    <span className="aula-list-top-name">{getFilterItemName(profesorSinAula, 'Profesor')}</span>
                    {getFilterItemEmail(profesorSinAula) && (
                      <span className="aula-list-top-email">{getFilterItemEmail(profesorSinAula)}</span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="aula-list-top-empty">Todos los profesores tienen aulas para alumnos.</p>
          )}
        </div>
      </div>

      <div className="content-card aula-list-card">
        <div className="card-header">
          <div className="card-title">
            <i className="fas fa-door-open"></i>
            <span>Listado de aulas</span>
          </div>
          <div className="professor-actions">
            <button className="refresh-btn" type="button" onClick={loadAulas} disabled={aulasLoading}>
              <i className={`fas ${aulasLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} />
            </button>
            <TableExportActions
              tableSelector="#admin-dashboard-aulas-table"
              fileName="aulas"
              title="Listado de aulas"
              compact
              disabled={filteredAulas.length === 0}
            />
          </div>
        </div>
        <div className="aula-list-filter-bar">
          <div className="aula-list-filter-field aula-list-filter-search">
            <label>Texto</label>
            <div className="professor-search">
              <i className="fas fa-search" aria-hidden="true" />
              <input
                type="text"
                placeholder="Buscar por aula, código, materia o profesor"
                value={aulasSearch}
                onChange={(e) => { setAulasSearch(e.target.value); setAulasPage(1); }}
              />
            </div>
          </div>
          <div className="aula-list-filter-field">
            <label>Materias</label>
            <SearchableSelect
              multiple
              values={aulasMateriaIds}
              options={aulasMateriaSelectOptions}
              placeholder="Todas las materias"
              emptyText="No hay materias disponibles."
              onChange={(nextValues) => {
                setAulasMateriaIds(nextValues);
                setAulasPage(1);
              }}
            />
          </div>
          <div className="aula-list-filter-field">
            <label>Profesores</label>
            <SearchableSelect
              multiple
              values={aulasProfesorIds}
              options={aulasProfesorSelectOptions}
              placeholder="Todos los profesores"
              emptyText="No hay profesores disponibles."
              onChange={(nextValues) => {
                setAulasProfesorIds(nextValues);
                setAulasPage(1);
              }}
            />
          </div>
          {hasAulasFilters && (
            <button
              className="aula-list-clear-filters"
              type="button"
              onClick={() => {
                setAulasSearch('');
                setAulasMateriaIds([]);
                setAulasProfesorIds([]);
                setAulasPage(1);
              }}
            >
              <i className="fas fa-times" />
              Limpiar
            </button>
          )}
        </div>
        <div className="card-content professors-table">
          {aulasLoading && (
            <div className="data-table-loading">
              <div className="loading-spinner">
                <i className="fas fa-spinner fa-spin"></i>
              </div>
              <p>Cargando aulas...</p>
            </div>
          )}

          {!aulasLoading && aulasError && (
            <div className="data-table-empty">
              <div className="empty-icon">
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <p>{aulasError}</p>
              <button className="refresh-btn" type="button" onClick={loadAulas}>
                <i className="fas fa-sync-alt" /> Reintentar
              </button>
            </div>
          )}

          {!aulasLoading && !aulasError && filteredAulas.length === 0 && (
            <div className="data-table-empty">
              <div className="empty-icon">
                <i className="fas fa-users-slash"></i>
              </div>
              <p>No hay aulas cargadas.</p>
            </div>
          )}

          {!aulasLoading && !aulasError && filteredAulas.length > 0 && (
            <div className="data-table-wrapper">
              <table id="admin-dashboard-aulas-table" className="data-table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th className="text-center width-sm">Ingresar</th>
                    <th>Materia</th>
                    <th>Profesor</th>
                    <th className="text-center width-sm">Asistentes</th>
                    <th className="text-center width-sm">Ciclo</th>
                    <th className="text-center width-sm">Estado</th>
                    <th className="text-center width-sm">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {paginate(filteredAulas, aulasPage).map((a) => {
                    const aulaId = a.id || a.Id || '';
                    const nombre = getAulaNombre(a);
                    const materia = a.nombreMateria || a.NombreMateria || '—';
                    const profesor = a.nombreProfesor || a.NombreProfesor || '—';
                    const cantidadAsistentes = getAulaAssistantCount(a);
                    const ciclo = a.cicloLectivo || a.CicloLectivo || '—';
                    const activo = typeof a.activo === 'boolean' ? a.activo : typeof a.Activo === 'boolean' ? a.Activo : true;
                    const descripcion = a.descripcion || a.Descripcion || '';

                    return (
                      <tr key={aulaId || nombre}>
                        <td>
                          <div className="prof-name">
                            <span className="name">{nombre}</span>
                            {descripcion && <span className="muted">{descripcion}</span>}
                          </div>
                        </td>
                        <td className="text-center">
                          <button
                            type="button"
                            className="secondary-btn aula-enter-btn"
                            onClick={() => handleOpenAula(a)}
                            disabled={!aulaId}
                            title="Ingresar al aula en una nueva pestaña"
                          >
                            <i className="fas fa-door-open" />
                            <span>Ingresar</span>
                          </button>
                        </td>
                        <td>{materia}</td>
                        <td>{profesor}</td>
                        <td className="text-center">{cantidadAsistentes}</td>
                        <td className="text-center">{ciclo}</td>
                        <td className="text-center">
                          <span className={`status-chip ${activo ? 'success' : 'danger'}`}>
                            {activo ? 'Activa' : 'Inactiva'}
                          </span>
                        </td>
                        <td className="text-center">
                          <div className="action-buttons">
                            <button
                              type="button"
                              className="action-stats-btn"
                              onClick={() => setAulaStatsModal({ open: true, aula: a })}
                              title="Ver estadísticas del aula"
                            >
                              <i className="fas fa-chart-line"></i>
                            </button>
                            <button
                              type="button"
                              className="action-edit-btn"
                              onClick={() => handleEditAula(a)}
                              title="Editar aula"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            {hasRecursosAula && (
                              <button
                                type="button"
                                className="action-resources-btn"
                                onClick={() => setAulaResourcesModal({ open: true, aula: a })}
                                title="Recursos del aula"
                              >
                                <i className="fas fa-layer-group" />
                              </button>
                            )}
                            <button
                              type="button"
                              className={`action-toggle-btn ${activo ? 'danger' : 'success'}`}
                              onClick={() => openAulaToggleModal(a)}
                              disabled={aulaToggleLoading}
                              title={activo ? 'Desactivar aula' : 'Activar aula'}
                            >
                              <i className={`fas ${activo ? 'fa-ban' : 'fa-check-circle'}`} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {!aulasLoading && !aulasError && aulasTotal > 0 && (
            renderPagination(aulasTotal, aulasPage, setAulasPage, aulasPageSize, setAulasPageSize)
          )}
        </div>
      </div>

      {aulaToggleModal.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className={`fas ${aulaToggleModal.nextActivo ? 'fa-check-circle' : 'fa-ban'}`} />
            </div>
            <h3>{aulaToggleModal.nextActivo ? 'Activar aula' : 'Desactivar aula'}</h3>
            <p>
              {aulaToggleModal.nextActivo
                ? `Se activará el aula ${getAulaNombre(aulaToggleModal.aula)}.`
                : `Se desactivará el aula ${getAulaNombre(aulaToggleModal.aula)}.`}
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={closeAulaToggleModal}
                disabled={aulaToggleLoading}
              >
                Cancelar
              </button>
              <button
                type="button"
                className={`btn-primary ${aulaToggleModal.nextActivo ? 'success' : 'danger'}`}
                onClick={handleToggleAula}
                disabled={aulaToggleLoading}
              >
                {aulaToggleLoading ? (
                  <i className="fas fa-spinner fa-spin" />
                ) : (
                  <i className={`fas ${aulaToggleModal.nextActivo ? 'fa-check-circle' : 'fa-ban'}`} />
                )}
                <span>{aulaToggleModal.nextActivo ? 'Sí, activar' : 'Sí, desactivar'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderAreasSection = () => (
    <div className="profesores-section">
      <div className="section-header">
        <div>
          <p className="eyebrow">Áreas</p>
          <h2>Áreas del colegio</h2>
          <p className="subtitle">Gestiona las áreas asociadas a este colegio.</p>
        </div>
        <div className="profesores-actions-header">
          <div className="profesor-count-card">
            <div className="count-icon">
              <i className="fas fa-sitemap"></i>
            </div>
            <div>
              <p className="count-label">Total áreas</p>
              <p className="count-number">{areasTotal}</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <a className="secondary-btn" href={areasTemplateUrl} download>
              <i className="fas fa-download" />
              <span>Descargar template</span>
            </a>
            <button className="secondary-btn" type="button" onClick={openImportAreasModal}>
              <i className="fas fa-file-excel" />
              <span>Importar Excel</span>
            </button>
            <button className="primary-btn" type="button" onClick={handleCreateArea}>
              <i className="fas fa-plus" />
              <span>Nueva área</span>
            </button>
          </div>
        </div>
      </div>

      <div className="content-card">
        <div className="card-header">
          <div className="card-title">
            <i className="fas fa-sitemap"></i>
            <span>Listado de áreas</span>
          </div>
          <div className="professor-actions">
            <div className="professor-search">
              <i className="fas fa-search" aria-hidden="true" />
              <input
                type="text"
                placeholder="Buscar por nombre o descripción"
                value={areasSearch}
                onChange={(e) => { setAreasSearch(e.target.value); setAreasPage(1); }}
              />
            </div>
            <button className="refresh-btn" type="button" onClick={loadAreas} disabled={areasLoading}>
              <i className={`fas ${areasLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} />
            </button>
            <TableExportActions
              tableSelector="#admin-dashboard-areas-table"
              fileName="areas"
              title="Listado de áreas"
              compact
              disabled={filteredAreas.length === 0}
            />
          </div>
        </div>
        <div className="card-content professors-table">
          {areasLoading && (
            <div className="data-table-loading">
              <div className="loading-spinner">
                <i className="fas fa-spinner fa-spin"></i>
              </div>
              <p>Cargando áreas...</p>
            </div>
          )}

          {!areasLoading && areasError && (
            <div className="data-table-empty">
              <div className="empty-icon">
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <p>{areasError}</p>
              <button className="refresh-btn" type="button" onClick={loadAreas}>
                <i className="fas fa-sync-alt" /> Reintentar
              </button>
            </div>
          )}

          {!areasLoading && !areasError && filteredAreas.length === 0 && (
            <div className="data-table-empty">
              <div className="empty-icon">
                <i className="fas fa-users-slash"></i>
              </div>
              <p>No hay áreas cargadas.</p>
            </div>
          )}

          {!areasLoading && !areasError && filteredAreas.length > 0 && (
            <div className="data-table-wrapper">
              <table id="admin-dashboard-areas-table" className="data-table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Descripción</th>
                    <th className="text-center width-sm">Estado</th>
                    <th className="text-center width-sm">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {paginate(filteredAreas, areasPage).map((a) => {
                    const nombre = getAreaNombre(a);
                    const descripcion = a.descripcion || a.Descripcion || '-';
                    const activo = typeof a.activo === 'boolean' ? a.activo : typeof a.Activo === 'boolean' ? a.Activo : true;

                    return (
                      <tr key={a.id || a.Id || nombre}>
                        <td>{nombre}</td>
                        <td>{descripcion}</td>
                        <td className="text-center">
                          <span className={`status-chip ${activo ? 'success' : 'danger'}`}>
                            {activo ? 'Activa' : 'Inactiva'}
                          </span>
                        </td>
                        <td className="text-center">
                          <div className="action-buttons">
                            <button
                              type="button"
                              className="action-edit-btn"
                              onClick={() => handleEditArea(a)}
                              title="Editar área"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button
                              type="button"
                              className={`action-toggle-btn ${activo ? 'danger' : 'success'}`}
                              onClick={() => openAreaToggleModal(a)}
                              disabled={areaToggleLoading}
                              title={activo ? 'Desactivar área' : 'Activar área'}
                            >
                              <i className={`fas ${activo ? 'fa-ban' : 'fa-check-circle'}`} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {!areasLoading && !areasError && areasTotal > 0 && (
            renderPagination(areasTotal, areasPage, setAreasPage, areasPageSize, setAreasPageSize)
          )}
        </div>
      </div>

      {importAreasModalOpen && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className="fas fa-file-excel" />
            </div>
            <h3>Importar áreas</h3>
            <p>
              Sube un archivo <strong>.xlsx</strong> con las columnas <strong>Nombre</strong> y <strong>Descripcion</strong>.
              La solicitud se enviará a la cola para procesarse en segundo plano.
            </p>
            <div className="modal-message" style={{ marginBottom: '1rem' }}>
              <a href={areasTemplateUrl} download style={{ fontWeight: 700 }}>
                <i className="fas fa-download" style={{ marginRight: '0.5rem' }} />
                Descargar template de importación
              </a>
            </div>
            <div className="form-field">
              <label>Archivo Excel</label>
              <input
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  setImportAreasFile(file);
                  setImportAreasMessage('');
                }}
                disabled={importAreasLoading}
              />
            </div>
            {importAreasFile && (
              <div className="modal-message">
                Archivo seleccionado: <strong>{importAreasFile.name}</strong>
              </div>
            )}
            {importAreasMessage && (
              <div className="modal-message">
                {importAreasMessage}
              </div>
            )}
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={closeImportAreasModal}
                disabled={importAreasLoading}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-primary success"
                onClick={handleImportAreas}
                disabled={importAreasLoading}
              >
                {importAreasLoading ? (
                  <i className="fas fa-spinner fa-spin" />
                ) : (
                  <i className="fas fa-upload" />
                )}
                <span>{importAreasLoading ? 'Enviando...' : 'Crear solicitud'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {areaToggleModal.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className={`fas ${areaToggleModal.nextActivo ? 'fa-check-circle' : 'fa-ban'}`} />
            </div>
            <h3>{areaToggleModal.nextActivo ? 'Activar área' : 'Desactivar área'}</h3>
            <p>
              {areaToggleModal.nextActivo
                ? `Se activará el área ${getAreaNombre(areaToggleModal.area)}.`
                : `Se desactivará el área ${getAreaNombre(areaToggleModal.area)}.`}
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={closeAreaToggleModal}
                disabled={areaToggleLoading}
              >
                Cancelar
              </button>
              <button
                type="button"
                className={`btn-primary ${areaToggleModal.nextActivo ? 'success' : 'danger'}`}
                onClick={handleToggleArea}
                disabled={areaToggleLoading}
              >
                {areaToggleLoading ? <i className="fas fa-spinner fa-spin" /> : <i className={`fas ${areaToggleModal.nextActivo ? 'fa-check-circle' : 'fa-ban'}`} />}
                <span>{areaToggleModal.nextActivo ? 'Sí, activar' : 'Sí, desactivar'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderDivisionesSection = () => (
    <div className="profesores-section">
      <div className="section-header">
        <div>
          <p className="eyebrow">Divisiones</p>
          <h2>Divisiones del colegio</h2>
          <p className="subtitle">Gestiona las divisiones disponibles para la creación y filtrado de cursos.</p>
        </div>
        <div className="profesores-actions-header">
          <div className="profesor-count-card">
            <div className="count-icon">
              <i className="fas fa-code-branch"></i>
            </div>
            <div>
              <p className="count-label">Total divisiones</p>
              <p className="count-number">{divisionesTotal}</p>
            </div>
          </div>
          <button className="primary-btn" type="button" onClick={handleCreateDivision}>
            <i className="fas fa-plus" />
            <span>Nueva división</span>
          </button>
        </div>
      </div>

      <div className="content-card">
        <div className="card-header">
          <div className="card-title">
            <i className="fas fa-code-branch"></i>
            <span>Listado de divisiones</span>
          </div>
          <div className="professor-actions">
            <div className="professor-search">
              <i className="fas fa-search" aria-hidden="true" />
              <input
                type="text"
                placeholder="Buscar por nombre o descripción"
                value={divisionesSearch}
                onChange={(e) => { setDivisionesSearch(e.target.value); setDivisionesPage(1); }}
              />
            </div>
            <button className="refresh-btn" type="button" onClick={loadDivisiones} disabled={divisionesLoading}>
              <i className={`fas ${divisionesLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} />
            </button>
            <TableExportActions
              tableSelector="#admin-dashboard-divisiones-table"
              fileName="divisiones"
              title="Listado de divisiones"
              compact
              disabled={filteredDivisiones.length === 0}
            />
          </div>
        </div>
        <div className="card-content professors-table">
          {divisionesLoading && (
            <div className="data-table-loading">
              <div className="loading-spinner">
                <i className="fas fa-spinner fa-spin"></i>
              </div>
              <p>Cargando divisiones...</p>
            </div>
          )}

          {!divisionesLoading && divisionesError && (
            <div className="data-table-empty">
              <div className="empty-icon">
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <p>{divisionesError}</p>
              <button className="refresh-btn" type="button" onClick={loadDivisiones}>
                <i className="fas fa-sync-alt" /> Reintentar
              </button>
            </div>
          )}

          {!divisionesLoading && !divisionesError && filteredDivisiones.length === 0 && (
            <div className="data-table-empty">
              <div className="empty-icon">
                <i className="fas fa-code-branch"></i>
              </div>
              <p>No hay divisiones cargadas.</p>
            </div>
          )}

          {!divisionesLoading && !divisionesError && filteredDivisiones.length > 0 && (
            <div className="data-table-wrapper">
              <table id="admin-dashboard-divisiones-table" className="data-table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Descripción</th>
                    <th className="text-center width-sm">Estado</th>
                    <th className="text-center width-sm">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {paginate(filteredDivisiones, divisionesPage).map((d) => {
                    const nombre = getDivisionNombre(d);
                    const descripcion = d.descripcion || d.Descripcion || '-';
                    const activo = typeof d.activo === 'boolean' ? d.activo : typeof d.Activo === 'boolean' ? d.Activo : true;

                    return (
                      <tr key={d.id || d.Id || nombre}>
                        <td>{nombre}</td>
                        <td>{descripcion}</td>
                        <td className="text-center">
                          <span className={`status-chip ${activo ? 'success' : 'danger'}`}>
                            {activo ? 'Activa' : 'Inactiva'}
                          </span>
                        </td>
                        <td className="text-center">
                          <div className="action-buttons">
                            <button
                              type="button"
                              className="action-edit-btn"
                              onClick={() => handleEditDivision(d)}
                              title="Editar división"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            {activo && (
                              <button
                                type="button"
                                className="action-remove-btn"
                                onClick={() => openDivisionDeleteModal(d)}
                                title="Eliminar división"
                                disabled={divisionDeleteLoading}
                              >
                                <i className="fas fa-trash-alt"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {!divisionesLoading && !divisionesError && divisionesTotal > 0 && (
            renderPagination(divisionesTotal, divisionesPage, setDivisionesPage, divisionesPageSize, setDivisionesPageSize)
          )}
        </div>
      </div>

      {divisionDeleteModal.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className="fas fa-trash-alt" />
            </div>
            <h3>Eliminar división</h3>
            <p>
              Se eliminará la división {getDivisionNombre(divisionDeleteModal.division)}.
            </p>
            <p style={{ color: '#475569' }}>
              La división dejará de estar disponible para nuevos cursos, pero se conservará el historial.
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={closeDivisionDeleteModal}
                disabled={divisionDeleteLoading}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-primary danger"
                onClick={handleDeleteDivision}
                disabled={divisionDeleteLoading}
              >
                {divisionDeleteLoading ? (
                  <i className="fas fa-spinner fa-spin" />
                ) : (
                  <i className="fas fa-trash-alt" />
                )}
                <span>{divisionDeleteLoading ? 'Eliminando...' : 'Sí, eliminar'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
  const renderColegiosSection = () => (
    <div className="profesores-section">
      <div className="section-header">
        <div>
          <p className="eyebrow">Colegios</p>
          <h2>Listado de colegios</h2>
          <p className="subtitle">Administra los colegios disponibles en el sistema.</p>
        </div>
        <div className="profesores-actions-header">
          <div className="profesor-count-card">
            <div className="count-icon">
              <i className="fas fa-globe-americas"></i>
            </div>
            <div>
              <p className="count-label">Total colegios</p>
              <p className="count-number">{colegiosList.length}</p>
            </div>
          </div>
          <button className="primary-btn" type="button" onClick={handleCreateColegio}>
            <i className="fas fa-plus" />
            <span>Nueva colegio</span>
          </button>
        </div>
      </div>

      <div className="content-card">
        <div className="card-header">
          <div className="card-title">
            <i className="fas fa-globe"></i>
            <span>Listado de colegios</span>
          </div>
          <div className="professor-actions">
            <div className="professor-search">
              <i className="fas fa-search" aria-hidden="true" />
              <input
                type="text"
                placeholder="Buscar por nombre o descripción"
                value={colegiosSearch}
                onChange={(e) => setColegiosSearch(e.target.value)}
              />
            </div>
            <button className="refresh-btn" type="button" onClick={loadColegios} disabled={colegiosLoading}>
              <i className={`fas ${colegiosLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} />
            </button>
            <TableExportActions
              tableSelector="#admin-dashboard-colegios-table"
              fileName="colegios-admin"
              title="Listado de colegios"
              compact
              disabled={filteredColegios.length === 0}
            />
          </div>
        </div>
        <div className="card-content professors-table">
          {colegiosLoading && (
            <div className="data-table-loading">
              <div className="loading-spinner">
                <i className="fas fa-spinner fa-spin"></i>
              </div>
              <p>Cargando colegios...</p>
            </div>
          )}

          {!colegiosLoading && colegiosError && (
            <div className="data-table-empty">
              <div className="empty-icon">
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <p>{colegiosError}</p>
              <button className="refresh-btn" type="button" onClick={loadColegios}>
                <i className="fas fa-sync-alt" /> Reintentar
              </button>
            </div>
          )}

          {!colegiosLoading && !colegiosError && filteredColegios.length === 0 && (
            <div className="data-table-empty">
              <div className="empty-icon">
                <i className="fas fa-users-slash"></i>
              </div>
              <p>No hay colegios cargadas.</p>
            </div>
          )}

          {!colegiosLoading && !colegiosError && filteredColegios.length > 0 && (
            <div className="data-table-wrapper">
              <table id="admin-dashboard-colegios-table" className="data-table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>CUIT</th>
                    <th>Descripción</th>
                    <th className="text-center width-sm">Estado</th>
                    <th className="text-center width-sm">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredColegios.map((reg) => {
                    const nombre = getColegioNombre(reg);
                    const cuit = reg.cuit || reg.Cuit || '-';
                    const descripcion = reg.descripcion || reg.Descripcion || '-';
                    const activo = typeof reg.activo === 'boolean' ? reg.activo : typeof reg.Activo === 'boolean' ? reg.Activo : true;

                    return (
                      <tr key={reg.id || reg.Id || nombre}>
                        <td>{nombre}</td>
                        <td>{cuit}</td>
                        <td>{descripcion}</td>
                        <td className="text-center">
                          <span className={`status-chip ${activo ? 'success' : 'danger'}`}>
                            {activo ? 'Activa' : 'Inactiva'}
                          </span>
                        </td>
                        <td className="text-center">
                          <div className="action-buttons">
                            {isGeneralAdmin && (
                              <button
                                type="button"
                                className="action-stats-btn"
                                onClick={() => handleViewColegioStats(reg)}
                                title="Ver estadísticas"
                              >
                                <i className="fas fa-chart-line"></i>
                              </button>
                            )}
                            <button
                              type="button"
                              className="action-edit-btn"
                              onClick={() => handleEditColegio(reg)}
                              title="Editar colegio"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button
                              type="button"
                              className={`action-toggle-btn ${activo ? 'danger' : 'success'}`}
                              onClick={() => openColegioToggleModal(reg)}
                              disabled={colegioToggleLoading}
                              title={activo ? 'Desactivar colegio' : 'Activar colegio'}
                            >
                              <i className={`fas ${activo ? 'fa-ban' : 'fa-check-circle'}`}></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {colegioToggleModal.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className={`fas ${colegioToggleModal.nextActivo ? 'fa-check-circle' : 'fa-ban'}`} />
            </div>
            <h3>
              {colegioToggleModal.nextActivo ? 'Activar colegio' : 'Desactivar colegio'}
            </h3>
            <p>
              {colegioToggleModal.nextActivo
                ? `Se activará el colegio ${getColegioNombre(colegioToggleModal.colegio)}.`
                : `Se desactivará el colegio ${getColegioNombre(colegioToggleModal.colegio)}.`}
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={closeColegioToggleModal}
                disabled={colegioToggleLoading}
              >
                Cancelar
              </button>
              <button
                type="button"
                className={`btn-primary ${colegioToggleModal.nextActivo ? 'success' : 'danger'}`}
                onClick={handleToggleColegio}
                disabled={colegioToggleLoading}
              >
                {colegioToggleLoading ? (
                  <i className="fas fa-spinner fa-spin" />
                ) : (
                  <i className={`fas ${colegioToggleModal.nextActivo ? 'fa-check-circle' : 'fa-ban'}`} />
                )}
                <span>{colegioToggleModal.nextActivo ? 'Sí, activar' : 'Sí, desactivar'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className={`new-dashboard admin-mode ${isSidebarOpen ? 'sidebar-open' : ''}`}>
      {isSidebarOpen && <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />}
      {/* Sidebar */}
      <aside className={`dashboard-sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-university"></i>
            <span>Admin</span>
          </div>
        </div>
        
        <AdminMenu
          activeId={activeMenuItem}
          user={user}
          featureFlags={featureFlags}
          onNavigate={(item) => handleMenuSelect(item.id)}
        />
        
        <div className="sidebar-footer">
          <ActiveColegioBadge />
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user-shield"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Administrador'}</span>
              <span className="user-role">Admin</span>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="dashboard-main">
        <header className="dashboard-header admin-dashboard-topbar">
          <div className="header-left">
            <button
              type="button"
              className="sidebar-toggle"
              onClick={() => setIsSidebarOpen((prev) => !prev)}
              title="Mostrar menú"
            >
              <i className="fas fa-bars"></i>
            </button>
            <h1 className="page-title">Dashboard Administrador</h1>
          </div>
          <div className="header-right">
            {(isGeneralAdmin || activeColegioId) && (
              <button
                type="button"
                className="metrics-btn-header"
                onClick={() => navigate('/admin/metricas-generales')}
                title="Métricas generales"
              >
                <i className="fas fa-chart-pie"></i>
                Métricas generales
              </button>
            )}
            {isGeneralAdmin && (
              <button
                type="button"
                className="metrics-btn-header"
                onClick={() => navigate('/admin/errores-generales')}
                title="Errores generales"
              >
                <i className="fas fa-bug"></i>
                Errores generales
              </button>
            )}
            {isGeneralAdmin && (
              <button
                type="button"
                className="metrics-btn-header"
                onClick={() => navigate('/admin/usuarios-en-linea')}
                title="Usuarios en línea"
              >
                <i className="fas fa-signal"></i>
                Usuarios en línea
              </button>
            )}
            {isGeneralAdmin && (
              <button
                type="button"
                className="inbox-btn-header"
                onClick={() => navigate('/admin/solicitudes')}
                title="Solicitudes recibidas"
              >
                <i className="fas fa-inbox"></i>
                Solicitudes recibidas
                {solicitudesPendientes > 0 && (
                  <span className="inbox-badge">{solicitudesPendientes}</span>
                )}
              </button>
            )}
            <ActiveColegioBadge />
            <UserSessionMenu
              user={user}
              onLogout={logout}
              extraItems={isGeneralAdmin ? [{ key: 'switch-school', label: 'Cambiar colegio', icon: 'fas fa-exchange-alt', onClick: handleBackToSelectColegio }] : []}
              userFallbackLabel="Administrador"
            />
          </div>
        </header>

        <div className="dashboard-content">
          {activeMenuItem === 'resumen' ? (
            dashboardLoading ? (
              <div className="content-card">
                <div className="card-content data-table-loading">
                  <div className="loading-spinner">
                    <i className="fas fa-spinner fa-spin"></i>
                  </div>
                  <p>Cargando indicadores del colegio...</p>
                </div>
              </div>
            ) : dashboardError ? (
              <div className="content-card">
                <div className="card-header">
                  <div className="card-title">
                    <i className="fas fa-exclamation-triangle" />
                    <span>Error al cargar el dashboard</span>
                  </div>
                </div>
                <div className="card-content">
                  <p style={{ color: '#b91c1c' }}>{dashboardError}</p>
                  <button className="primary-btn" type="button" onClick={loadDashboard}>
                    <i className="fas fa-sync-alt" />
                    <span>Reintentar</span>
                  </button>
                </div>
              </div>
            ) : (
              renderDashboardSection()
            )
          ) : activeMenuItem === 'profesores' ? (
            renderProfesoresSection()
          ) : activeMenuItem === 'alumnos' ? (
            renderAlumnosSection()
          ) : activeMenuItem === 'aulas' ? (
            renderAulasSection()
          ) : activeMenuItem === 'materias' ? (
            renderMateriasSection()
          ) : activeMenuItem === 'cursos' ? (
            <AdminCursosSection activeColegioId={activeColegioId} />
          ) : activeMenuItem === 'areas' ? (
            renderAreasSection()
          ) : activeMenuItem === 'divisiones' ? (
            renderDivisionesSection()
          ) : activeMenuItem === 'procesos-asincronicos' ? (
            renderProcesosAsincronicosSection()
          ) : activeMenuItem === 'asistentes' ? (
            renderAsistentesSection()
          ) : (
            <div className="content-card">
              <div className="card-header">
                <h2 className="card-title">
                  <i className="fas fa-tools"></i>
                  Contenido en construcción
                </h2>
              </div>
              <div className="card-content">
                <p>Pronto verás métricas y gestiones para Colegio, Profesores, Alumnos, Áreas, Materias y Asistentes.</p>
                <p>Menú lateral y estilos replican el look del panel de profesor.</p>
              </div>
            </div>
          )}
        </div>
      </main>
      <ChatModal
        isOpen={showChatModal}
        onClose={handleCloseChatModal}
        assistant={selectedAssistant}
      />

      {asistenteToggleModal.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-header">
              <h3><i className="fas fa-power-off" /> Cambiar estado del asistente</h3>
              <button className="close-btn" onClick={() => setAsistenteToggleModal({ open: false, asistente: null, nextActivo: true })}>
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="modal-body">
              <p>
                {asistenteToggleModal.nextActivo
                  ? `Se activará el asistente "${asistenteToggleModal.asistente?.nombre || asistenteToggleModal.asistente?.Nombre || 'Asistente'}" para que pueda ser usado.`
                  : `Se desactivará el asistente "${asistenteToggleModal.asistente?.nombre || asistenteToggleModal.asistente?.Nombre || 'Asistente'}" y dejará de estar disponible.`}
              </p>
              {asistenteToggleModal.error && (
                <p style={{ color: '#b91c1c' }}>{asistenteToggleModal.error}</p>
              )}
            </div>
            <div className="modal-footer" style={{ justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button className="btn btn-secondary" onClick={() => setAsistenteToggleModal({ open: false, asistente: null, nextActivo: true })}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={handleConfirmToggleAsistente} disabled={asistenteToggleLoading}>
                {asistenteToggleLoading ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-check" />}
                <span>Confirmar</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {aulaStatsModal.open && (
        <AulaStatsModal
          open={aulaStatsModal.open}
          aula={aulaStatsModal.aula}
          onClose={() => setAulaStatsModal({ open: false, aula: null })}
        />
      )}

      {aulaResourcesModal.open && (
        <AulaRecursosModal
          open={aulaResourcesModal.open}
          aula={aulaResourcesModal.aula}
          hasMensajes={hasMensajesAula}
          hasArchivos={hasArchivosAula}
          hasAsistentes={hasAgentesClasicos}
          hasLinks={hasLinksAula}
          globalAvailability={{
            mostrarArchivos: hasArchivosAula,
            mostrarLinks: hasLinksAula,
            mostrarPizarra: hasPizarraAula,
            mostrarChat: hasChatAula,
            mostrarVideollamada: hasVideollamadaAula,
            mostrarForo: hasMensajesAula,
            mostrarAsistentesIA: hasAgentesClasicos || hasOpenAIChats
          }}
          resourceVisibilityService={(id, payload) => aulaService.updateAulaRecursosVisibilidad(id, payload)}
          onAulaUpdated={handleAulaUpdated}
          canUploadFiles
          onClose={() => setAulaResourcesModal({ open: false, aula: null })}
        />
      )}

      <AdminAlumnoFamiliaresModal
        open={alumnoFamiliaresModal.open}
        alumno={alumnoFamiliaresModal.alumno}
        idColegio={activeColegioId}
        colegioName={activeColegioName}
        onClose={closeAlumnoFamiliaresModal}
      />

    </div>
  );
};

export default AdminDashboard;
